import {
  Loader2,
  CheckCircle,
  Bell,
  BellOff,
  Archive,
  ChevronLeft,
  EllipsisVertical,
  ShieldBan,
  Pin,
  X,
  CircleDollarSign,
} from "lucide-react";
import { ArchiveConfirmModal } from "./archive-confirm-modal";
import { useStore } from "../store";
import { useShallow } from "zustand/react/shallow";
import {
  ChatType,
  DecryptedMessageEntryResponse,
  getPaginatedAccessGroupMembers,
  getPaginatedDMThread,
  getPaginatedGroupChatThread,
  getUsersStateless,
  NewMessageEntryResponse,
  ProfileEntryResponse,
  PublicKeyToProfileEntryResponseMap,
  sendDeso,
  identity,
  transferDeSoToken,
  updateAccessGroup,
} from "deso-protocol";
import { useInterval } from "hooks/useInterval";
import { useIdleDetection } from "hooks/useIdleDetection";
import {
  FC,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  startTransition,
  ViewTransition,
} from "react";
import { toast } from "sonner";
import { useMessageSearch } from "../hooks/useMessageSearch";
import { useMobile } from "../hooks/useMobile";
import { useWebSocket } from "../hooks/useWebSocket";
import {
  useTypingIndicator,
  useTypingDisplay,
} from "../hooks/useTypingIndicator";
import { usePresence } from "../hooks/usePresence";
import { useSwipeBack } from "../hooks/useSwipeBack";
import {
  needsPermissionUpgrade,
  requestFullPermissions,
} from "../utils/with-auth";
import {
  classifyConversation,
  createApprovalAssociation,
  createArchiveChatAssociation,
  createArchiveAssociation,
  createBlockAssociation,
  createDismissAssociation,
  buildShellConversations,
  decryptAccessGroupMessagesWithRetry,
  decryptConversationPreviews,
  cleanupOwnJoinRequests,
  deleteArchiveAssociation,
  deleteAssociationById,
  cacheDecryptionResult,
  invalidateMessageCache,
  encryptAndSendNewMessage,
  encryptAndUpdateMessage,
  fetchAcceptedGroups,
  fetchArchivedGroups,
  fetchAssociationsByType,
  fetchChatAssociations,
  createGroupAcceptedAssociation,
  fetchJoinRequestCountsForOwner,
  fetchMessageThreadsRaw,
  fetchFollowedUsers,
  fetchPaidMessagingSettings,
  fetchSpamFilterSettings,
  fetchUserPaidMessagingSettings,
  createPaidAssociation,
  prepareEncryptedDMMessage,
  fetchPrivacyMode,
  getConversations,
  getConversationsDifferential,
  sendSystemMessage,
} from "../services/conversations.service";
import {
  cacheConversations,
  cacheConversationMessages,
  cacheLastConversationKey,
  cacheLastReadTimestamp,
  cacheUsernameMap,
  syncUnreadConversations,
  removeUnreadConversation,
  getCachedClassificationData,
  getCachedConversationMessages,
  getCachedConversations,
  getCachedLastConversationKey,
  getCachedLastReadTimestamps,
  getCachedMutedConversations,
  getCachedDmPrice,
  getCachedPrivacyMode,
  getCachedSpamFilter,
  getCachedUsernameMap,
  getCachedUserProfile,
  getCachedProfilePics,
  cacheProfilePics,
  mergeReadCursors,
  consumePendingNotificationConversation,
} from "../services/cache.service";
import {
  ASSOCIATION_TYPE_BLOCKED,
  ASSOCIATION_TYPE_CHAT_ARCHIVED,
  ASSOCIATION_TYPE_DISMISSED,
  BASE_TITLE,
  DEFAULT_KEY_MESSAGING_GROUP_NAME,
  MAX_MESSAGE_TEXT_BYTES,
  MESSAGES_ONE_REQUEST_LIMIT,
  PUBLIC_KEY_LENGTH,
  FOREGROUND_RESUME_DEBOUNCE_MS,
  REFRESH_MESSAGES_INTERVAL_MS,
  REFRESH_MESSAGES_MAX_INTERVAL_MS,
  REFRESH_MESSAGES_MOBILE_INTERVAL_MS,
  REFRESH_MESSAGES_WS_CONNECTED_MS,
  TITLE_DIVIDER,
} from "../utils/constants";
import {
  formatLastSeen,
  getChatNameFromConversation,
  hasSetupMessaging,
  scrollContainerToElement,
} from "../utils/helpers";
import { Conversation, ConversationMap, updateConv } from "../utils/types";
import {
  buildExtraData,
  getGroupDisplayName,
  getGroupImageUrl,
  getGroupPinnedMessage,
  GROUP_PINNED_MESSAGE,
  GROUP_PINNED_PREVIEW,
  parseMessageType,
  MSG_REPLY_TO,
  MSG_REPLY_PREVIEW,
  MSG_REPLY_SENDER,
  MSG_LANG,
  type MentionEntry,
} from "../utils/extra-data";
import { detectLanguageSync } from "../utils/detect-language";
import { preloadMarkdownPipeline } from "./messages/formatted-message";
import { sendAtomicPaidMessage } from "../utils/atomic-paid-message";
const LazyTipConfirmDialog = lazy(() =>
  import("./tip-confirm-dialog").then((m) => ({ default: m.TipConfirmDialog }))
);
import { withAuth } from "../utils/with-auth";
import { fetchExchangeRate, usdToNanos } from "../utils/exchange-rate";
import {
  fetchUsdcBalance,
  usdToUsdcBaseUnits,
  toHexUint256,
  invalidateUsdcBalanceCache,
} from "../utils/usdc-balance";
import { USDC_CREATOR_PUBLIC_KEY } from "../utils/constants";
import { getCachedTipCurrency } from "../services/cache.service";
import type { TipCurrency } from "../utils/extra-data";

// Micro-tip cooldown (3 seconds between tips)
let lastMicroTipTime = 0;
const MICRO_TIP_COOLDOWN_MS = 3000;
import { JoinGroupModal } from "./join-group-modal";
const LazyJoinConfirmationModal = lazy(() =>
  import("./join-confirmation-modal").then((m) => ({
    default: m.JoinConfirmationModal,
  }))
);
const LazyManageMembersDialog = lazy(() =>
  import("./manage-members-dialog").then((m) => ({
    default: m.ManageMembersDialog,
  }))
);
import {
  MessagingBubblesAndAvatar,
  type MessagingBubblesHandle,
} from "./messaging-bubbles";
import { MessagingConversationAccount } from "./messaging-conversation-accounts";
import { MessagingConversationButton } from "./messaging-conversation-button";
import { MessagingDisplayAvatar } from "./messaging-display-avatar";
import { UserActionMenu } from "./user-action-menu";
import { ProfileModal } from "./profile-modal";
import { MessagingSetupButton } from "./messaging-setup-button";
import { OnboardingWizard } from "./onboarding/onboarding-wizard";
import {
  isOnboardingComplete,
  markOnboardingComplete,
} from "../utils/onboarding";
import { shortenLongWord } from "../utils/search-helpers";
import {
  SendMessageButtonAndInput,
  SendMessageInputHandle,
} from "./send-message-button-and-input";
import {
  addPendingMessage,
  removePendingMessage,
  getPendingMessages,
  clearPendingMessages,
  incrementPendingMessageRetryCount,
  resetPendingMessageRetryCount,
  type PendingMessage,
} from "../services/pending-messages.service";
import {
  getHiddenMessageIds,
  hideMessage,
} from "../services/hidden-messages.service";

/**
 * Find the timestamp of the latest message NOT sent by the current user.
 * In group chats the user might have sent the latest message (or a reaction)
 * while there are still unread messages from others above it.
 * Returns 0 if no incoming message is found.
 */
function looksLikeEncryptedHex(text: string): boolean {
  return text.length >= 64 && /^[0-9a-f]+$/i.test(text);
}

function latestIncomingTimestamp(
  convo: Conversation,
  myPublicKey: string
): number {
  for (const m of convo.messages) {
    if (!m.IsSender && m.SenderInfo.OwnerPublicKeyBase58Check !== myPublicKey)
      return m.MessageInfo.TimestampNanos;
  }
  return 0;
}

export const MockBubble: FC<{
  username: string;
  avatar: string;
  timestamp: string;
  text: string;
  direction: string;
}> = ({ username, avatar, timestamp, text, direction = "left" }) => {
  return (
    <>
      {direction == "receiver" && (
        <div className="flex flex-row items-start gap-3">
          <div className="flex flex-col w-[60px] top-2 relative justify-center items-center">
            <img src={avatar} className="w-[38px] rounded-full mb-2" />
            <span className="whitespace-nowrap text-xs text-fg-500">
              {timestamp}
            </span>
          </div>
          <div className="flex flex-col items-start">
            <div className="mb-2 text-brand text-xs">{username}</div>
            <div className="text-xs w-auto bg-surface-raised border border-ink/6 rounded-2xl rounded-bl-sm pl-5 mt-auto mb-2 md:mb-5 py-2 px-2 md:px-4 text-ink break-words flex text-left relative items-center">
              <div className="text-ink text-sm">{text}</div>
            </div>
          </div>
        </div>
      )}
      {direction == "sender" && (
        <div className="flex flex-row-reverse items-start gap-3">
          <div className="flex flex-col w-[60px] justify-center items-center">
            <img src={avatar} className="w-[38px] rounded-full mb-2" />
            <span className="whitespace-nowrap text-xs text-fg-500">
              {timestamp}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <div className="mb-2 text-brand text-xs">{username}</div>
            <div className="bg-[#34F080]/10 border border-[#34F080]/15 text-ink rounded-2xl rounded-br-sm pl-5 mt-auto mb-2 md:mb-5 py-2 px-2 md:px-4 break-words inline-flex text-left relative items-center">
              <div className="text-ink text-sm">{text}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Build a dedup key for matching optimistic messages to blockchain-confirmed ones.
 * Includes ExtraData fingerprint so media messages (images, videos, GIFs) with
 * identical or empty captions don't collide.
 */
function msgDedupeKey(
  senderPk: string,
  text: string | undefined,
  extraData?: Record<string, string>
): string {
  let key = senderPk + ":" + (text?.slice(0, 50) ?? "");
  if (extraData) {
    // Use media URLs as discriminators — they're unique per upload
    const media =
      extraData.encryptedImageURLs ||
      extraData.encryptedVideoURLs ||
      extraData["msg:gifUrl"] ||
      "";
    if (media) key += ":" + media.slice(0, 120);
  }
  return key;
}

// URL routing for individual chats. /chat/<encoded conversation key> selects
// a conversation; "/" deselects (chat list). Encoding handles `:` (group
// chats use "<ownerPublicKey>:<groupKeyName>") and any non-ASCII keyNames.
const CHAT_PATH_PREFIX = "/chat/";

function conversationKeyFromPath(pathname: string): string {
  if (!pathname.startsWith(CHAT_PATH_PREFIX)) return "";
  try {
    return decodeURIComponent(pathname.slice(CHAT_PATH_PREFIX.length));
  } catch {
    return "";
  }
}

function chatPathFromKey(key: string): string {
  return key ? `${CHAT_PATH_PREFIX}${encodeURIComponent(key)}` : "/";
}

export const MessagingApp: FC = () => {
  // State — only these cause re-renders (useShallow does shallow equality)
  const {
    appUser,
    isLoadingUser,
    allAccessGroups,
    lockRefresh,
    followedUsers,
    approvedUsers,
    blockedUsers,
    initiatedChats,
    chatRequestsLoaded,
    archivedGroups,
    acceptedGroups,
    archivedChats,
    dismissedUsers,
    paidUsers,
    unreadByConversation,
    mutedConversations,
    spamFilter,
  } = useStore(
    useShallow((s) => ({
      appUser: s.appUser,
      isLoadingUser: s.isLoadingUser,
      allAccessGroups: s.allAccessGroups,
      lockRefresh: s.lockRefresh,
      followedUsers: s.followedUsers,
      approvedUsers: s.approvedUsers,
      blockedUsers: s.blockedUsers,
      initiatedChats: s.initiatedChats,
      chatRequestsLoaded: s.chatRequestsLoaded,
      archivedGroups: s.archivedGroups,
      acceptedGroups: s.acceptedGroups,
      archivedChats: s.archivedChats,
      dismissedUsers: s.dismissedUsers,
      paidUsers: s.paidUsers,
      unreadByConversation: s.unreadByConversation,
      mutedConversations: s.mutedConversations,
      spamFilter: s.spamFilter,
    }))
  );
  // Actions — stable references, never cause re-renders
  const setAllAccessGroups = useStore((s) => s.setAllAccessGroups);
  const setLockRefresh = useStore((s) => s.setLockRefresh);
  const setClassificationData = useStore((s) => s.setClassificationData);
  const addInitiatedChat = useStore((s) => s.addInitiatedChat);
  const approveUser = useStore((s) => s.approveUser);
  const rollbackApproval = useStore((s) => s.rollbackApproval);
  const blockUser = useStore((s) => s.blockUser);
  const rollbackBlock = useStore((s) => s.rollbackBlock);
  const archiveGroup = useStore((s) => s.archiveGroup);
  const unarchiveGroup = useStore((s) => s.unarchiveGroup);
  const rollbackArchive = useStore((s) => s.rollbackArchive);
  const rollbackUnarchive = useStore((s) => s.rollbackUnarchive);
  const mergeArchivedGroupIds = useStore((s) => s.mergeArchivedGroupIds);
  const acceptGroup = useStore((s) => s.acceptGroup);
  const rollbackAcceptGroup = useStore((s) => s.rollbackAcceptGroup);
  const mergeAcceptedGroupIds = useStore((s) => s.mergeAcceptedGroupIds);
  const archiveChat = useStore((s) => s.archiveChat);
  const unarchiveChat = useStore((s) => s.unarchiveChat);
  const rollbackArchiveChat = useStore((s) => s.rollbackArchiveChat);
  const rollbackUnarchiveChat = useStore((s) => s.rollbackUnarchiveChat);
  const mergeArchivedChatIds = useStore((s) => s.mergeArchivedChatIds);
  const dismissUser = useStore((s) => s.dismissUser);
  const undismissUser = useStore((s) => s.undismissUser);
  const rollbackDismiss = useStore((s) => s.rollbackDismiss);
  const rollbackUndismiss = useStore((s) => s.rollbackUndismiss);
  const mergeDismissedIds = useStore((s) => s.mergeDismissedIds);
  const unblockUser = useStore((s) => s.unblockUser);
  const rollbackUnblock = useStore((s) => s.rollbackUnblock);
  const clearUnread = useStore((s) => s.clearUnread);
  const initializeUnread = useStore((s) => s.initializeUnread);
  const toggleMute = useStore((s) => s.toggleMute);
  const setJoinRequestCounts = useStore((s) => s.setJoinRequestCounts);
  const [usernameByPublicKeyBase58Check, setUsernameByPublicKeyBase58Check] =
    useState<{ [key: string]: string }>({});
  const [profilePicByPublicKey, setProfilePicByPublicKey] = useState<{
    [key: string]: string;
  }>(() => getCachedProfilePics());
  const [senderProfiles, setSenderProfiles] = useState<
    Map<string, ProfileEntryResponse>
  >(new Map());
  // autoFetchConversations was removed — conversationsLoading now tracks this
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [, setPubKeyPlusGroupName] = useState<string>("");
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState<string | null>(
    null
  );
  const [loadingConversation, setLoadingConversation] = useState(false);
  // Conversation selection lives in the URL (/chat/<encoded-key>) so the
  // browser back button returns to the chat list instead of closing the
  // app/site. Lazy-init from the URL on first mount so deep links work.
  const [selectedConversationPublicKey, setSelectedConversationPublicKey] =
    useState(() =>
      typeof window === "undefined"
        ? ""
        : conversationKeyFromPath(window.location.pathname)
    );
  const [profileModal, setProfileModal] = useState<{
    publicKey: string;
    username?: string;
  } | null>(null);
  const [conversations, setConversations] = useState<ConversationMap>({});
  const [membersByGroupKey, setMembersByGroupKey] = useState<{
    [groupKey: string]: PublicKeyToProfileEntryResponseMap;
  }>({});
  const [replyToMessage, setReplyToMessage] = useState<{
    text: string;
    timestamp: string;
    sender: string;
  } | null>(null);
  const [recipientDmPrice, setRecipientDmPrice] = useState<{
    cents: number;
    followingCents: number;
  } | null>(null);
  // Tracks whether the user has confirmed paying for this conversation (resets on switch)
  const [paidDmConfirmed, setPaidDmConfirmed] = useState(false);
  const sendInputRef = useRef<SendMessageInputHandle>(null);
  const bubblesRef = useRef<MessagingBubblesHandle>(null);
  const fetchedBalanceKeysRef = useRef<Set<string>>(new Set());
  const [editingMessage, setEditingMessage] = useState<{
    text: string;
    timestamp: string;
  } | null>(null);
  const [hiddenMessageIds, setHiddenMessageIds] = useState<Set<string>>(
    new Set()
  );
  // Captured at conversation-open time, before we update it to the latest message.
  // Passed to MessagingBubblesAndAvatar so it can render the "new messages" divider.
  const [openedLastReadNanos, setOpenedLastReadNanos] = useState<number | null>(
    null
  );
  const [tipTarget, setTipTarget] = useState<{
    recipientPublicKey: string;
    recipientUsername?: string;
    tipReplyTo?: string;
    initialAmountUsd?: number;
  } | null>(null);
  const [pendingTipTimestamps, setPendingTipTimestamps] = useState<Set<string>>(
    new Set()
  );
  const [dmMenuOpen, setDmMenuOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<{
    type: "dm" | "group";
    conversationKey: string;
    publicKey: string;
    name: string;
    /** Group-only fields */
    groupOwnerPublicKey?: string;
    groupKeyName?: string;
  } | null>(null);
  const [blockConfirm, setBlockConfirm] = useState<{
    conversationKey: string;
    publicKey: string;
    name: string;
  } | null>(null);
  const { isMobile } = useMobile();
  const isIdle = useIdleDetection();

  // Expose conversation setters for Playwright tests (dev only)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as any).__CHATON_MSG__ = {
      setConversations,
      setSelectedConversationPublicKey,
      setMembersByGroupKey,
    };
    return () => {
      delete (window as any).__CHATON_MSG__;
    };
  }, []);

  // Adaptive polling: starts at base interval, backs off additively each cycle,
  // resets on WebSocket activity, and pauses entirely when user is idle.
  const baseDelay = isMobile
    ? REFRESH_MESSAGES_MOBILE_INTERVAL_MS
    : REFRESH_MESSAGES_INTERVAL_MS;
  const [pollingDelay, setPollingDelay] = useState<number | null>(baseDelay);

  // Pause polling when idle, resume at base rate when active
  useEffect(() => {
    setPollingDelay(isIdle ? null : baseDelay);
  }, [isIdle, baseDelay]);

  // Persist NFT/LargeProfilePicURL map so chat-list avatars render correctly
  // on the first render of the next session — no flash from the default pic
  // endpoint to the NFT URL after profile data reloads. cacheProfilePics
  // debounces internally so rapid updates coalesce into one localStorage write.
  useEffect(() => {
    cacheProfilePics(profilePicByPublicKey);
  }, [profilePicByPublicKey]);

  // Debounce foreground resume to prevent bursty API calls on rapid tab switches
  const lastResumeRef = useRef(0);

  // Close DM menu, block dialog, and tip dialog when switching conversations
  useEffect(() => {
    setDmMenuOpen(false);
    setBlockConfirm(null);
    setTipTarget(null);
    setPendingTipTimestamps(new Set());
    // Pre-warm exchange rate cache so micro-tips don't hit a cold fetch
    fetchExchangeRate().catch(() => {});
  }, [selectedConversationPublicKey]);

  // Fetch the recipient's DM price when opening a DM conversation.
  // Resets when switching conversations. Skips group chats and self-DMs.
  // If the sender already follows the recipient, uses the follower price tier.
  useEffect(() => {
    setRecipientDmPrice(null);
    setPaidDmConfirmed(false);
    if (!selectedConversationPublicKey || !appUser) return;
    const convo = conversationsRef.current[selectedConversationPublicKey];
    if (!convo || convo.ChatType === ChatType.GROUPCHAT) return;
    const otherKey = convo.firstMessagePublicKey;
    if (!otherKey || otherKey === appUser.PublicKeyBase58Check) return;
    // Skip if already approved (no payment needed)
    if (approvedUsers.has(otherKey)) return;

    const senderFollowsRecipient = followedUsers.has(otherKey);

    let cancelled = false;
    fetchUserPaidMessagingSettings(otherKey).then((result) => {
      if (cancelled || result.cents === null || result.cents <= 0) return;
      // Use follower price if the sender follows the recipient
      const effectiveCents = senderFollowsRecipient
        ? result.followingCents
        : result.cents;
      // If effective price is 0, no payment needed
      if (effectiveCents <= 0) return;
      setRecipientDmPrice({
        cents: effectiveCents,
        followingCents: result.followingCents,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [selectedConversationPublicKey, appUser, followedUsers, approvedUsers]);

  // Fire-and-forget: claim any pending Focus tips when opening a DM thread.
  // Focus's API accepts standard DeSo auth (Public-Key header + JWT).
  // Silent failure — this is a nice-to-have, not critical.
  useEffect(() => {
    if (!selectedConversationPublicKey || !appUser) return;
    const convo = conversationsRef.current[selectedConversationPublicKey];
    if (!convo || convo.ChatType === ChatType.GROUPCHAT) return;
    const otherKey = convo.firstMessagePublicKey;
    if (!otherKey || otherKey === appUser.PublicKeyBase58Check) return;

    const myPk = appUser.PublicKeyBase58Check;
    identity
      .jwt()
      .then((jwt: string) => {
        // Try both thread identifier orderings
        const threadId = `${myPk}-${otherKey}-default-key-default-key-false`;
        fetch("https://focus.xyz/api/v0/message/views", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Public-Key": myPk,
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            MessageViews: [{ ThreadIdentifier: threadId }],
          }),
        }).catch(() => {});
      })
      .catch(() => {});
  }, [selectedConversationPublicKey, appUser]);

  // Proactively tell the service worker which conversation is active so it can
  // suppress push notifications for the conversation the user is viewing.
  // Uses controller when available, falls back to registration.active for the
  // first page load before clientsClaim takes effect (controller can be null).
  // If the SW restarts, the variable resets to null (fail-open: all notifications show).
  //
  // The SW uses a 10s TTL on activeConversationKey to guard against iOS not
  // firing visibilitychange when the PWA is backgrounded. A 5s heartbeat keeps
  // the TTL alive while the user is genuinely viewing the conversation.
  useEffect(() => {
    const sendActiveConversation = () => {
      const msg = {
        type: "set-active-conversation",
        conversationKey: selectedConversationPublicKey || null,
      };
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage(msg);
      } else {
        navigator.serviceWorker?.ready.then((reg) =>
          reg.active?.postMessage(msg)
        );
      }
    };

    sendActiveConversation();

    // Heartbeat: re-send every 5s so the SW's 10s TTL stays fresh
    const heartbeat = selectedConversationPublicKey
      ? setInterval(sendActiveConversation, 5_000)
      : undefined;

    return () => clearInterval(heartbeat);
  }, [selectedConversationPublicKey]);

  // Clear the active conversation when the tab loses focus or is closed,
  // so notifications resume immediately.
  useEffect(() => {
    const sendToSW = (conversationKey: string | null) => {
      const msg = { type: "set-active-conversation", conversationKey };
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage(msg);
      } else {
        navigator.serviceWorker?.ready.then((reg) =>
          reg.active?.postMessage(msg)
        );
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendToSW(null);
      } else if (selectedConversationPublicKeyRef.current) {
        sendToSW(selectedConversationPublicKeyRef.current);
      }
    };

    const handleBeforeUnload = () => sendToSW(null);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Message search
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: searchResults,
    isSearching: isMessageSearching,
    isDeepSearching,
    progress: searchProgress,
    clearSearch,
  } = useMessageSearch({
    userPublicKey: appUser?.PublicKeyBase58Check || "",
    conversations,
    usernameMap: usernameByPublicKeyBase58Check,
    allAccessGroups,
  });

  const [searchClearTrigger, setSearchClearTrigger] = useState(0);
  const [pendingSearchJump, setPendingSearchJump] = useState<{
    convKey: string;
    ts: string;
  } | null>(null);

  const handleSearchResultClick = useCallback(
    (conversationKey: string, timestampNanos: string) => {
      clearSearch();
      setSearchClearTrigger((n) => n + 1);
      setSelectedConversationPublicKey(conversationKey);
      setPubKeyPlusGroupName(conversationKey);
      setPendingSearchJump({ convKey: conversationKey, ts: timestampNanos });
    },
    [clearSearch]
  );

  // Ref that always reflects the latest accumulated username map.
  // React state updates are batched, so reading `usernameByPublicKeyBase58Check`
  // inside an async callback gives a stale value. This ref is updated alongside
  // every `setUsernameByPublicKeyBase58Check` call so dedup checks in
  // `fetchUsersStateless` never re-fetch keys we already resolved.
  const usernameMapRef = useRef<Record<string, string>>({});

  // Helper: update both state and ref in one call.
  // The ref is mutated in-place (never rendered directly) to avoid
  // O(n) shallow-copies when called multiple times in a row.
  const mergeUsernames = (patch: Record<string, string>) => {
    Object.assign(usernameMapRef.current, patch);
    setUsernameByPublicKeyBase58Check((s) => ({ ...s, ...patch }));
  };

  // Refs for accessing latest state inside callbacks without causing re-renders
  const lockRefreshRef = useRef(lockRefresh);
  lockRefreshRef.current = lockRefresh;
  const selectedConversationPublicKeyRef = useRef(
    selectedConversationPublicKey
  );
  selectedConversationPublicKeyRef.current = selectedConversationPublicKey;
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const allAccessGroupsRef = useRef(allAccessGroups);
  allAccessGroupsRef.current = allAccessGroups;

  // Tracks the conversation key that's currently displaying a historical
  // slice (loaded by clicking a search result, replied-to, or pinned message).
  // The slice ends AT the target message — there's nothing newer in it — so
  // polling must NOT replace `messages` or wipe `savedMessagesRef` until the
  // user explicitly returns to latest via the Jump-to-Latest button.
  const viewingHistoricalSliceRef = useRef<string | null>(null);
  const [viewingHistoricalSliceKey, setViewingHistoricalSliceKey] = useState<
    string | null
  >(null);

  // Shared merge logic: reconcile optimistic messages with blockchain-confirmed data.
  // Used by both the WebSocket handler and the polling interval.
  const mergeConversationUpdate = useCallback(
    (
      updatedConversations: ConversationMap,
      selectedKey: string,
      updatedMessages: DecryptedMessageEntryResponse[]
    ) => {
      // While viewing a historical slice for this conversation, don't overwrite
      // the visible messages or wipe the saved-tail snapshot. Refresh the
      // snapshot to the freshest tail so Jump-to-Latest restores up-to-date
      // messages, and keep IndexedDB caches current.
      if (viewingHistoricalSliceRef.current === selectedKey) {
        savedMessagesRef.current = {
          messages: updatedMessages,
          convKey: selectedKey,
        };
        if (appUser) {
          cacheConversations(
            appUser.PublicKeyBase58Check,
            updatedConversations
          );
          cacheConversationMessages(
            appUser.PublicKeyBase58Check,
            selectedKey,
            updatedMessages
          );
        }
        return;
      }

      // Preserve the user's reading position. When this refresh targets the
      // conversation currently on screen and the user has scrolled away from
      // the live tail to read older messages, don't overwrite the visible
      // thread — replacing it with the latest tail discards the older messages
      // they paged in and snaps the view to the present (e.g. on returning to
      // the tab after opening a link in a new tab). Keep the caches fresh so
      // Jump-to-Latest still restores up-to-date messages. Skip this guard
      // while an optimistic send is pending so its confirmation still merges.
      if (
        selectedKey === selectedConversationPublicKeyRef.current &&
        bubblesRef.current?.isScrolledAway() &&
        !conversationsRef.current[selectedKey]?.messages.some(
          (m: any) => m._localId
        )
      ) {
        if (appUser) {
          cacheConversations(
            appUser.PublicKeyBase58Check,
            updatedConversations
          );
          cacheConversationMessages(
            appUser.PublicKeyBase58Check,
            selectedKey,
            updatedMessages
          );
        }
        return;
      }

      let hasNewlyConfirmed = false;
      // Only invalidate the snapshot when the merge is actually for the
      // conversation the snapshot belongs to. Otherwise an unrelated merge
      // (e.g. runConfirmationCheck firing for a different conv) would wipe
      // the slice's saved tail mid-view.
      if (savedMessagesRef.current?.convKey === selectedKey) {
        savedMessagesRef.current = null;
      }

      setConversations((prev) => {
        const existing = prev[selectedKey];
        if (!existing) return prev;

        const currentMessages = existing.messages;

        const optimisticMessages = currentMessages.filter(
          (m: any) => m._localId
        );

        // Map dedup key → _localId so we can transfer it to the confirmed message
        const optimisticByKey = new Map<string, string>();
        for (const m of optimisticMessages) {
          const key = msgDedupeKey(
            (m as any).SenderInfo.OwnerPublicKeyBase58Check,
            (m as any).DecryptedMessage,
            (m as any).MessageInfo?.ExtraData
          );
          optimisticByKey.set(key, (m as any)._localId);
        }

        const confirmedKeys = new Set(
          updatedMessages.map((m) =>
            msgDedupeKey(
              m.SenderInfo.OwnerPublicKeyBase58Check,
              m.DecryptedMessage,
              m.MessageInfo?.ExtraData
            )
          )
        );

        const stillPendingOptimistic = optimisticMessages.filter(
          (m: any) =>
            !confirmedKeys.has(
              msgDedupeKey(
                m.SenderInfo.OwnerPublicKeyBase58Check,
                m.DecryptedMessage,
                m.MessageInfo?.ExtraData
              )
            )
        );

        // Build a map of locally-deleted messages by timestamp so we can
        // preserve the tombstone when the server hasn't indexed the delete yet.
        const locallyDeletedByTs = new Map<string, any>();
        for (const m of currentMessages) {
          if ((m as any)._deletedLocally) {
            locallyDeletedByTs.set(m.MessageInfo?.TimestampNanosString, m);
          }
        }

        // Tag blockchain messages that just confirmed an optimistic entry:
        // transfer _localId (keeps React key stable) and set _status: "confirmed"
        // so the user sees the double-checkmark briefly before it fades out.
        const taggedMessages = updatedMessages.map((m) => {
          const ts = m.MessageInfo.TimestampNanosString;
          const localTombstone = locallyDeletedByTs.get(ts);
          if (localTombstone) {
            // Server has caught up — use canonical server data
            if (m.MessageInfo?.ExtraData?.["msg:deleted"]) {
              return m;
            }
            // Server hasn't indexed the delete yet — keep local tombstone
            return localTombstone;
          }

          const key = msgDedupeKey(
            m.SenderInfo.OwnerPublicKeyBase58Check,
            m.DecryptedMessage,
            m.MessageInfo?.ExtraData
          );
          const localId = optimisticByKey.get(key);
          if (localId) {
            hasNewlyConfirmed = true;
            return { ...m, _status: "confirmed" as const, _localId: localId };
          }
          return m;
        });

        const merged = {
          ...prev,
          [selectedKey]: {
            ...existing,
            messages: [...stillPendingOptimistic, ...taggedMessages],
          },
        };

        if (appUser) {
          cacheConversations(appUser.PublicKeyBase58Check, merged);
          cacheConversationMessages(
            appUser.PublicKeyBase58Check,
            selectedKey,
            updatedMessages
          );
        }

        return merged;
      });

      // Strip confirmed tags after the CSS animation completes (pop-in 300ms +
      // hold + fade-out at 1.2s for 300ms = 1.5s total). The composite animation
      // on .status-confirm-enter handles the visual lifecycle purely in CSS,
      // so we only need this single state update to clean up.
      if (hasNewlyConfirmed) {
        setTimeout(() => {
          setConversations((prev) => {
            const conv = prev[selectedKey];
            if (!conv) return prev;
            const hasConfirmed = conv.messages.some(
              (m: any) => m._status === "confirmed"
            );
            if (!hasConfirmed) return prev;
            return {
              ...prev,
              [selectedKey]: {
                ...conv,
                messages: conv.messages.map((m: any) => {
                  if (m._status !== "confirmed") return m;
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { _status, _localId, ...rest } = m;
                  return rest;
                }),
              },
            };
          });
        }, 1500);
      }
    },
    [appUser]
  );

  // Expose the merge handler for Playwright tests (dev only). Declared here
  // rather than alongside the other setters because it depends on
  // mergeConversationUpdate, which is defined just above.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const hook = (window as any).__CHATON_MSG__;
    if (hook) hook.mergeConversationUpdate = mergeConversationUpdate;
  }, [mergeConversationUpdate]);

  // Lightweight merge for conversations we're not currently viewing:
  // keeps optimistic messages, layers in fresh blockchain data.
  // IMPORTANT: The `updated` map comes from getConversations() which only
  // carries the latest ~1 message per conversation. For the currently viewed
  // conversation we already have a full thread loaded, so we must NOT replace
  // its messages — only update metadata (firstMessagePublicKey, ChatType).
  const simpleConversationMerge = useCallback(
    (updated: ConversationMap) => {
      setConversations((prev) => {
        const merged = { ...prev };
        const currentKey = selectedConversationPublicKeyRef.current;
        for (const [key, convo] of Object.entries(updated)) {
          if (!merged[key]) {
            merged[key] = convo;
          } else if (key === currentKey) {
            // Preserve the full thread; only refresh conversation metadata
            merged[key] = { ...convo, messages: merged[key].messages };
          } else {
            const optimistic = merged[key].messages.filter(
              (m: any) => m._localId
            );
            merged[key] = {
              ...convo,
              messages: [...optimistic, ...convo.messages],
            };
          }
        }
        if (appUser) cacheConversations(appUser.PublicKeyBase58Check, merged);
        return merged;
      });
    },
    [appUser]
  );

  // Dedicated sender-side confirmation poller: after a message is sent, poll the
  // single conversation at short intervals to confirm the message on-chain quickly
  // instead of waiting for the general poll cycle (15s–5min).
  //
  // Coalesced per-conversation: rapid-fire sends share a single poller per conv
  // instead of spawning one poller per message, avoiding API call storms.
  const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pendingConfirmationsRef = useRef<
    Map<
      string,
      {
        localIds: Set<string>;
        chatType: ChatType;
        firstMessagePublicKey: string;
        recipientGroupKeyName: string;
        attempt: number;
      }
    >
  >(new Map());
  // Clean up confirmation timer on unmount
  useEffect(
    () => () => {
      if (confirmationTimerRef.current)
        clearTimeout(confirmationTimerRef.current);
    },
    []
  );

  const CONFIRM_MAX_ATTEMPTS = 8;
  const CONFIRM_INTERVAL_MS = 3000;

  const runConfirmationCheck = useCallback(async () => {
    confirmationTimerRef.current = null;
    if (!appUser) return;
    const myPk = appUser.PublicKeyBase58Check;
    const pending = pendingConfirmationsRef.current;
    if (pending.size === 0) return;

    // Process each conversation with pending confirmations
    const convKeysToRemove: string[] = [];
    for (const [convKey, entry] of pending) {
      entry.attempt++;
      try {
        // Remove localIds already confirmed by normal polling
        const conv = conversationsRef.current[convKey];
        if (!conv) {
          convKeysToRemove.push(convKey);
          continue;
        }
        const activeLocalIds = conv.messages
          .filter((m: any) => m._localId && entry.localIds.has(m._localId))
          .map((m: any) => m._localId as string);
        if (activeLocalIds.length === 0) {
          convKeysToRemove.push(convKey);
          continue;
        }
        // Update the set to only track still-pending messages
        entry.localIds = new Set(activeLocalIds);

        // Lightweight single-conversation fetch
        let decrypted: DecryptedMessageEntryResponse[];
        if (entry.chatType === ChatType.DM) {
          const resp = await getPaginatedDMThread({
            UserGroupOwnerPublicKeyBase58Check: myPk,
            UserGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            PartyGroupOwnerPublicKeyBase58Check: entry.firstMessagePublicKey,
            PartyGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
            StartTimeStamp: new Date().valueOf() * 1e6,
          });
          const result = await decryptAccessGroupMessagesWithRetry(
            myPk,
            resp.ThreadMessages,
            allAccessGroupsRef.current
          );
          decrypted = result.decrypted;
        } else {
          const resp = await getPaginatedGroupChatThread({
            UserPublicKeyBase58Check: entry.firstMessagePublicKey,
            AccessGroupKeyName: entry.recipientGroupKeyName,
            StartTimeStamp: new Date().valueOf() * 1e6,
            MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
          });
          const result = await decryptAccessGroupMessagesWithRetry(
            myPk,
            resp.GroupChatMessages,
            allAccessGroupsRef.current
          );
          decrypted = result.decrypted;
        }

        // Check if any pending messages now appear on-chain
        const currentConv = conversationsRef.current[convKey];
        if (!currentConv) {
          convKeysToRemove.push(convKey);
          continue;
        }

        const anyFound = currentConv.messages.some((m: any) => {
          if (!m._localId || !entry.localIds.has(m._localId)) return false;
          const targetKey = msgDedupeKey(
            m.SenderInfo.OwnerPublicKeyBase58Check,
            m.DecryptedMessage,
            m.MessageInfo?.ExtraData
          );
          return decrypted.some(
            (d) =>
              msgDedupeKey(
                d.SenderInfo.OwnerPublicKeyBase58Check,
                d.DecryptedMessage,
                d.MessageInfo?.ExtraData
              ) === targetKey
          );
        });

        if (anyFound) {
          // At least one message confirmed — merge full thread to reconcile all
          mergeConversationUpdate(
            {
              ...conversationsRef.current,
              [convKey]: { ...currentConv, messages: decrypted },
            },
            convKey,
            decrypted
          );
          // Remove confirmed localIds; mergeConversationUpdate handles the rest
          convKeysToRemove.push(convKey);
        } else if (entry.attempt >= CONFIRM_MAX_ATTEMPTS) {
          convKeysToRemove.push(convKey);
        }
      } catch {
        // Silently ignore — normal polling is the fallback
        if (entry.attempt >= CONFIRM_MAX_ATTEMPTS) {
          convKeysToRemove.push(convKey);
        }
      }
    }

    for (const key of convKeysToRemove) {
      pending.delete(key);
    }

    // Schedule next tick if there are still pending confirmations
    if (pending.size > 0) {
      confirmationTimerRef.current = setTimeout(
        runConfirmationCheck,
        CONFIRM_INTERVAL_MS
      );
    }
  }, [appUser, mergeConversationUpdate]);

  const scheduleConfirmationCheck = useCallback(
    (
      convKey: string,
      localId: string,
      chatType: ChatType,
      firstMessagePublicKey: string,
      recipientGroupKeyName: string
    ) => {
      const pending = pendingConfirmationsRef.current;
      const existing = pending.get(convKey);
      if (existing) {
        // Coalesce: add this message to the existing conversation poller
        existing.localIds.add(localId);
      } else {
        pending.set(convKey, {
          localIds: new Set([localId]),
          chatType,
          firstMessagePublicKey,
          recipientGroupKeyName,
          attempt: 0,
        });
      }

      // Ensure the shared timer is running
      if (!confirmationTimerRef.current) {
        confirmationTimerRef.current = setTimeout(
          runConfirmationCheck,
          CONFIRM_INTERVAL_MS
        );
      }
    },
    [runConfirmationCheck]
  );

  // Guard against concurrent WS-triggered fetches (e.g. rapid-fire notifications)
  const wsFetchingRef = useRef(false);
  // Guard against overlapping polling ticks (setInterval fires even if previous callback is still async)
  const pollingRef = useRef(false);
  const welcomeMessageSentRef = useRef(false);

  // Real-time WebSocket relay
  const handleWsNewMessage = useCallback(
    (threadId: string, _from: string) => {
      if (!appUser || wsFetchingRef.current) return;
      wsFetchingRef.current = true;

      // WS is delivering messages — keep the slow safety-net rate.
      // No need to reset to base delay since real-time is handled by WS.

      getConversationsDifferential(
        appUser.PublicKeyBase58Check,
        allAccessGroups
      )
        .then(
          async ({
            conversations: updated,
            updatedAllAccessGroups,
            publicKeyToProfileEntryResponseMap,
          }) => {
            setAllAccessGroups(updatedAllAccessGroups);

            // Update username + profile pic maps from fresh profile data
            const freshUsernames: Record<string, string> = {};
            const freshPics: Record<string, string> = {};
            for (const [pk, profile] of Object.entries(
              publicKeyToProfileEntryResponseMap
            )) {
              if (profile?.Username) freshUsernames[pk] = profile.Username;
              const ed = profile?.ExtraData;
              if (ed) {
                const picUrl = ed.NFTProfilePictureUrl || ed.LargeProfilePicURL;
                if (picUrl) freshPics[pk] = picUrl;
              }
            }
            if (Object.keys(freshUsernames).length) {
              mergeUsernames(freshUsernames);
            }
            if (Object.keys(freshPics).length) {
              setProfilePicByPublicKey((s) => ({ ...s, ...freshPics }));
            }
            // Merge sender profiles for spam filter classification
            setSenderProfiles((prev) => {
              const next = new Map(prev);
              for (const [pk, profile] of Object.entries(
                publicKeyToProfileEntryResponseMap
              )) {
                if (profile) next.set(pk, profile);
              }
              return next;
            });

            const currentSelectedKey = selectedConversationPublicKeyRef.current;

            // The sender passes their own conversation key as threadId, but for
            // DMs the recipient indexes the same conversation under the sender's
            // public key. Resolve to the local key so comparisons and unread
            // tracking use the correct key.
            let localThreadId = threadId;
            if (threadId && !updated[threadId] && _from) {
              const candidateKey = _from + DEFAULT_KEY_MESSAGING_GROUP_NAME;
              if (updated[candidateKey]) {
                localThreadId = candidateKey;
              }
            }

            if (localThreadId && localThreadId === currentSelectedKey) {
              // Notification is for the conversation we're looking at —
              // fetch the full thread so new messages appear immediately.
              try {
                const { updatedConversations, pubKeyPlusGroupName: newPubKey } =
                  await getConversation(currentSelectedKey, {
                    ...updated,
                    [currentSelectedKey]: updated[currentSelectedKey]!,
                  });

                // Guard: user may have switched conversations during the await —
                // still merge the conversation list so new chats appear, but skip
                // the full-thread merge and pubKeyPlusGroupName update.
                if (
                  selectedConversationPublicKeyRef.current !==
                  currentSelectedKey
                ) {
                  simpleConversationMerge(updated);
                  return;
                }

                const updatedMessages =
                  updatedConversations[currentSelectedKey]?.messages;
                if (updatedMessages) {
                  mergeConversationUpdate(
                    updatedConversations,
                    currentSelectedKey,
                    updatedMessages
                  );
                  setPubKeyPlusGroupName(newPubKey);
                  // Update last-read since user is viewing this conversation
                  if (updatedMessages[0]) {
                    const ts = updatedMessages[0].MessageInfo.TimestampNanos;
                    cacheLastReadTimestamp(
                      appUser.PublicKeyBase58Check,
                      currentSelectedKey,
                      ts
                    );
                    sendRead(currentSelectedKey, String(ts));
                  }
                }
              } catch {
                // Fall back to the simple conversation-list merge
                simpleConversationMerge(updated);
              }
            } else {
              // Different conversation — just merge the conversation list
              simpleConversationMerge(updated);

              const isDmConvo =
                updated[localThreadId]?.ChatType === ChatType.DM;
              const isSelfMessage =
                _from && _from === appUser.PublicKeyBase58Check;
              if (
                localThreadId &&
                !isSelfMessage &&
                !useStore.getState().mutedConversations.has(localThreadId) &&
                !useStore.getState().archivedGroups.has(localThreadId) &&
                !(
                  isDmConvo &&
                  _from &&
                  useStore.getState().blockedUsers.has(_from)
                ) &&
                !(
                  isDmConvo &&
                  _from &&
                  useStore.getState().dismissedUsers.has(_from)
                ) &&
                !(
                  isDmConvo &&
                  _from &&
                  useStore.getState().archivedChats.has(_from)
                )
              ) {
                useStore.getState().incrementUnread(localThreadId);
              } else if (!localThreadId) {
                // Resume from background — fetch full thread and compute
                // unread in parallel (they're independent operations).
                const threadPromise =
                  currentSelectedKey && updated[currentSelectedKey]
                    ? getConversation(currentSelectedKey, {
                        ...updated,
                        [currentSelectedKey]: updated[currentSelectedKey],
                      })
                        .then(
                          ({
                            updatedConversations,
                            pubKeyPlusGroupName: newPubKey,
                          }) => {
                            // Guard: user may have switched conversations during the await
                            if (
                              selectedConversationPublicKeyRef.current !==
                              currentSelectedKey
                            )
                              return;
                            const updatedMessages =
                              updatedConversations[currentSelectedKey]
                                ?.messages;
                            if (updatedMessages) {
                              mergeConversationUpdate(
                                updatedConversations,
                                currentSelectedKey,
                                updatedMessages
                              );
                              setPubKeyPlusGroupName(newPubKey);
                              if (updatedMessages[0]) {
                                const ts =
                                  updatedMessages[0].MessageInfo.TimestampNanos;
                                cacheLastReadTimestamp(
                                  appUser.PublicKeyBase58Check,
                                  currentSelectedKey,
                                  ts
                                );
                                sendRead(currentSelectedKey, String(ts));
                              }
                            }
                          }
                        )
                        .catch(() => {
                          // Full thread fetch failed — simple merge already applied above
                        })
                    : Promise.resolve();

                // Compute unread from timestamps (no await needed — sync reads)
                const lastReadTimestamps = getCachedLastReadTimestamps(
                  appUser.PublicKeyBase58Check
                );
                const store = useStore.getState();
                const unreadMap = new Map<string, number>();
                for (const [k, convo] of Object.entries(updated)) {
                  if (k === currentSelectedKey) continue;
                  if (
                    store.mutedConversations.has(k) ||
                    store.archivedGroups.has(k)
                  )
                    continue;
                  const msgTs = latestIncomingTimestamp(
                    convo,
                    appUser.PublicKeyBase58Check
                  );
                  if (!msgTs) continue;
                  const lastRead = lastReadTimestamps[k];
                  if (lastRead === undefined) {
                    cacheLastReadTimestamp(
                      appUser.PublicKeyBase58Check,
                      k,
                      msgTs
                    );
                  } else if (msgTs > lastRead) {
                    unreadMap.set(k, 1);
                  }
                }
                syncUnreadConversations(Array.from(unreadMap.keys()));
                if (unreadMap.size > 0) {
                  useStore.getState().initializeUnread(unreadMap);
                } else {
                  navigator.clearAppBadge?.()?.catch?.(() => {});
                }

                // Wait for thread fetch before releasing wsFetchingRef
                await threadPromise;
              }
            }
          }
        )
        .catch(() => {
          // WS-triggered differential fetch failed — temporarily reset polling
          // to base rate so we retry soon instead of waiting 5 minutes.
          // Only reset if user is active — don't override idle pause.
          if (!isIdleRef.current) {
            setPollingDelay(baseDelay);
          }
        })
        .finally(() => {
          wsFetchingRef.current = false;
        });
    },
    [
      appUser,
      allAccessGroups,
      mergeConversationUpdate,
      simpleConversationMerge,
      baseDelay,
    ]
  );

  const { onTypingReceived, getTypingUsersForConversation } =
    useTypingDisplay();

  const setOnlineUsers = useStore((s) => s.setOnlineUsers);
  const { getPresence, getOnlineCount, fetchPresenceForKeys } = usePresence();

  const {
    sendNotify,
    sendTyping,
    sendRead,
    sendReadSyncInit,
    isConnected: wsConnected,
  } = useWebSocket({
    onNewMessage: handleWsNewMessage,
    onTyping: onTypingReceived,
    onPresence: (users) => {
      setOnlineUsers(new Set(Object.keys(users)));
    },
    onReadSync: (conversationKey, timestamp) => {
      // Another device read this conversation — update local state
      const user = useStore.getState().appUser;
      if (!user) return;
      const ts = Number(timestamp);
      cacheLastReadTimestamp(user.PublicKeyBase58Check, conversationKey, ts);
      // Clear unread if cursor >= latest incoming message
      const convo = conversationsRef.current[conversationKey];
      const incomingTs = convo
        ? latestIncomingTimestamp(convo, user.PublicKeyBase58Check)
        : 0;
      if (incomingTs && ts >= incomingTs) {
        clearUnread(conversationKey);
        removeUnreadConversation(conversationKey);
      }
    },
    onReadSyncBulk: (cursors) => {
      const user = useStore.getState().appUser;
      if (!user) return;
      const pk = user.PublicKeyBase58Check;
      const { merged, localNewer } = mergeReadCursors(pk, cursors);
      // Send back any cursors that are newer locally
      if (Object.keys(localNewer).length > 0) {
        sendReadSyncInit(localNewer);
      }
      // Recompute unread badges from merged cursors
      const store = useStore.getState();
      const convos = conversationsRef.current;
      const unreadMap = new Map<string, number>();
      // Track conversations we positively confirmed as read (cursor >= latest
      // incoming message). Only these should have their badges cleared —
      // skipping a conversation (no messages, missing cursor, etc.) must NOT
      // remove an existing badge set by incrementUnread.
      const confirmedRead = new Set<string>();
      const currentKey = selectedConversationPublicKeyRef.current;
      for (const [k, convo] of Object.entries(convos)) {
        if (k === currentKey) continue; // Viewing — already read
        if (store.mutedConversations.has(k) || store.archivedGroups.has(k))
          continue;
        const msgTs = latestIncomingTimestamp(convo, pk);
        if (!msgTs) continue; // No incoming messages — nothing to mark unread
        const lastRead = merged[k];
        if (lastRead === undefined) continue; // No cursor — preserve existing state
        if (msgTs > lastRead) {
          unreadMap.set(k, 1);
        } else {
          confirmedRead.add(k);
        }
      }
      // Only clear conversations we have positive evidence are read
      for (const existingKey of store.unreadByConversation.keys()) {
        if (confirmedRead.has(existingKey)) {
          clearUnread(existingKey);
          removeUnreadConversation(existingKey);
        }
      }
      syncUnreadConversations(Array.from(unreadMap.keys()));
      if (unreadMap.size > 0) {
        initializeUnread(unreadMap);
      }
    },
  });

  const { onKeystroke } = useTypingIndicator(sendTyping);

  // When WebSocket is connected, slow polling to a safety-net interval (5 min).
  // When it disconnects, resume normal polling so we don't miss messages.
  useEffect(() => {
    if (isIdle) return; // idle effect handles pause
    setPollingDelay(wsConnected ? REFRESH_MESSAGES_WS_CONNECTED_MS : baseDelay);
  }, [wsConnected, baseDelay, isIdle]);

  // Derive classified conversation maps from the single conversations state + store Sets
  const { chatConversations, requestConversations, archivedConversations } =
    useMemo(() => {
      if (!appUser)
        return {
          chatConversations: {} as ConversationMap,
          requestConversations: {} as ConversationMap,
          archivedConversations: {} as ConversationMap,
        };

      const myPk = appUser.PublicKeyBase58Check;

      // Pre-migration: the group request feature requires chaton:group-accepted
      // associations to exist for each group the user has consented to. Before
      // migration runs (one-time background job), treat ALL existing groups as
      // implicitly accepted so they stay in Chats, not Requests.
      const migrated = !!localStorage.getItem(
        `chaton:group-accept-migrated-v2:${myPk}`
      );

      // Pre-compute which groups the user has sent a message in (implicit acceptance)
      const groupsWithMyMessages = new Set<string>();
      for (const [key, convo] of Object.entries(conversations)) {
        if (convo.ChatType !== ChatType.GROUPCHAT) continue;
        if (!migrated) {
          // Pre-migration: treat all existing groups as implicitly accepted
          groupsWithMyMessages.add(key);
          continue;
        }
        if (
          convo.messages?.some(
            (m) =>
              m.IsSender || m.SenderInfo?.OwnerPublicKeyBase58Check === myPk
          )
        ) {
          groupsWithMyMessages.add(key);
        }
      }

      const chats: ConversationMap = {};
      const requests: ConversationMap = {};
      const archived: ConversationMap = {};
      for (const [key, convo] of Object.entries(conversations)) {
        const cls = classifyConversation(
          convo,
          key,
          myPk,
          followedUsers,
          approvedUsers,
          blockedUsers,
          initiatedChats,
          archivedGroups,
          archivedChats,
          dismissedUsers,
          paidUsers,
          spamFilter,
          senderProfiles,
          acceptedGroups,
          groupsWithMyMessages
        );
        if (cls === "chat") chats[key] = convo;
        else if (cls === "request") requests[key] = convo;
        else if (cls === "archived") archived[key] = convo;
        // "blocked" and "dismissed" → omitted
      }
      return {
        chatConversations: chats,
        requestConversations: requests,
        archivedConversations: archived,
      };
    }, [
      conversations,
      followedUsers,
      approvedUsers,
      blockedUsers,
      initiatedChats,
      archivedGroups,
      acceptedGroups,
      archivedChats,
      dismissedUsers,
      paidUsers,
      appUser,
      spamFilter,
      senderProfiles,
    ]);

  // One-time migration: create chaton:group-accepted associations for all
  // existing group memberships so the group request feature works going forward.
  // Runs in background, gated by localStorage so it only happens once per user.
  // Key is versioned (v2) because v1 had a bug that set the flag prematurely
  // when conversations hadn't loaded yet.
  useEffect(() => {
    if (!appUser || !chatRequestsLoaded) return;
    // Wait until conversations have actually loaded (non-empty)
    if (Object.keys(conversations).length === 0) return;

    const pk = appUser.PublicKeyBase58Check;
    const migrationKey = `chaton:group-accept-migrated-v2:${pk}`;
    if (localStorage.getItem(migrationKey)) return;

    const groupsToMigrate = Object.entries(conversations).filter(
      ([key, convo]) =>
        convo.ChatType === ChatType.GROUPCHAT &&
        !archivedGroups.has(key) &&
        convo.firstMessagePublicKey !== pk &&
        !acceptedGroups.has(key)
    );

    if (groupsToMigrate.length === 0) {
      localStorage.setItem(migrationKey, "1");
      return;
    }

    // Fire-and-forget: create associations in background, don't block UI
    Promise.all(
      groupsToMigrate.map(([key]) => {
        const ownerPk = key.slice(0, PUBLIC_KEY_LENGTH);
        const gkn = key.slice(PUBLIC_KEY_LENGTH);
        return createGroupAcceptedAssociation(pk, ownerPk, gkn).catch(() => {});
      })
    ).then(() => {
      localStorage.setItem(migrationKey, "1");
      // Refresh accepted groups so next classification uses real associations
      fetchAcceptedGroups(pk)
        .then((ids) => mergeAcceptedGroupIds(ids))
        .catch(() => {});
    });
  }, [
    appUser,
    chatRequestsLoaded,
    conversations,
    archivedGroups,
    acceptedGroups,
  ]);

  // DeSo messaging APIs may return 0 for DESOBalanceNanos in profile responses.
  // When the spam filter needs balance checking, fetch real balances via
  // getUsersStateless and update senderProfiles with the accurate data.
  useEffect(() => {
    if (!spamFilter?.enabled || !spamFilter.minBalanceNanos) return;

    const keysToFetch = [...senderProfiles.keys()].filter(
      (k) => !fetchedBalanceKeysRef.current.has(k)
    );
    if (keysToFetch.length === 0) return;

    // Mark as pending immediately to avoid duplicate requests
    for (const k of keysToFetch) fetchedBalanceKeysRef.current.add(k);

    getUsersStateless({
      PublicKeysBase58Check: keysToFetch,
      SkipForLeaderboard: true,
    })
      .then((res) => {
        setSenderProfiles((prev) => {
          const next = new Map(prev);
          let changed = false;
          for (const u of res.UserList ?? []) {
            if (u.ProfileEntryResponse) {
              const existing = next.get(u.PublicKeyBase58Check);
              if (
                !existing ||
                existing.DESOBalanceNanos !==
                  u.ProfileEntryResponse.DESOBalanceNanos
              ) {
                next.set(u.PublicKeyBase58Check, u.ProfileEntryResponse);
                changed = true;
              }
            }
          }
          return changed ? next : prev;
        });
      })
      .catch(() => {
        // Allow retry on failure
        for (const k of keysToFetch) fetchedBalanceKeysRef.current.delete(k);
      });
  }, [spamFilter, senderProfiles]);

  // Compute per-conversation highlights: unread @mentions and reactions to my messages.
  // Uses the most recent messages loaded in each conversation (the preview page).
  // rerender-dependencies: depend on primitive publicKey, not the appUser object ref.
  const myPublicKey = appUser?.PublicKeyBase58Check;
  const highlightsByConversation = useMemo(() => {
    if (!myPublicKey)
      return new Map<string, { hasMention: boolean; hasReaction: boolean }>();
    const lastReadTimestamps = getCachedLastReadTimestamps(myPublicKey);
    const result = new Map<
      string,
      { hasMention: boolean; hasReaction: boolean }
    >();
    for (const [key, convo] of Object.entries(conversations)) {
      const lastRead = lastReadTimestamps[key];
      if (lastRead === undefined) continue;
      let hasMention = false;
      let hasReaction = false;
      // Build set of my message timestamps for reaction target lookup
      const myTimestamps = new Set<string>();
      for (const msg of convo.messages) {
        if (
          msg.IsSender ||
          msg.SenderInfo.OwnerPublicKeyBase58Check === myPublicKey
        ) {
          myTimestamps.add(msg.MessageInfo.TimestampNanosString);
        }
      }
      for (const msg of convo.messages) {
        if (msg.MessageInfo.TimestampNanos <= lastRead) break; // sorted newest-first
        if (
          msg.IsSender ||
          msg.SenderInfo.OwnerPublicKeyBase58Check === myPublicKey
        )
          continue;
        const parsed = parseMessageType(msg);
        if (
          !hasMention &&
          parsed.mentions &&
          parsed.mentions.some((m) => m.pk === myPublicKey)
        ) {
          hasMention = true;
        }
        if (
          !hasReaction &&
          parsed.type === "reaction" &&
          parsed.replyTo &&
          parsed.action !== "remove" &&
          myTimestamps.has(parsed.replyTo)
        ) {
          hasReaction = true;
        }
        if (hasMention && hasReaction) break;
      }
      if (hasMention || hasReaction) {
        result.set(key, { hasMention, hasReaction });
      }
    }
    return result;
  }, [conversations, myPublicKey, unreadByConversation]); // unreadByConversation triggers recompute on new messages

  // Clean up UI state after a conversation's classification changes
  const cleanupAfterClassificationChange = (conversationKey: string) => {
    // Clear selection if the user is viewing this conversation
    if (selectedConversationPublicKey === conversationKey) {
      setSelectedConversationPublicKey("");
    }
    // Clear any unread badge for this conversation
    clearUnread(conversationKey);
    removeUnreadConversation(conversationKey);
    // Clear search results so stale conversations don't appear
    if (searchQuery) {
      clearSearch();
      setSearchClearTrigger((n) => n + 1);
    }
  };

  const handleAcceptRequest = async (
    conversationKey: string,
    publicKey: string
  ) => {
    if (!appUser) return;
    approveUser(publicKey);
    // Clear search so the conversation appears in the right tab
    if (searchQuery) {
      clearSearch();
      setSearchClearTrigger((n) => n + 1);
    }
    try {
      await createApprovalAssociation(appUser.PublicKeyBase58Check, publicKey);
    } catch {
      rollbackApproval(publicKey);
      toast.error("Failed to accept chat request");
    }
  };

  const handleAcceptGroupRequest = async (conversationKey: string) => {
    if (!appUser) return;
    acceptGroup(conversationKey);
    if (searchQuery) {
      clearSearch();
      setSearchClearTrigger((n) => n + 1);
    }
    const ownerKey = conversationKey.slice(0, PUBLIC_KEY_LENGTH);
    const groupKeyName = conversationKey.slice(PUBLIC_KEY_LENGTH);
    try {
      await createGroupAcceptedAssociation(
        appUser.PublicKeyBase58Check,
        ownerKey,
        groupKeyName
      );
      fetchAcceptedGroups(appUser.PublicKeyBase58Check)
        .then((ids) => mergeAcceptedGroupIds(ids))
        .catch(() => {});
    } catch {
      rollbackAcceptGroup(conversationKey);
      toast.error("Failed to accept group");
    }
  };

  const handleLeaveGroupRequest = async (conversationKey: string) => {
    if (!appUser) return;
    archiveGroup(conversationKey);
    cleanupAfterClassificationChange(conversationKey);
    const ownerKey = conversationKey.slice(0, PUBLIC_KEY_LENGTH);
    const groupKeyName = conversationKey.slice(PUBLIC_KEY_LENGTH);
    try {
      await createArchiveAssociation(
        appUser.PublicKeyBase58Check,
        ownerKey,
        groupKeyName
      );
      toast("Left group");
      fetchArchivedGroups(appUser.PublicKeyBase58Check)
        .then((ids) => mergeArchivedGroupIds(ids))
        .catch(() => {});
    } catch {
      rollbackArchive(conversationKey);
      toast.error("Failed to leave group");
    }
  };

  const handleBlockRequest = async (
    conversationKey: string,
    publicKey: string
  ) => {
    if (!appUser) return;
    blockUser(publicKey);
    cleanupAfterClassificationChange(conversationKey);
    try {
      await createBlockAssociation(appUser.PublicKeyBase58Check, publicKey);
      toast("User blocked", {
        action: { label: "Undo", onClick: () => handleUnblock(publicKey) },
      });
      // Background-fetch the association ID so unblock works
      fetchAssociationsByType(
        appUser.PublicKeyBase58Check,
        ASSOCIATION_TYPE_BLOCKED
      )
        .then((ids) => {
          const current = useStore.getState().blockedAssociationIds;
          const next = new Map(current);
          for (const [k, v] of ids) next.set(k, v);
          useStore.setState({ blockedAssociationIds: next });
        })
        .catch(() => {});
    } catch {
      rollbackBlock(publicKey);
      toast.error("Failed to block user");
    }
  };

  const handleArchiveGroup = async (
    conversationKey: string,
    groupOwnerPublicKey: string,
    groupKeyName: string
  ) => {
    if (!appUser) return;

    // Best-effort: send "left" system message while still a member
    const username =
      appUser.ProfileEntryResponse?.Username ||
      appUser.PublicKeyBase58Check.slice(0, 8);
    sendSystemMessage(
      appUser.PublicKeyBase58Check,
      groupOwnerPublicKey,
      groupKeyName,
      "member-left",
      [{ pk: appUser.PublicKeyBase58Check, un: username }]
    );

    archiveGroup(conversationKey);
    cleanupAfterClassificationChange(conversationKey);
    try {
      await createArchiveAssociation(
        appUser.PublicKeyBase58Check,
        groupOwnerPublicKey,
        groupKeyName
      );
      toast("Left group chat", {
        action: {
          label: "Undo",
          onClick: () => handleUnarchiveGroup(conversationKey),
        },
      });
      // Background: fetch the association ID so Rejoin works without page refresh
      fetchArchivedGroups(appUser.PublicKeyBase58Check)
        .then((ids) => mergeArchivedGroupIds(ids))
        .catch(() => {});
    } catch {
      rollbackArchive(conversationKey);
      toast.error("Failed to leave group");
    }
  };

  const handleOptimisticSystemMessage = useCallback(
    (members: MentionEntry[]) => {
      if (!appUser) return;
      const key = selectedConversationPublicKeyRef.current;
      if (!key) return;

      const names = members.map((m) => m.un || m.pk.slice(0, 8));
      const label =
        names.length <= 3
          ? names.join(", ")
          : `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
      const fallback = `${label} joined the group`;

      const TimestampNanos = Date.now() * 1e6;
      const localId = `sys-${crypto.randomUUID()}`;

      setConversations((prev) => {
        const c = prev[key];
        if (!c?.messages.length) return prev;
        const recipient = c.messages[0]!.RecipientInfo;

        const mockMessage = {
          DecryptedMessage: fallback,
          IsSender: true,
          _status: "sending" as const,
          _localId: localId,
          SenderInfo: {
            OwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
            AccessGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
          },
          RecipientInfo: {
            OwnerPublicKeyBase58Check: recipient.OwnerPublicKeyBase58Check,
            AccessGroupKeyName: recipient.AccessGroupKeyName,
          },
          MessageInfo: {
            TimestampNanos,
            TimestampNanosString: String(TimestampNanos),
            ExtraData: buildExtraData({
              type: "system",
              systemAction: "member-joined",
              systemMembers: members,
            }),
          },
        } as DecryptedMessageEntryResponse & {
          _status: string;
          _localId: string;
        };

        return {
          ...prev,
          [key]: { ...c, messages: [mockMessage, ...c.messages] },
        };
      });
    },
    [appUser]
  );

  const handleUnarchiveGroup = async (conversationKey: string) => {
    if (!appUser) return;
    const associationId = useStore
      .getState()
      .archivedGroupAssociationIds.get(conversationKey);
    if (!associationId) {
      toast.error("Cannot rejoin — association not found");
      return;
    }
    unarchiveGroup(conversationKey);
    try {
      await deleteArchiveAssociation(
        appUser.PublicKeyBase58Check,
        associationId
      );
      toast.success("Rejoined group chat");
    } catch {
      rollbackUnarchive(conversationKey, associationId);
      toast.error("Failed to rejoin group");
    }
  };

  // ── Archive DM chat ──
  const handleArchiveChat = async (
    conversationKey: string,
    publicKey: string
  ) => {
    if (!appUser) return;
    archiveChat(publicKey);
    cleanupAfterClassificationChange(conversationKey);
    try {
      await createArchiveChatAssociation(
        appUser.PublicKeyBase58Check,
        publicKey
      );
      toast("Chat archived", {
        action: {
          label: "Undo",
          onClick: () => handleUnarchiveChat(publicKey),
        },
      });
      // Background-fetch the association ID so unarchive works
      fetchAssociationsByType(
        appUser.PublicKeyBase58Check,
        ASSOCIATION_TYPE_CHAT_ARCHIVED
      )
        .then((ids) => mergeArchivedChatIds(ids))
        .catch(() => {});
    } catch {
      rollbackArchiveChat(publicKey);
      toast.error("Failed to archive chat");
    }
  };

  const handleUnarchiveChat = async (publicKey: string) => {
    if (!appUser) return;
    // Read latest from store — not the stale closure — since the ID arrives via background fetch
    const associationId = useStore
      .getState()
      .archivedChatAssociationIds.get(publicKey);
    if (!associationId) {
      toast.error(
        "Cannot unarchive — association not found. Try again in a moment."
      );
      return;
    }
    unarchiveChat(publicKey);
    try {
      await deleteAssociationById(appUser.PublicKeyBase58Check, associationId);
      toast.success("Chat unarchived");
    } catch {
      rollbackUnarchiveChat(publicKey, associationId);
      toast.error("Failed to unarchive chat");
    }
  };

  // ── Dismiss request ──
  const handleDismissRequest = async (
    conversationKey: string,
    publicKey: string
  ) => {
    if (!appUser) return;
    dismissUser(publicKey);
    cleanupAfterClassificationChange(conversationKey);
    try {
      await createDismissAssociation(appUser.PublicKeyBase58Check, publicKey);
      toast("Request dismissed", {
        action: { label: "Undo", onClick: () => handleUndismiss(publicKey) },
      });
      // Background-fetch the association ID so undo works
      fetchAssociationsByType(
        appUser.PublicKeyBase58Check,
        ASSOCIATION_TYPE_DISMISSED
      )
        .then((ids) => mergeDismissedIds(ids))
        .catch(() => {});
    } catch {
      rollbackDismiss(publicKey);
      toast.error("Failed to dismiss request");
    }
  };

  const handleUndismiss = async (publicKey: string) => {
    if (!appUser) return;
    const associationId = useStore
      .getState()
      .dismissedAssociationIds.get(publicKey);
    if (!associationId) {
      toast.error(
        "Cannot undo — association not found. Try again in a moment."
      );
      return;
    }
    undismissUser(publicKey);
    try {
      await deleteAssociationById(appUser.PublicKeyBase58Check, associationId);
      toast.success("Request restored");
    } catch {
      rollbackUndismiss(publicKey, associationId);
      toast.error("Failed to restore request");
    }
  };

  // ── Unblock user ──
  const handleUnblock = async (publicKey: string) => {
    if (!appUser) return;
    const associationId = useStore
      .getState()
      .blockedAssociationIds.get(publicKey);
    if (!associationId) {
      toast.error(
        "Cannot unblock — association not found. Try again in a moment."
      );
      return;
    }
    unblockUser(publicKey);
    try {
      await deleteAssociationById(appUser.PublicKeyBase58Check, associationId);
      toast.success("User unblocked");
    } catch {
      rollbackUnblock(publicKey, associationId);
      toast.error("Failed to unblock user");
    }
  };

  // Retry a single pending message (used by startup retry and the retry button).
  // When isAutoRetry=true, a failure bumps the on-disk retryCount so the next
  // boot skips auto-retry and prompts the user instead.
  const retryingIds = useRef(new Set<string>());
  const retryPendingMessage = useCallback(
    async (pending: PendingMessage, opts?: { isAutoRetry?: boolean }) => {
      if (!appUser) return;
      const isAutoRetry = opts?.isAutoRetry === true;
      const {
        localId,
        conversationKey,
        messageText,
        recipientPublicKey,
        recipientAccessGroupKeyName,
        extraData,
        senderPublicKey,
      } = pending;

      // Don't retry messages from a different account
      if (senderPublicKey && senderPublicKey !== appUser.PublicKeyBase58Check)
        return;

      // Guard against concurrent retries for the same message
      if (retryingIds.current.has(localId)) return;
      retryingIds.current.add(localId);

      // Insert optimistic message into conversations if not already present
      const TimestampNanos = pending.createdAt * 1e6;
      const mockMessage = {
        DecryptedMessage: messageText,
        IsSender: true,
        _status: "sending" as const,
        _localId: localId,
        SenderInfo: {
          OwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
          AccessGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
        },
        RecipientInfo: {
          OwnerPublicKeyBase58Check: recipientPublicKey,
          AccessGroupKeyName: recipientAccessGroupKeyName,
        },
        MessageInfo: {
          TimestampNanos,
          TimestampNanosString: String(TimestampNanos),
          ExtraData: extraData || {},
        },
      } as DecryptedMessageEntryResponse & {
        _status: string;
        _localId: string;
      };

      // Check if this message already landed on-chain (app closed after
      // blockchain accepted but before we could remove from IndexedDB).
      // Uses the same dedup key as the poll reconciliation.
      const dedupeKey = msgDedupeKey(
        appUser.PublicKeyBase58Check,
        messageText,
        extraData
      );
      const convo = conversationsRef.current[conversationKey];
      if (convo) {
        const alreadyOnChain = convo.messages.some(
          (m: any) =>
            !m._localId &&
            msgDedupeKey(
              m.SenderInfo.OwnerPublicKeyBase58Check,
              m.DecryptedMessage,
              m.MessageInfo?.ExtraData
            ) === dedupeKey
        );
        if (alreadyOnChain) {
          // Message already confirmed — just clean up
          removePendingMessage(appUser.PublicKeyBase58Check, localId);
          // Remove the optimistic entry if it's lingering in state
          setConversations((prev) => {
            if (!prev[conversationKey]) return prev;
            return {
              ...prev,
              [conversationKey]: {
                ...prev[conversationKey],
                messages: prev[conversationKey].messages.filter(
                  (m: any) => m._localId !== localId
                ),
              },
            };
          });
          retryingIds.current.delete(localId);
          return;
        }
      }

      // Upsert: update existing failed message or insert fresh.
      // If the conversation hasn't loaded yet, create a minimal placeholder so
      // the retry still fires the blockchain call.
      setConversations((prev) => {
        const c = prev[conversationKey];
        if (!c) {
          return {
            ...prev,
            [conversationKey]: {
              ChatType: ChatType.DM,
              firstMessagePublicKey: recipientPublicKey,
              messages: [mockMessage],
            },
          };
        }
        const exists = c.messages.some((m: any) => m._localId === localId);
        return {
          ...prev,
          [conversationKey]: {
            ...c,
            messages: exists
              ? c.messages.map((m: any) =>
                  m._localId === localId ? { ...m, _status: "sending" } : m
                )
              : [mockMessage, ...c.messages],
          },
        };
      });

      try {
        await encryptAndSendNewMessage(
          messageText,
          appUser.PublicKeyBase58Check,
          recipientPublicKey,
          recipientAccessGroupKeyName,
          DEFAULT_KEY_MESSAGING_GROUP_NAME,
          extraData
        );
        removePendingMessage(appUser.PublicKeyBase58Check, localId);
        notifyConversation(
          conversationKey,
          recipientPublicKey,
          usernameByPublicKeyBase58Check[appUser.PublicKeyBase58Check] ||
            appUser.PublicKeyBase58Check
        );
        setConversations((prev) => {
          if (!prev[conversationKey]) return prev;
          return {
            ...prev,
            [conversationKey]: {
              ...prev[conversationKey],
              messages: prev[conversationKey].messages.map((m: any) =>
                m._localId === localId ? { ...m, _status: "sent" } : m
              ),
            },
          };
        });
        // Start dedicated confirmation poller for this message
        const retryConv = conversationsRef.current[conversationKey];
        scheduleConfirmationCheck(
          conversationKey,
          localId,
          retryConv?.ChatType ?? ChatType.DM,
          recipientPublicKey,
          recipientAccessGroupKeyName
        );
      } catch (e: any) {
        const errorStr = e?.toString() || "Unknown error";

        const isPermanent =
          /TxnTooBig|TxnTooLarge|MaxMessageLength|EncryptedTextLengthExceedsMax/i.test(
            errorStr
          );

        setConversations((prev) => {
          if (!prev[conversationKey]) return prev;
          return {
            ...prev,
            [conversationKey]: {
              ...prev[conversationKey],
              messages: prev[conversationKey].messages.map((m: any) =>
                m._localId === localId ? { ...m, _status: "failed" } : m
              ),
            },
          };
        });

        // Track failed auto-retries so the next boot prompts the user
        // instead of silently retrying again.
        if (isAutoRetry) {
          incrementPendingMessageRetryCount(
            appUser.PublicKeyBase58Check,
            localId
          );
        }

        if (isPermanent) {
          toast.error("Message too large to send.", {
            duration: Infinity,
            action: {
              label: "Delete",
              onClick: () => {
                removePendingMessage(appUser.PublicKeyBase58Check, localId);
                setConversations((prev) => {
                  if (!prev[conversationKey]) return prev;
                  return {
                    ...prev,
                    [conversationKey]: {
                      ...prev[conversationKey],
                      messages: prev[conversationKey].messages.filter(
                        (m: any) => m._localId !== localId
                      ),
                    },
                  };
                });
              },
            },
          });
        } else if (!isAutoRetry) {
          // User-initiated retry failed — surface the error. For auto-retries
          // we stay quiet and let the startup prompt handle it.
          toast.error(`Failed to send message: ${errorStr}`);
        }
      } finally {
        retryingIds.current.delete(localId);
      }
    },
    [appUser, sendNotify, usernameByPublicKeyBase58Check]
  );

  // Retry any pending messages from IndexedDB on startup (or user switch)
  const pendingRetryRanForUser = useRef<string | null>(null);
  useEffect(() => {
    if (!appUser || conversationsLoading) return;
    if (pendingRetryRanForUser.current === appUser.PublicKeyBase58Check) return;
    pendingRetryRanForUser.current = appUser.PublicKeyBase58Check;

    getPendingMessages(appUser.PublicKeyBase58Check).then((pendingMsgs) => {
      if (pendingMsgs.length === 0) return;

      // Partition: fresh failures get one automatic retry; messages that have
      // already failed an auto-retry wait for the user to decide.
      const toAutoRetry = pendingMsgs.filter((m) => (m.retryCount ?? 0) < 1);
      const toPrompt = pendingMsgs.filter((m) => (m.retryCount ?? 0) >= 1);

      const clearMessagesFromUi = (msgs: PendingMessage[]) => {
        const localIds = new Set(msgs.map((m) => m.localId));
        setConversations((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(next)) {
            const c = next[key];
            if (!c) continue;
            const filtered = c.messages.filter(
              (m: any) => !m._localId || !localIds.has(m._localId)
            );
            if (filtered.length !== c.messages.length) {
              next[key] = { ...c, messages: filtered };
            }
          }
          return next;
        });
      };

      if (toAutoRetry.length > 0) {
        toast.info(
          `Retrying ${toAutoRetry.length} unsent message${
            toAutoRetry.length > 1 ? "s" : ""
          }…`,
          {
            action: {
              label: "Clear all",
              onClick: () => {
                clearPendingMessages(appUser.PublicKeyBase58Check);
                clearMessagesFromUi(pendingMsgs);
                toast.success("Cleared all pending messages");
              },
            },
          }
        );
        for (const pending of toAutoRetry) {
          retryPendingMessage(pending, { isAutoRetry: true });
        }
      }

      if (toPrompt.length > 0) {
        // Surface the failed messages in the UI so the user sees what's stuck
        // (with the per-message retry/delete buttons), and show a batch prompt.
        for (const pending of toPrompt) {
          const TimestampNanos = pending.createdAt * 1e6;
          const failedMock = {
            DecryptedMessage: pending.messageText,
            IsSender: true,
            _status: "failed" as const,
            _localId: pending.localId,
            SenderInfo: {
              OwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
              AccessGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            },
            RecipientInfo: {
              OwnerPublicKeyBase58Check: pending.recipientPublicKey,
              AccessGroupKeyName: pending.recipientAccessGroupKeyName,
            },
            MessageInfo: {
              TimestampNanos,
              TimestampNanosString: String(TimestampNanos),
              ExtraData: pending.extraData || {},
            },
          } as DecryptedMessageEntryResponse & {
            _status: string;
            _localId: string;
          };
          setConversations((prev) => {
            const c = prev[pending.conversationKey];
            if (!c) {
              return {
                ...prev,
                [pending.conversationKey]: {
                  ChatType: ChatType.DM,
                  firstMessagePublicKey: pending.recipientPublicKey,
                  messages: [failedMock],
                },
              };
            }
            if (c.messages.some((m: any) => m._localId === pending.localId)) {
              return prev;
            }
            return {
              ...prev,
              [pending.conversationKey]: {
                ...c,
                messages: [failedMock, ...c.messages],
              },
            };
          });
        }

        toast.warning(
          `${toPrompt.length} message${
            toPrompt.length > 1 ? "s" : ""
          } failed to send after retrying. Try again or discard?`,
          {
            duration: Infinity,
            action: {
              label: "Try again",
              onClick: () => {
                for (const pending of toPrompt) {
                  // User opted in — reset the counter so another failure will
                  // prompt again rather than being swallowed silently.
                  resetPendingMessageRetryCount(
                    appUser.PublicKeyBase58Check,
                    pending.localId
                  );
                  retryPendingMessage(
                    { ...pending, retryCount: 0 },
                    { isAutoRetry: true }
                  );
                }
              },
            },
            cancel: {
              label: "Discard",
              onClick: () => {
                for (const pending of toPrompt) {
                  removePendingMessage(
                    appUser.PublicKeyBase58Check,
                    pending.localId
                  );
                }
                clearMessagesFromUi(toPrompt);
                toast.success(
                  `Discarded ${toPrompt.length} unsent message${
                    toPrompt.length > 1 ? "s" : ""
                  }`
                );
              },
            },
          }
        );
      }
    });
  }, [appUser, conversationsLoading, retryPendingMessage]);

  // Load hidden message IDs from IndexedDB on startup
  useEffect(() => {
    if (!appUser) return;
    getHiddenMessageIds(appUser.PublicKeyBase58Check).then(setHiddenMessageIds);
  }, [appUser]);

  // Reset account-scoped state and rehydrate when the user changes.
  // The clear MUST happen before rehydrate starts so stale data from
  // the previous account is gone before new data arrives.
  const prevRehydrateUserRef = useRef(appUser?.PublicKeyBase58Check);
  useEffect(() => {
    if (!appUser) return;

    const currentKey = appUser.PublicKeyBase58Check;
    const switched =
      prevRehydrateUserRef.current &&
      prevRehydrateUserRef.current !== currentKey;
    prevRehydrateUserRef.current = currentKey;

    if (switched) {
      setConversations({});
      usernameMapRef.current = {};
      setUsernameByPublicKeyBase58Check({});
      setProfilePicByPublicKey({});
      setMembersByGroupKey({});
      setHiddenMessageIds(new Set());
      setPubKeyPlusGroupName("");
    }

    // Seed the current user's own username so it's always available for
    // notifications and display, even before conversations are fetched.
    const ownUsername = appUser.ProfileEntryResponse?.Username;
    if (ownUsername) {
      mergeUsernames({ [currentKey]: ownUsername });
    }

    // Skip rehydration if this is a background refresh of the same user
    // (e.g. hydrateUser cache-hit then API refresh). The appUser object
    // reference changes but the public key is identical — re-running
    // rehydrateConversation would unmount/remount the bubbles component
    // (via loadingConversation toggle), causing all animated emojis to
    // flash as they lose their loaded state.
    const alreadyHydrated =
      !switched && Object.keys(conversationsRef.current).length > 0;
    if (alreadyHydrated) {
      // Still seed username above, but don't re-run the full rehydration
      return;
    }

    if (hasSetupMessaging(appUser)) {
      // Kick off the markdown pipeline load so it's ready before messages
      // render — prevents the ready:false→true transition that would
      // recreate all inline emoji <img> elements (restarting animations).
      preloadMarkdownPipeline();

      // New users who haven't completed onboarding see the wizard first.
      // Existing users are auto-graduated: anyone with cached data OR an
      // existing on-chain profile is clearly not a first-time user.
      if (!isOnboardingComplete(appUser.PublicKeyBase58Check)) {
        const isReturningUser =
          !!getCachedUserProfile(appUser.PublicKeyBase58Check) ||
          !!appUser.ProfileEntryResponse;
        if (isReturningUser) {
          markOnboardingComplete(appUser.PublicKeyBase58Check);
        } else {
          setShowOnboarding(true);
          return;
        }
      }

      // If the app was opened from a push notification, navigate to that conversation
      const pending = useStore.getState().pendingConversationKey;
      if (pending) useStore.getState().setPendingConversationKey(null);

      // Check if the derived key needs upgrading (new association types, etc.).
      // Show a toast with a click action — must be user gesture to avoid popup blockers.
      if (needsPermissionUpgrade()) {
        toast("Update permissions to use new features", {
          duration: 15000,
          action: {
            label: "Update now",
            onClick: () => {
              requestFullPermissions().then((ok) => {
                if (ok) toast.success("Permissions updated!");
              });
            },
          },
        });
      }

      setConversationsLoading(true);
      setConversationsError(null);

      // If opened from a notification, always select the conversation even on mobile
      rehydrateConversation(
        pending || "",
        false,
        !isMobile || !!pending,
        isLoadingUser
      );
    }
  }, [appUser, isMobile]);

  // Handle push notification clicks from service worker
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (
        event.data?.type === "notification-click" &&
        event.data.conversationKey
      ) {
        const key = event.data.conversationKey;
        setSelectedConversationPublicKey(key);
        clearUnread(key);
        // Clear the IDB fallback so the visibility-change handler doesn't
        // double-navigate when the fast path (postMessage) already worked.
        consumePendingNotificationConversation();

        // Directly fetch the thread via the paginated endpoint (fast, ~25 msgs)
        // instead of waiting for the slow full-thread-list differential fetch.
        const currentAppUser = useStore.getState().appUser;
        if (currentAppUser && conversationsRef.current[key]) {
          try {
            const { updatedConversations, pubKeyPlusGroupName } =
              await getConversation(key, conversationsRef.current);
            if (selectedConversationPublicKeyRef.current === key) {
              setConversations((prev) => ({
                ...prev,
                [key]: updatedConversations[key],
              }));
              setPubKeyPlusGroupName(pubKeyPlusGroupName);
              if (updatedConversations[key]) {
                cacheConversationMessages(
                  currentAppUser.PublicKeyBase58Check,
                  key,
                  updatedConversations[key].messages
                );
              }
            }
          } catch (e) {
            console.error("[ChatOn] Notification thread fetch failed:", e);
          }
        }
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () =>
      navigator.serviceWorker?.removeEventListener("message", handler);
  }, []);

  // advanced-event-handler-refs: store handler in ref so the listener
  // doesn't re-register when handleWsNewMessage changes.
  const visibilityHandlerRef = useRef(handleWsNewMessage);
  visibilityHandlerRef.current = handleWsNewMessage;
  const loadingRef = useRef(conversationsLoading);
  loadingRef.current = conversationsLoading;
  const isIdleRef = useRef(isIdle);
  isIdleRef.current = isIdle;
  const wsConnectedRef = useRef(wsConnected);
  wsConnectedRef.current = wsConnected;

  // Refresh conversations + classification data when PWA resumes from background
  useEffect(() => {
    if (!appUser) return;
    const publicKey = appUser.PublicKeyBase58Check;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !loadingRef.current) {
        // Clear all OS notifications — the user is now in the app and can see
        // messages inline. On Android 8+, notification dots on the app icon
        // persist as long as there are pending notifications in the tray,
        // independent of the Badging API. This removes the dot.
        try {
          const msg = { type: "clear-all-notifications" };
          if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage(msg);
          } else {
            navigator.serviceWorker?.ready.then((reg) =>
              reg.active?.postMessage(msg)
            );
          }
        } catch {
          // SW not available
        }

        // Check if a notification click wrote a pending conversation to IndexedDB.
        // postMessage from the SW is unreliable when the app is resuming from a
        // suspended/frozen state, so IndexedDB acts as the durable fallback.
        consumePendingNotificationConversation().then(async (pendingKey) => {
          if (pendingKey) {
            setSelectedConversationPublicKey(pendingKey);
            clearUnread(pendingKey);
            // Directly fetch the thread (fast paginated endpoint) so the new
            // message appears immediately instead of waiting for the slow
            // full-thread-list differential fetch below.
            if (conversationsRef.current[pendingKey]) {
              try {
                const { updatedConversations, pubKeyPlusGroupName } =
                  await getConversation(pendingKey, conversationsRef.current);
                if (selectedConversationPublicKeyRef.current === pendingKey) {
                  const updatedConvo = updatedConversations[pendingKey];
                  if (updatedConvo) {
                    setConversations((prev) => ({
                      ...prev,
                      [pendingKey]: updatedConvo,
                    }));
                    cacheConversationMessages(
                      publicKey,
                      pendingKey,
                      updatedConvo.messages
                    );
                  }
                  setPubKeyPlusGroupName(pubKeyPlusGroupName);
                }
              } catch {
                // Fall through to the differential fetch below
              }
            }
          }
        });

        // Debounce: skip if another resume happened within 2s (rapid tab switching)
        const now = Date.now();
        if (now - lastResumeRef.current < FOREGROUND_RESUME_DEBOUNCE_MS) return;
        lastResumeRef.current = now;

        // Reset polling backoff on foreground resume — but keep the slow
        // rate if WS is connected (it handles real-time delivery).
        if (!wsConnectedRef.current) {
          setPollingDelay(baseDelay);
        }

        // Refresh conversations — pass the selected conversation key so
        // handleWsNewMessage fetches its full thread instead of preserving
        // stale messages (simpleConversationMerge skips messages for the
        // selected conversation when threadId doesn't match).
        visibilityHandlerRef.current(
          selectedConversationPublicKeyRef.current || "",
          ""
        );

        // Silently re-fetch classification data (associations, follows) so
        // accept/block/dismiss actions from other devices sync without reload.
        Promise.all([
          fetchFollowedUsers(publicKey),
          fetchChatAssociations(publicKey),
          fetchArchivedGroups(publicKey),
          fetchAcceptedGroups(publicKey),
        ])
          .then(([mutual, assoc, archivedMap, acceptedMap]) => {
            const approvedSet = new Set(assoc.approved.keys());
            const blockedSet = new Set(assoc.blocked.keys());
            const archivedSet = new Set(archivedMap.keys());
            const archivedChatsSet = new Set(assoc.archivedChats.keys());
            const dismissedSet = new Set(assoc.dismissed.keys());
            const paidSet = new Set(assoc.paid.keys());
            const acceptedSet = new Set(acceptedMap.keys());
            setClassificationData(
              mutual,
              approvedSet,
              blockedSet,
              assoc.approved,
              assoc.blocked,
              archivedSet,
              archivedMap,
              archivedChatsSet,
              assoc.archivedChats,
              dismissedSet,
              assoc.dismissed,
              paidSet,
              assoc.paid,
              acceptedSet,
              acceptedMap
            );
          })
          .catch(() => {});

        // Refresh join request counts for owned groups
        fetchJoinRequestCountsForOwner(publicKey)
          .then(setJoinRequestCounts)
          .catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [appUser]);

  useEffect(() => {
    setLockRefresh(isLoadingUser);
    // Only blank the screen during initial login (no conversations yet).
    // During same-user re-auth (derived key upgrade), conversations are
    // already visible — don't reset selection or show loading spinner.
    const hasConversations = Object.keys(conversations).length > 0;
    if (!hasConversations) {
      setSelectedConversationPublicKey("");
    }
  }, [isLoadingUser, appUser]);

  useEffect(() => {
    if (conversations[selectedConversationPublicKey]) {
      const chatName = getChatNameFromConversation(
        conversations[selectedConversationPublicKey],
        usernameByPublicKeyBase58Check,
        allAccessGroups
      );

      if (chatName) {
        document.title = [chatName, BASE_TITLE].join(TITLE_DIVIDER);
      }
    }

    return () => {
      document.title = BASE_TITLE;
    };
  }, [
    selectedConversationPublicKey,
    conversations,
    usernameByPublicKeyBase58Check,
    allAccessGroups,
  ]);

  useInterval(async () => {
    const initConversationKey = selectedConversationPublicKey;

    if (
      !appUser ||
      !selectedConversationPublicKey ||
      lockRefreshRef.current ||
      pollingRef.current ||
      !navigator.onLine
    ) {
      return;
    }
    pollingRef.current = true;
    try {
      const {
        conversations,
        updatedAllAccessGroups,
        publicKeyToProfileEntryResponseMap: pollProfiles,
      } = await getConversationsDifferential(
        appUser.PublicKeyBase58Check,
        allAccessGroups
      );
      setAllAccessGroups(updatedAllAccessGroups);

      // Update username + profile pic maps from fresh profile data
      const pollUsernames: Record<string, string> = {};
      const pollPics: Record<string, string> = {};
      for (const [pk, profile] of Object.entries(pollProfiles)) {
        if (profile?.Username) pollUsernames[pk] = profile.Username;
        const ed = profile?.ExtraData;
        if (ed) {
          const picUrl = ed.NFTProfilePictureUrl || ed.LargeProfilePicURL;
          if (picUrl) pollPics[pk] = picUrl;
        }
      }
      if (Object.keys(pollUsernames).length) {
        mergeUsernames(pollUsernames);
      }
      if (Object.keys(pollPics).length) {
        setProfilePicByPublicKey((s) => ({ ...s, ...pollPics }));
      }
      const { updatedConversations, pubKeyPlusGroupName } =
        await getConversation(selectedConversationPublicKey, {
          ...conversations,
          [selectedConversationPublicKey]:
            conversations[selectedConversationPublicKey]!,
        });

      if (
        !lockRefreshRef.current &&
        conversations[selectedConversationPublicKey] &&
        initConversationKey === selectedConversationPublicKeyRef.current
      ) {
        const updatedMessages =
          updatedConversations[selectedConversationPublicKey]?.messages;
        if (updatedMessages) {
          mergeConversationUpdate(
            updatedConversations,
            selectedConversationPublicKey,
            updatedMessages
          );
        }
        setPubKeyPlusGroupName(pubKeyPlusGroupName);
      }
    } finally {
      pollingRef.current = false;
      // Only back off when WS is disconnected — when connected, the
      // wsConnected effect already holds us at the slow safety-net rate.
      if (!wsConnectedRef.current) {
        setPollingDelay((prev) =>
          prev
            ? Math.min(prev + baseDelay, REFRESH_MESSAGES_MAX_INTERVAL_MS)
            : prev
        );
      }
    }
  }, pollingDelay);

  const fetchUsersStateless = async (newPublicKeysToGet: Array<string>) => {
    // Use the ref (always current) instead of state (stale in async callbacks)
    const known = usernameMapRef.current;
    const diff = newPublicKeysToGet.filter((k) => !(k in known));

    if (diff.length === 0) {
      return Promise.resolve(known);
    }

    return getUsersStateless({
      PublicKeysBase58Check: diff,
      SkipForLeaderboard: true,
    }).then((usersStatelessResponse) => {
      const newPublicKeyToUsernames: { [k: string]: string } = {};

      (usersStatelessResponse.UserList || []).forEach((u) => {
        newPublicKeyToUsernames[u.PublicKeyBase58Check] =
          u.ProfileEntryResponse?.Username || "";
      });

      mergeUsernames(newPublicKeyToUsernames);
      return usernameMapRef.current;
    });
  };

  const fetchGroupMembers = async (conversation: Conversation) => {
    if (conversation.ChatType !== ChatType.GROUPCHAT) {
      return;
    }

    const firstMsg = conversation.messages[0];
    if (!firstMsg) return; // No messages yet (e.g. user just joined)

    const { AccessGroupKeyName, OwnerPublicKeyBase58Check } =
      firstMsg.RecipientInfo;

    // Fetch all members with pagination + owner profile in parallel.
    // The members API may not include the group owner, so fetch separately.
    const PAGE_SIZE = 50;
    const allMemberKeys: string[] = [];
    const allProfiles: PublicKeyToProfileEntryResponseMap = {};

    // Retry helper — QUIC/network errors are transient; one retry usually succeeds
    const fetchMembersWithRetry = async (
      params: Parameters<typeof getPaginatedAccessGroupMembers>[0]
    ) => {
      try {
        return await getPaginatedAccessGroupMembers(params);
      } catch {
        return getPaginatedAccessGroupMembers(params);
      }
    };

    const [firstPageResult, ownerResult] = await Promise.all([
      fetchMembersWithRetry({
        AccessGroupOwnerPublicKeyBase58Check: OwnerPublicKeyBase58Check,
        AccessGroupKeyName,
        MaxMembersToFetch: PAGE_SIZE,
      }),
      getUsersStateless({
        PublicKeysBase58Check: [OwnerPublicKeyBase58Check],
        SkipForLeaderboard: true,
      }),
    ]);

    const firstPageKeys = firstPageResult.AccessGroupMembersBase58Check ?? [];
    allMemberKeys.push(...firstPageKeys);
    Object.assign(
      allProfiles,
      firstPageResult.PublicKeyToProfileEntryResponse ?? {}
    );

    // Paginate remaining members
    if (firstPageKeys.length >= PAGE_SIZE) {
      let cursor = firstPageKeys[firstPageKeys.length - 1]!;
      for (;;) {
        const page = await fetchMembersWithRetry({
          AccessGroupOwnerPublicKeyBase58Check: OwnerPublicKeyBase58Check,
          AccessGroupKeyName,
          MaxMembersToFetch: PAGE_SIZE,
          StartingAccessGroupMemberPublicKeyBase58Check: cursor,
        });
        const pageKeys = page.AccessGroupMembersBase58Check ?? [];
        allMemberKeys.push(...pageKeys);
        Object.assign(allProfiles, page.PublicKeyToProfileEntryResponse ?? {});
        if (pageKeys.length < PAGE_SIZE) break;
        cursor = pageKeys[pageKeys.length - 1]!;
      }
    }

    // Merge the owner's profile into the members map
    const ownerProfile = ownerResult.UserList?.[0]?.ProfileEntryResponse;
    if (!allMemberKeys.includes(OwnerPublicKeyBase58Check)) {
      allMemberKeys.push(OwnerPublicKeyBase58Check);
    }
    if (ownerProfile && !allProfiles[OwnerPublicKeyBase58Check]) {
      allProfiles[OwnerPublicKeyBase58Check] = ownerProfile;
    }

    // Fetch profiles for members missing from PublicKeyToProfileEntryResponse.
    // The DeSo node may omit profiles for some members; without this, those
    // members won't appear in the mention picker.
    const missingProfileKeys = allMemberKeys.filter((pk) => !allProfiles[pk]);
    if (missingProfileKeys.length > 0) {
      try {
        const missingRes = await getUsersStateless({
          PublicKeysBase58Check: missingProfileKeys,
          SkipForLeaderboard: true,
        });
        for (const u of missingRes.UserList ?? []) {
          if (u.ProfileEntryResponse) {
            allProfiles[u.PublicKeyBase58Check] = u.ProfileEntryResponse;
          }
        }
      } catch {
        // Non-fatal — members without profiles will be excluded from mentions
      }
    }

    setMembersByGroupKey((state) => ({
      ...state,
      [`${OwnerPublicKeyBase58Check}${AccessGroupKeyName}`]: allProfiles,
    }));
    const usernamesByPublicKeyFromGroup: Record<string, string> = {};
    const groupProfilePics: Record<string, string> = {};
    Object.entries(allProfiles || {}).forEach(([pk, profile]) => {
      usernamesByPublicKeyFromGroup[pk] = profile?.Username || "";
      const ed = profile?.ExtraData;
      if (ed) {
        const picUrl = ed.NFTProfilePictureUrl || ed.LargeProfilePicURL;
        if (picUrl) groupProfilePics[pk] = picUrl;
      }
    });
    mergeUsernames(usernamesByPublicKeyFromGroup);
    setProfilePicByPublicKey((state) => ({
      ...state,
      ...groupProfilePics,
    }));

    return allProfiles;
  };

  const rehydrateConversation = async (
    selectedKey = "",
    autoScroll = false,
    selectConversation = true,
    userChange = false
  ) => {
    if (!appUser) {
      toast.error("You must be logged in to use this feature");
      setConversationsLoading(false);
      return;
    }

    const publicKey = appUser.PublicKeyBase58Check;

    // Mark user-initiated conversations (from search) so they go to Chats
    if (selectedKey) {
      addInitiatedChat(selectedKey.slice(0, PUBLIC_KEY_LENGTH));
    }

    // --- Cache-first: try to show cached data immediately ---
    const cachedConvos = await getCachedConversations(publicKey);
    const cachedClassification = getCachedClassificationData(publicKey);
    const cachedUsernames = getCachedUsernameMap(publicKey);
    const cachedLastKey = getCachedLastConversationKey(publicKey);

    // Load muted conversations from cache
    const cachedMuted = getCachedMutedConversations(publicKey);
    if (cachedMuted.size > 0) {
      useStore.getState().setMutedConversations(cachedMuted);
    }

    // Load privacy mode from cache
    const cachedPrivacy = getCachedPrivacyMode(publicKey);
    if (cachedPrivacy) {
      useStore.getState().setPrivacyMode(cachedPrivacy as "standard" | "full");
    }

    // Load DM price from cache
    const cachedDmPrice = getCachedDmPrice(publicKey);
    if (cachedDmPrice) {
      useStore
        .getState()
        .setDmPrice(
          cachedDmPrice.cents,
          cachedDmPrice.followingCents,
          cachedDmPrice.associationId
        );
    }
    // Load spam filter from cache
    const cachedSpamFilter = getCachedSpamFilter(publicKey);
    if (cachedSpamFilter) {
      useStore
        .getState()
        .setSpamFilter(cachedSpamFilter.config, cachedSpamFilter.associationId);
    }

    let renderedFromCache = false;

    if (cachedConvos && Object.keys(cachedConvos).length > 0) {
      // Hydrate classification from cache if not yet loaded
      if (cachedClassification && !chatRequestsLoaded) {
        setClassificationData(
          cachedClassification.followedUsers,
          cachedClassification.approvedUsers,
          cachedClassification.blockedUsers,
          cachedClassification.approvedAssociationIds,
          cachedClassification.blockedAssociationIds,
          cachedClassification.archivedGroups,
          cachedClassification.archivedGroupAssociationIds,
          cachedClassification.archivedChats,
          cachedClassification.archivedChatAssociationIds,
          cachedClassification.dismissedUsers,
          cachedClassification.dismissedAssociationIds,
          cachedClassification.paidUsers,
          cachedClassification.paidAssociationIds,
          cachedClassification.acceptedGroups,
          cachedClassification.acceptedGroupAssociationIds
        );
      }

      if (cachedUsernames) {
        mergeUsernames(cachedUsernames);
      }

      const cachedKeyToUse =
        selectedKey ||
        (!userChange && selectedConversationPublicKey) ||
        cachedLastKey ||
        Object.keys(cachedConvos)[0];

      // Preserve any placeholder conversation injected before rehydration
      // (e.g. from onPrivateMessage) so the safeSelectedKey guard doesn't
      // reset the selection.
      setConversations((prev) => {
        if (selectedKey && prev[selectedKey] && !cachedConvos[selectedKey]) {
          return { [selectedKey]: prev[selectedKey], ...cachedConvos };
        }
        // Inject a placeholder for brand-new conversations (e.g. compose to
        // a user never messaged) so the render guard doesn't reset the
        // selected key before the network fetch completes.
        if (selectedKey && !cachedConvos[selectedKey]) {
          const groupKeyName = selectedKey.slice(PUBLIC_KEY_LENGTH);
          const isGroup =
            groupKeyName.length > 0 &&
            groupKeyName !== DEFAULT_KEY_MESSAGING_GROUP_NAME;
          return {
            [selectedKey]: {
              ChatType: isGroup ? ChatType.GROUPCHAT : ChatType.DM,
              firstMessagePublicKey: selectedKey.slice(0, PUBLIC_KEY_LENGTH),
              messages: [],
            },
            ...cachedConvos,
          };
        }
        return cachedConvos;
      });
      setConversationsLoading(false);

      renderedFromCache = true;

      // Load cached messages BEFORE mounting the bubbles component so
      // it renders with the full message set on first mount. The old
      // approach set loadingConversation=true (unmounting the bubbles),
      // loaded messages, then set loadingConversation=false (remounting).
      // That unmount/remount cycle destroyed all AnimatedEmoji loaded
      // state, causing every emoji to flash black→visible.
      let hasCachedMessages = false;
      if (cachedKeyToUse && cachedConvos[cachedKeyToUse]) {
        const cachedMsgs = await getCachedConversationMessages(
          publicKey,
          cachedKeyToUse
        );
        if (cachedMsgs && cachedMsgs.length > 0) {
          setConversations((prev) =>
            updateConv(prev, cachedKeyToUse, (c) => ({
              ...c,
              messages: cachedMsgs as DecryptedMessageEntryResponse[],
            }))
          );
          hasCachedMessages = true;
        }
      }

      if (selectConversation && cachedKeyToUse) {
        // Capture the last-read timestamp before selecting, for the unread divider
        const lastReadTs = getCachedLastReadTimestamps(publicKey);
        setOpenedLastReadNanos(lastReadTs[cachedKeyToUse] ?? null);

        if (!hasCachedMessages) {
          // No cached messages — show spinner until the network fetch arrives.
          // This only gates the initial mount, not a remount.
          setLoadingConversation(true);
        }
        setSelectedConversationPublicKey(cachedKeyToUse);
        setPubKeyPlusGroupName(cachedKeyToUse);
      }

      // Eagerly refresh the selected conversation via the fast paginated
      // endpoint so new messages appear immediately, instead of waiting for
      // the slow full-thread-list revalidation below.
      if (cachedKeyToUse && cachedConvos[cachedKeyToUse]) {
        getConversation(cachedKeyToUse, cachedConvos)
          .then(({ updatedConversations, pubKeyPlusGroupName }) => {
            if (selectedConversationPublicKeyRef.current !== cachedKeyToUse)
              return;
            const updatedConvo = updatedConversations[cachedKeyToUse];
            if (updatedConvo) {
              setConversations((prev) => ({
                ...prev,
                [cachedKeyToUse]: updatedConvo,
              }));
              cacheConversationMessages(
                publicKey,
                cachedKeyToUse,
                updatedConvo.messages
              );
            }
            setPubKeyPlusGroupName(pubKeyPlusGroupName);
          })
          .catch(() => {});
      }

      // Restore unread badges from cached data immediately so badges don't
      // flash away and reappear when the user reopens the app. The last-read
      // timestamps are in localStorage, and cached conversations have the
      // latest message timestamps — everything needed to compute badges.
      const cachedLastRead = getCachedLastReadTimestamps(publicKey);
      const {
        mutedConversations: cachedMuted,
        archivedGroups: cachedArchived,
      } = useStore.getState();
      const cachedUnreadMap = new Map<string, number>();
      for (const [k, convo] of Object.entries(cachedConvos)) {
        if (k === cachedKeyToUse) continue;
        if (cachedMuted.has(k) || cachedArchived.has(k)) continue;
        const msgTs = latestIncomingTimestamp(convo, publicKey);
        if (!msgTs) continue;
        const lastRead = cachedLastRead[k];
        if (lastRead !== undefined && msgTs > lastRead) {
          cachedUnreadMap.set(k, 1);
        }
      }
      if (cachedUnreadMap.size > 0) {
        initializeUnread(cachedUnreadMap);
      }
    }

    // --- Classification + privacy (always run in parallel) ---
    const classificationPromise = !chatRequestsLoaded
      ? Promise.all([
          fetchFollowedUsers(publicKey),
          fetchChatAssociations(publicKey),
          fetchArchivedGroups(publicKey),
          fetchAcceptedGroups(publicKey),
        ])
          .then(([mutual, assoc, archivedMap, acceptedMap]) => {
            const approvedSet = new Set(assoc.approved.keys());
            const blockedSet = new Set(assoc.blocked.keys());
            const archivedSet = new Set(archivedMap.keys());
            const archivedChatsSet = new Set(assoc.archivedChats.keys());
            const dismissedSet = new Set(assoc.dismissed.keys());
            const paidSet = new Set(assoc.paid.keys());
            const acceptedSet = new Set(acceptedMap.keys());
            setClassificationData(
              mutual,
              approvedSet,
              blockedSet,
              assoc.approved,
              assoc.blocked,
              archivedSet,
              archivedMap,
              archivedChatsSet,
              assoc.archivedChats,
              dismissedSet,
              assoc.dismissed,
              paidSet,
              assoc.paid,
              acceptedSet,
              acceptedMap
            );
          })
          .catch((e) => {
            console.error(
              "Failed to load chat request classification data:",
              e
            );
          })
      : Promise.resolve();

    // Fetch privacy mode from on-chain in parallel
    const privacyPromise = fetchPrivacyMode(publicKey)
      .then(({ mode, associationId }) => {
        useStore.getState().setPrivacyMode(mode, associationId);
      })
      .catch((e) => {
        console.error("Failed to fetch privacy mode:", e);
      });

    // Fetch paid messaging settings (DM price) from on-chain in parallel
    fetchPaidMessagingSettings(publicKey)
      .then(({ cents, followingCents, associationId }) => {
        useStore.getState().setDmPrice(cents, followingCents, associationId);
      })
      .catch((e) => {
        console.error("Failed to fetch paid messaging settings:", e);
      });

    // Fetch spam filter settings from on-chain in parallel
    fetchSpamFilterSettings(publicKey)
      .then(({ config, associationId }) => {
        useStore.getState().setSpamFilter(config, associationId);
      })
      .catch((e) => {
        console.error("Failed to fetch spam filter settings:", e);
      });

    // --- Conversation loading: progressive for first load, blocking for cache revalidation ---
    let conversationResult;

    // AbortController for progressive decryption — cancelled on logout/re-login
    const decryptAbort = new AbortController();

    if (!renderedFromCache) {
      // ── Progressive path: show shells immediately, decrypt in batches ──
      let flushTimer: ReturnType<typeof setTimeout> | null = null;
      try {
        const [rawResult] = await Promise.all([
          fetchMessageThreadsRaw(
            publicKey,
            (partial) => {
              // Bail if user logged out while the request was in flight
              if (decryptAbort.signal.aborted) return;
              // Whichever resolves first (DMs or groups) — render shells immediately
              const { conversations: partialShells } = buildShellConversations(
                partial.messageThreads,
                publicKey
              );
              if (Object.keys(partialShells).length > 0) {
                setConversations((prev) => ({ ...prev, ...partialShells }));
                setConversationsLoading(false);
              }
              // Extract usernames/pics from partial profile data
              const usernames: Record<string, string> = {};
              const pics: Record<string, string> = {};
              for (const [pk, profile] of Object.entries(
                partial.publicKeyToProfileEntryResponseMap
              )) {
                if (profile?.Username) usernames[pk] = profile.Username;
                const ed = profile?.ExtraData;
                if (ed) {
                  const pic = ed.NFTProfilePictureUrl || ed.LargeProfilePicURL;
                  if (pic) pics[pk] = pic;
                }
              }
              if (Object.keys(usernames).length) mergeUsernames(usernames);
              if (Object.keys(pics).length)
                setProfilePicByPublicKey((s) => ({ ...s, ...pics }));
            },
            true /* useTimeout */
          ),
          classificationPromise,
          privacyPromise,
        ]);

        let { messageThreads } = rawResult;
        const { publicKeyToProfileEntryResponseMap } = rawResult;

        // Cap at 1000 threads for extreme cases — sort by recency so
        // the most recent conversations are kept. Prevents React from
        // managing thousands of conversation objects in state.
        const MAX_INITIAL_THREADS = 1000;
        if (messageThreads.length > MAX_INITIAL_THREADS) {
          messageThreads = [...messageThreads]
            .sort(
              (a, b) =>
                b.MessageInfo.TimestampNanos - a.MessageInfo.TimestampNanos
            )
            .slice(0, MAX_INITIAL_THREADS);
        }

        if (messageThreads.length === 0) {
          // No threads — show empty state immediately instead of blocking
          // on auto-first-message + blockchain confirmation (was 30-60s).
          // The conversation appears in the background once confirmed.
          setConversations({});
          setConversationsLoading(false);

          // Fire-and-forget: send welcome message, merge when confirmed.
          // Guard against double-sends (e.g., StrictMode, rapid remount).
          if (welcomeMessageSentRef.current) return;
          welcomeMessageSentRef.current = true;
          getConversations(publicKey, allAccessGroups)
            .then((result) => {
              if (
                !result.conversations ||
                Object.keys(result.conversations).length === 0
              )
                return;
              // Bail if user logged out or switched accounts
              const currentUser = useStore.getState().appUser;
              if (currentUser?.PublicKeyBase58Check !== publicKey) return;

              setConversations((prev) => {
                // If user already started a conversation, merge — don't overwrite
                if (Object.keys(prev).length > 0) {
                  return { ...result.conversations, ...prev };
                }
                return result.conversations;
              });
              setAllAccessGroups(result.updatedAllAccessGroups);

              const profileMap = result.publicKeyToProfileEntryResponseMap;
              const usernames: Record<string, string> = {};
              for (const [pk, profile] of Object.entries(profileMap)) {
                if (profile?.Username) usernames[pk] = profile.Username;
              }
              if (Object.keys(usernames).length) mergeUsernames(usernames);
            })
            .catch((e) => {
              console.error(
                "[ChatOn] Background auto-first-message failed:",
                e
              );
            });

          return;
        } else {
          // Build shell conversations and show them immediately.
          // Also get latestByKey to pass directly to decryptConversationPreviews.
          const { conversations: shellConversations, latestByKey } =
            buildShellConversations(messageThreads, publicKey);

          // Separate local tracker from React state to avoid mutating state directly
          const localTracker: ConversationMap = { ...shellConversations };

          // Extract usernames and profile pics (available without decryption)
          const publicKeyToUsername: { [k: string]: string } = {};
          const profilePicUrls: { [k: string]: string } = {};
          Object.entries(publicKeyToProfileEntryResponseMap).forEach(
            ([pk, profileEntryResponse]) => {
              publicKeyToUsername[pk] = profileEntryResponse?.Username || "";
              const ed = profileEntryResponse?.ExtraData;
              if (ed) {
                const picUrl = ed.NFTProfilePictureUrl || ed.LargeProfilePicURL;
                if (picUrl) profilePicUrls[pk] = picUrl;
              }
            }
          );
          mergeUsernames(publicKeyToUsername);
          // Populate sender profiles for spam filter classification
          setSenderProfiles((prev) => {
            const next = new Map(prev);
            for (const [pk, profile] of Object.entries(
              publicKeyToProfileEntryResponseMap
            )) {
              if (profile) next.set(pk, profile);
            }
            return next;
          });
          setProfilePicByPublicKey((state) => ({
            ...state,
            ...profilePicUrls,
          }));

          // Show conversation list with shimmer previews — clears the loading skeleton
          setConversations(shellConversations);
          setConversationsLoading(false);

          const shellKeyToUse =
            selectedKey ||
            (!userChange && selectedConversationPublicKey) ||
            Object.keys(shellConversations)[0];
          if (
            selectConversation &&
            shellKeyToUse &&
            (!selectedConversationPublicKeyRef.current ||
              selectedConversationPublicKeyRef.current === shellKeyToUse)
          ) {
            setSelectedConversationPublicKey(shellKeyToUse);
          }

          // Decrypt previews progressively in batches.
          // Updates go to localTracker (for conversationResult) AND React state (for UI).
          // Debounce React state updates: accumulate batch results and flush every 150ms
          // to avoid 50+ re-renders during initial load.
          let pendingUpdates = new Map<string, DecryptedMessageEntryResponse>();

          const flushPendingUpdates = () => {
            if (pendingUpdates.size === 0) return;
            const toFlush = pendingUpdates;
            pendingUpdates = new Map();
            setConversations((prev) => {
              const merged = { ...prev };
              for (const [key, decryptedMsg] of toFlush) {
                if (merged[key] && merged[key].messages.length > 0) {
                  merged[key] = {
                    ...merged[key],
                    messages: [decryptedMsg, ...merged[key].messages.slice(1)],
                  };
                }
              }
              return merged;
            });
          };

          const updatedAccessGroups = await decryptConversationPreviews(
            latestByKey,
            publicKey,
            allAccessGroups,
            (batchUpdates) => {
              // Update local tracker (never shared with React state)
              for (const [key, decryptedMsg] of batchUpdates) {
                if (
                  localTracker[key] &&
                  localTracker[key].messages.length > 0
                ) {
                  localTracker[key] = {
                    ...localTracker[key],
                    messages: [
                      decryptedMsg,
                      ...localTracker[key].messages.slice(1),
                    ],
                  };
                }
              }
              // Accumulate for debounced React state update
              for (const [key, msg] of batchUpdates) {
                pendingUpdates.set(key, msg);
              }
              if (flushTimer) clearTimeout(flushTimer);
              flushTimer = setTimeout(flushPendingUpdates, 150);
            },
            decryptAbort.signal
          );
          // Flush any remaining accumulated updates
          if (flushTimer) clearTimeout(flushTimer);
          flushPendingUpdates();
          setAllAccessGroups(updatedAccessGroups);

          // Fetch group members and DM usernames now that we have data
          const DMChats = Object.values(localTracker).filter(
            (e) => e.ChatType === ChatType.DM
          );
          const GroupChats = Object.values(localTracker).filter(
            (e) => e.ChatType === ChatType.GROUPCHAT
          );
          try {
            await Promise.all([
              updateUsernameToPublicKeyMapFromConversations(DMChats),
              ...GroupChats.map((e) => fetchGroupMembers(e)),
            ]);
          } catch (e) {
            console.error(
              "[ChatOn] Failed to fetch group members/usernames:",
              e
            );
          }
          cacheUsernameMap(publicKey, usernameMapRef.current);

          // Fire-and-forget cleanup
          cleanupOwnJoinRequests(publicKey, updatedAccessGroups).catch(
            () => {}
          );
          fetchJoinRequestCountsForOwner(publicKey)
            .then(setJoinRequestCounts)
            .catch(() => {});

          // Now load the full thread for the selected conversation
          setLoadingConversation(true);
          conversationResult = {
            conversations: localTracker,
            publicKeyToProfileEntryResponseMap,
            updatedAllAccessGroups: updatedAccessGroups,
          };
        }
      } catch (e) {
        decryptAbort.abort();
        // Clear debounced flush timer to prevent stale state writes
        if (flushTimer) clearTimeout(flushTimer);
        console.error("[ChatOn] Failed to fetch conversations:", e);
        setConversationsLoading(false);
        setLoadingConversation(false);

        setConversationsError(
          e instanceof Error && e.message.includes("timed out")
            ? "Loading timed out — your messages are safe on-chain."
            : "Couldn't load conversations."
        );
        return;
      }
    } else {
      // ── Cache revalidation path: full blocking load in background ──
      const conversationPromise = getConversations(publicKey, allAccessGroups);
      try {
        [conversationResult] = await Promise.all([
          conversationPromise,
          classificationPromise,
          privacyPromise,
        ]);
      } catch (e) {
        console.error("[ChatOn] Failed to fetch conversations:", e);
        setConversationsLoading(false);
        setLoadingConversation(false);

        return;
      }
    }

    const {
      conversations: freshConversations,
      publicKeyToProfileEntryResponseMap: profileMap,
      updatedAllAccessGroups,
    } = conversationResult;
    setAllAccessGroups(updatedAllAccessGroups);

    if (renderedFromCache) {
      // These only need to run on the cache-revalidation path;
      // the progressive path handles them inline above
      cleanupOwnJoinRequests(publicKey, updatedAllAccessGroups).catch(() => {});
      fetchJoinRequestCountsForOwner(publicKey)
        .then(setJoinRequestCounts)
        .catch(() => {});
    }

    let conversationsResponse = freshConversations || {};
    const keyToUse =
      selectedKey ||
      (!userChange && selectedConversationPublicKey) ||
      Object.keys(conversationsResponse)[0] ||
      "";

    if (keyToUse && !conversationsResponse[keyToUse]) {
      const groupKeyName = keyToUse.slice(PUBLIC_KEY_LENGTH);
      const isGroup =
        groupKeyName.length > 0 &&
        groupKeyName !== DEFAULT_KEY_MESSAGING_GROUP_NAME;
      conversationsResponse = {
        [keyToUse]: {
          ChatType: isGroup ? ChatType.GROUPCHAT : ChatType.DM,
          firstMessagePublicKey: keyToUse.slice(0, PUBLIC_KEY_LENGTH),
          messages: [],
        },
        ...conversationsResponse,
      };
    }

    if (renderedFromCache) {
      const DMChats = Object.values(conversationsResponse).filter(
        (e) => e.ChatType === ChatType.DM
      );
      const GroupChats = Object.values(conversationsResponse).filter(
        (e) => e.ChatType === ChatType.GROUPCHAT
      );

      const publicKeyToUsername: { [k: string]: string } = {};
      const profilePicUrls: { [k: string]: string } = {};
      Object.entries(profileMap).forEach(([pk, profileEntryResponse]) => {
        publicKeyToUsername[pk] = profileEntryResponse?.Username || "";
        const ed = profileEntryResponse?.ExtraData;
        if (ed) {
          const picUrl = ed.NFTProfilePictureUrl || ed.LargeProfilePicURL;
          if (picUrl) profilePicUrls[pk] = picUrl;
        }
      });
      mergeUsernames(publicKeyToUsername);
      setSenderProfiles((prev) => {
        const next = new Map(prev);
        for (const [pk, profile] of Object.entries(profileMap)) {
          if (profile) next.set(pk, profile);
        }
        return next;
      });
      setProfilePicByPublicKey((state) => ({
        ...state,
        ...profilePicUrls,
      }));
      try {
        await Promise.all([
          updateUsernameToPublicKeyMapFromConversations(DMChats),
          ...GroupChats.map((e) => fetchGroupMembers(e)),
        ]);
      } catch (e) {
        console.error("[ChatOn] Failed to fetch group members/usernames:", e);
      }
      cacheUsernameMap(publicKey, usernameMapRef.current);
    }

    // Only force-select if user hasn't already navigated to a different conversation
    if (
      selectConversation &&
      (!selectedConversationPublicKeyRef.current ||
        selectedConversationPublicKeyRef.current === keyToUse)
    ) {
      setSelectedConversationPublicKey(keyToUse);
    }

    if (!renderedFromCache) {
      setLoadingConversation(true);
    }

    try {
      const { updatedConversations, pubKeyPlusGroupName } =
        await getConversation(keyToUse, conversationsResponse);

      // Merge: start from prev state so we don't wipe full threads the user
      // loaded via the click handler while this rehydration was in-flight.
      setConversations((prev) => {
        const merged = { ...prev };
        const currentKey = selectedConversationPublicKeyRef.current;
        for (const [k, convo] of Object.entries(updatedConversations)) {
          if (!merged[k]) {
            merged[k] = convo;
          } else if (k === currentKey && k !== keyToUse) {
            // User is viewing a different conversation — preserve its full thread
            merged[k] = { ...convo, messages: merged[k].messages };
          } else {
            // For the rehydrated conversation and background conversations,
            // use fresh data but preserve optimistic messages
            const optimistic = merged[k].messages.filter(
              (m: any) => m._localId && m._status !== "sent"
            );
            merged[k] = convo;
            if (optimistic.length > 0) {
              merged[k] = {
                ...merged[k],
                messages: [...optimistic, ...merged[k].messages],
              };
            }
          }
        }
        // Remove conversations no longer returned by the blockchain
        // (e.g. user was removed from a group) — but keep the one the user is viewing.
        // Only do this on the cache-revalidation path; on the progressive path
        // updatedConversations is the shell set which may be missing conversations
        // that arrived via WebSocket during decryption.
        if (renderedFromCache) {
          for (const k of Object.keys(merged)) {
            if (
              !updatedConversations[k] &&
              k !== currentKey &&
              k !== keyToUse
            ) {
              delete merged[k];
            }
          }
        }
        // Fire-and-forget cache writes
        cacheConversations(publicKey, merged);
        return merged;
      });
      // Only update pubKeyPlusGroupName if user is still on this conversation
      if (selectedConversationPublicKeyRef.current === keyToUse) {
        setPubKeyPlusGroupName(pubKeyPlusGroupName);
      }

      // Compute initial unread state from persisted last-read timestamps
      const lastReadTimestamps = getCachedLastReadTimestamps(publicKey);
      const { mutedConversations: muted, archivedGroups: archived } =
        useStore.getState();
      const unreadMap = new Map<string, number>();
      for (const [k, convo] of Object.entries(conversationsResponse)) {
        if (k === keyToUse) continue; // Currently selected conversation
        if (muted.has(k) || archived.has(k)) continue; // Skip muted/archived
        const msgTs = latestIncomingTimestamp(convo, publicKey);
        if (!msgTs) continue; // No incoming messages
        const lastRead = lastReadTimestamps[k];
        if (lastRead === undefined) {
          // First time seeing this conversation — seed as read
          cacheLastReadTimestamp(publicKey, k, msgTs);
        } else if (msgTs > lastRead) {
          unreadMap.set(k, 1);
        }
      }
      // Sync unread state to IndexedDB so the service worker has accurate counts
      syncUnreadConversations(Array.from(unreadMap.keys()));
      if (unreadMap.size > 0) {
        initializeUnread(unreadMap);
      } else {
        // No unread conversations — clear any stale PWA badge set by the service worker
        navigator.clearAppBadge?.()?.catch?.(() => {});
      }
      // Mark the selected conversation as read
      if (keyToUse) {
        const selectedConvo = conversationsResponse[keyToUse];
        if (selectedConvo?.messages[0]) {
          cacheLastReadTimestamp(
            publicKey,
            keyToUse,
            selectedConvo.messages[0]!.MessageInfo.TimestampNanos
          );
        }
      }

      // Cache messages for the selected conversation
      const updatedConvo = updatedConversations[keyToUse];
      if (updatedConvo) {
        cacheConversationMessages(publicKey, keyToUse, updatedConvo.messages);
      }

      // Cache the last selected conversation (use the actual current selection
      // so a user navigation during rehydration is preserved on next launch)
      const finalKey = selectedConversationPublicKeyRef.current || keyToUse;
      if (finalKey) {
        cacheLastConversationKey(publicKey, finalKey);
      }
    } catch (e) {
      if (!renderedFromCache) {
        toast.error(`Error fetching current conversation: ${e}`);
        console.error(e);
      }
    } finally {
      // Only clear conversation loading if user hasn't navigated away —
      // their click handler owns the loading state in that case.
      if (
        !selectedConversationPublicKeyRef.current ||
        selectedConversationPublicKeyRef.current === keyToUse
      ) {
        setLoadingConversation(false);
      }
      setConversationsLoading(false);
    }

    if (autoScroll) {
      scrollContainerToElement(".conversations-list", ".selected-conversation");
    }
  };

  const updateUsernameToPublicKeyMapFromConversations = async (
    DMChats: Conversation[]
  ) => {
    const newPublicKeysToGet = new Set<string>();
    DMChats.map((e) => {
      newPublicKeysToGet.add(e.firstMessagePublicKey);
      e.messages.forEach((m: NewMessageEntryResponse) => {
        newPublicKeysToGet.add(m.RecipientInfo.OwnerPublicKeyBase58Check);
        newPublicKeysToGet.add(m.SenderInfo.OwnerPublicKeyBase58Check);
      });
    });
    return await fetchUsersStateless(Array.from(newPublicKeysToGet));
  };

  // TODO: add support pagination
  const getConversation = async (
    pubKeyPlusGroupName: string,
    currentConversations = conversations
  ): Promise<{
    updatedConversations: ConversationMap;
    pubKeyPlusGroupName: string;
  }> => {
    if (!appUser) {
      toast.error("You must be logged in to use this feature");
      return { updatedConversations: {}, pubKeyPlusGroupName: "" };
    }

    const currentConvo = currentConversations[pubKeyPlusGroupName];
    if (!currentConvo) {
      return { updatedConversations: {}, pubKeyPlusGroupName: "" };
    }
    const convo = currentConvo.messages;

    if (currentConvo.ChatType === ChatType.DM) {
      let messages;
      try {
        messages = await getPaginatedDMThread({
          UserGroupOwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
          UserGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
          PartyGroupOwnerPublicKeyBase58Check:
            currentConvo.firstMessagePublicKey,
          PartyGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
          MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
          StartTimeStamp: new Date().valueOf() * 1e6,
        });
      } catch (e: unknown) {
        // New DM thread with no messages yet returns a 404 — treat as empty.
        // Re-throw any other error (network, auth, 500) so callers can handle it.
        const status = (e as { status?: number }).status;
        if (status !== 404) throw e;
        return {
          updatedConversations: {
            ...currentConversations,
            [pubKeyPlusGroupName]: {
              firstMessagePublicKey: currentConvo.firstMessagePublicKey,
              messages: [],
              ChatType: ChatType.DM,
            },
          },
          pubKeyPlusGroupName,
        };
      }

      const { decrypted, updatedAllAccessGroups } =
        await decryptAccessGroupMessagesWithRetry(
          appUser.PublicKeyBase58Check,
          messages.ThreadMessages,
          allAccessGroups
        );

      setAllAccessGroups(updatedAllAccessGroups);

      const updatedConversations = {
        ...currentConversations,
        ...{
          [pubKeyPlusGroupName]: {
            firstMessagePublicKey: decrypted.length
              ? decrypted[0]!.IsSender
                ? decrypted[0]!.RecipientInfo.OwnerPublicKeyBase58Check
                : decrypted[0]!.SenderInfo.OwnerPublicKeyBase58Check
              : currentConvo.firstMessagePublicKey,
            messages: decrypted,
            ChatType: ChatType.DM,
          },
        },
      };

      if (
        currentConvo &&
        currentConvo.firstMessagePublicKey &&
        usernameByPublicKeyBase58Check[currentConvo.firstMessagePublicKey] ===
          undefined
      ) {
        await fetchUsersStateless([currentConvo.firstMessagePublicKey]);
      }

      return {
        updatedConversations,
        pubKeyPlusGroupName,
      };
    } else {
      if (!convo || convo.length === 0) {
        return {
          updatedConversations: currentConversations,
          pubKeyPlusGroupName,
        };
      }
      const firstMessage = convo[0]!;
      const messages = await getPaginatedGroupChatThread({
        UserPublicKeyBase58Check:
          firstMessage.RecipientInfo.OwnerPublicKeyBase58Check,
        AccessGroupKeyName: firstMessage.RecipientInfo.AccessGroupKeyName,
        StartTimeStamp: firstMessage.MessageInfo.TimestampNanos * 10,
        MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
      });

      const { decrypted, updatedAllAccessGroups } =
        await decryptAccessGroupMessagesWithRetry(
          appUser.PublicKeyBase58Check,
          messages.GroupChatMessages,
          allAccessGroups
        );
      setAllAccessGroups(updatedAllAccessGroups);

      const updatedConversations = {
        ...currentConversations,
        ...{
          [pubKeyPlusGroupName]: {
            firstMessagePublicKey:
              firstMessage.RecipientInfo.OwnerPublicKeyBase58Check,
            messages: decrypted,
            ChatType: ChatType.GROUPCHAT,
          },
        },
      };

      return {
        updatedConversations,
        pubKeyPlusGroupName,
      };
    }
  };

  const getCurrentChatName = () => {
    if (!selectedConversation || !Object.keys(activeChatUsersMap).length) {
      return "";
    }

    const name = getChatNameFromConversation(
      selectedConversation,
      activeChatUsersMap,
      allAccessGroups
    );
    return (
      name ||
      shortenLongWord(
        selectedConversation.messages.length
          ? selectedConversation.messages[0]!.RecipientInfo
              .OwnerPublicKeyBase58Check
          : selectedConversation.firstMessagePublicKey
      ) ||
      ""
    );
  };

  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    if (!appUser || !hasSetupMessaging(appUser)) return;

    const pending = useStore.getState().pendingConversationKey;
    if (pending) useStore.getState().setPendingConversationKey(null);

    setConversationsLoading(true);
    setConversationsError(null);
    rehydrateConversation(pending || "", false, !isMobile || !!pending);
  }, [appUser, isMobile, rehydrateConversation]);

  // In-app deselect (chevron, swipe-back). Pop the URL entry instead of
  // calling setState directly — the popstate listener clears selection.
  // Falling back to setState handles the rare case where there's no /chat/
  // entry to pop (e.g. selection was set without going through the URL).
  const handleMobileBack = useCallback(() => {
    if (window.location.pathname.startsWith(CHAT_PATH_PREFIX)) {
      window.history.back();
      return;
    }
    startTransition(() => {
      setSelectedConversationPublicKey("");
      setEditingMessage(null);
      setReplyToMessage(null);
    });
  }, []);

  const swipeHandlers = useSwipeBack(handleMobileBack);

  // ── URL ↔ selection sync ──────────────────────────────────────────────
  // Each conversation gets its own URL (/chat/<encoded-key>) so the browser
  // back button returns to the chat list (and another press exits) on every
  // platform — desktop browsers, mobile web, and PWAs alike. This replaces
  // the mobile-only synthetic-history-entry trick from useAndroidBack.
  //
  // History semantics: list → chat = push (so back returns to the list);
  // chat → chat or chat → list (state-only) = replace (no deep stacks of
  // recently-viewed chats, no clutter of "/" entries).
  const skipUrlSyncRef = useRef(false);
  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const desiredPath = chatPathFromKey(selectedConversationPublicKey);
    if (window.location.pathname === desiredPath) return;
    const wasOnChatPath = window.location.pathname.startsWith(CHAT_PATH_PREFIX);
    const goingToChat = selectedConversationPublicKey !== "";
    if (goingToChat && !wasOnChatPath) {
      window.history.pushState({}, "", desiredPath);
    } else {
      window.history.replaceState({}, "", desiredPath);
    }
  }, [selectedConversationPublicKey]);

  useEffect(() => {
    const onPop = () => {
      const fromUrl = conversationKeyFromPath(window.location.pathname);
      if (fromUrl === selectedConversationPublicKeyRef.current) return;
      skipUrlSyncRef.current = true;
      startTransition(() => {
        setSelectedConversationPublicKey(fromUrl);
        setEditingMessage(null);
        setReplyToMessage(null);
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const conversationsReady = Object.keys(conversations).length > 0;
  // Guard: if the selected key doesn't match any conversation (e.g. stale
  // cache, race condition during hydration), use a safe key for THIS render
  // and schedule a state update for the next. Prevents crashes from bare
  // `selectedConversation.property` accesses deep in the JSX tree.
  let safeSelectedKey = selectedConversationPublicKey;
  if (
    conversationsReady &&
    safeSelectedKey &&
    !conversations[safeSelectedKey] &&
    !loadingConversation
  ) {
    safeSelectedKey = Object.keys(conversations)[0] || "";
    setSelectedConversationPublicKey(safeSelectedKey);
  }
  const selectedConversation = conversations[safeSelectedKey];
  const isGroupChat = selectedConversation?.ChatType === ChatType.GROUPCHAT;
  const isChatOwner =
    isGroupChat &&
    appUser &&
    selectedConversation?.messages[0]?.RecipientInfo
      ?.OwnerPublicKeyBase58Check === appUser.PublicKeyBase58Check;
  const isGroupOwner = isGroupChat && isChatOwner;
  const selectedGroupImageUrl =
    isGroupChat && selectedConversation?.messages[0]
      ? getGroupImageUrl(
          allAccessGroups,
          selectedConversation.messages[0].RecipientInfo
            .OwnerPublicKeyBase58Check,
          selectedConversation.messages[0].RecipientInfo.AccessGroupKeyName
        )
      : undefined;
  const pinnedData =
    isGroupChat && selectedConversation?.messages[0]
      ? getGroupPinnedMessage(
          allAccessGroups,
          selectedConversation.messages[0]!.RecipientInfo
            .OwnerPublicKeyBase58Check,
          selectedConversation.messages[0]!.RecipientInfo.AccessGroupKeyName
        )
      : undefined;
  const pinnedMessageTimestamp = pinnedData?.timestamp;
  const pinnedPreview = pinnedData?.preview;
  const pinnedMessage = useMemo(() => {
    if (!pinnedMessageTimestamp || !selectedConversation) return undefined;
    return selectedConversation.messages.find(
      (m) => m.MessageInfo.TimestampNanosString === pinnedMessageTimestamp
    );
  }, [pinnedMessageTimestamp, selectedConversation]);
  const [loadingPinnedMessage, setLoadingPinnedMessage] = useState(false);
  const handleScrollToPinnedMessage = useCallback(async () => {
    if (!pinnedMessageTimestamp) return;
    // If the message is already in the DOM, scroll to it directly
    if (bubblesRef.current?.scrollToMessage(pinnedMessageTimestamp)) return;
    // Message not loaded — fetch a batch around the pinned timestamp
    if (!appUser || !selectedConversation || !isGroupChat) return;
    const recipientInfo = selectedConversation.messages[0]?.RecipientInfo;
    if (!recipientInfo) return;
    const convKey = selectedConversationPublicKeyRef.current;
    setLoadingPinnedMessage(true);
    try {
      const tsNanos = BigInt(pinnedMessageTimestamp);
      const messages = await getPaginatedGroupChatThread({
        UserPublicKeyBase58Check: recipientInfo.OwnerPublicKeyBase58Check,
        AccessGroupKeyName: recipientInfo.AccessGroupKeyName,
        StartTimeStampString: (tsNanos + 1n).toString(),
        MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
      });
      if (messages.GroupChatMessages.length === 0) {
        toast.error("Pinned message not found");
        return;
      }
      const { decrypted, updatedAllAccessGroups } =
        await decryptAccessGroupMessagesWithRetry(
          appUser.PublicKeyBase58Check,
          messages.GroupChatMessages,
          allAccessGroups
        );
      setAllAccessGroups(updatedAllAccessGroups);
      // Snapshot the live tail so Jump-to-Latest can restore it. Guard against
      // overwriting a prior snapshot from a search/reply jump in the same conv.
      const currentConv = conversationsRef.current[convKey];
      if (
        currentConv &&
        (!savedMessagesRef.current ||
          savedMessagesRef.current.convKey !== convKey)
      ) {
        savedMessagesRef.current = {
          messages: currentConv.messages,
          convKey,
        };
      }
      viewingHistoricalSliceRef.current = convKey;
      setViewingHistoricalSliceKey(convKey);
      setConversations((prev) => {
        const existing = prev[convKey];
        if (!existing) return prev;
        const optimistic = existing.messages.filter(
          (m: any) => m._localId && m._status === "sending"
        );
        return updateConv(prev, convKey, (c) => ({
          ...c,
          messages: [...decrypted, ...optimistic],
        }));
      });
      // Wait for re-render then scroll
      requestAnimationFrame(() => {
        bubblesRef.current?.scrollToMessage(pinnedMessageTimestamp);
      });
    } catch (e) {
      console.error("Failed to load pinned message:", e);
      toast.error("Failed to load pinned message");
    } finally {
      setLoadingPinnedMessage(false);
    }
  }, [
    pinnedMessageTimestamp,
    appUser,
    selectedConversation,
    isGroupChat,
    allAccessGroups,
    setAllAccessGroups,
  ]);
  const replyFetchIdRef = useRef(0);
  const savedMessagesRef = useRef<{
    messages: DecryptedMessageEntryResponse[];
    convKey: string;
  } | null>(null);

  const handleReloadLatest = useCallback(async (): Promise<boolean> => {
    const convKey = selectedConversationPublicKeyRef.current;

    const clearHistoricalState = () => {
      viewingHistoricalSliceRef.current = null;
      setViewingHistoricalSliceKey(null);
      savedMessagesRef.current = null;
    };

    // Always re-fetch the latest tail. The previous snapshot-restore path
    // could resurrect a thin snapshot (e.g. a 1-message preview from the
    // initial conversation list) that left the user staring at one bubble.
    const conv = conversationsRef.current[convKey];
    if (!appUser || !conv) {
      clearHistoricalState();
      return false;
    }
    const firstMsg = conv.messages[0];
    if (!firstMsg) {
      clearHistoricalState();
      return false;
    }

    try {
      let rawMessages;
      if (conv.ChatType === ChatType.GROUPCHAT) {
        const recipientInfo = firstMsg.RecipientInfo;
        if (!recipientInfo) {
          clearHistoricalState();
          return false;
        }
        const resp = await getPaginatedGroupChatThread({
          UserPublicKeyBase58Check: recipientInfo.OwnerPublicKeyBase58Check,
          AccessGroupKeyName: recipientInfo.AccessGroupKeyName,
          StartTimeStamp: new Date().valueOf() * 1e6,
          MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
        });
        rawMessages = resp.GroupChatMessages;
      } else {
        const resp = await getPaginatedDMThread({
          UserGroupOwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
          UserGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
          PartyGroupOwnerPublicKeyBase58Check: conv.firstMessagePublicKey,
          PartyGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
          StartTimeStamp: new Date().valueOf() * 1e6,
          MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
        });
        rawMessages = resp.ThreadMessages;
      }

      // User may have switched conversations during the fetch.
      if (selectedConversationPublicKeyRef.current !== convKey) return false;

      if (!rawMessages || rawMessages.length === 0) {
        // Keep historical state so the user can retry — clearing it here
        // would hide the Jump-to-Latest button and trap them.
        toast.error("Failed to reload messages");
        return false;
      }

      const { decrypted, updatedAllAccessGroups } =
        await decryptAccessGroupMessagesWithRetry(
          appUser.PublicKeyBase58Check,
          rawMessages,
          allAccessGroupsRef.current
        );

      if (selectedConversationPublicKeyRef.current !== convKey) return false;

      setAllAccessGroups(updatedAllAccessGroups);

      setConversations((prev) => {
        const existing = prev[convKey];
        if (!existing) return prev;
        const optimistic = existing.messages.filter(
          (m: any) =>
            m._localId && (m._status === "sending" || m._status === "sent")
        );
        const decryptedTs = new Set(
          decrypted.map((m) => m.MessageInfo.TimestampNanosString)
        );
        const newOptimistic = optimistic.filter(
          (m: any) => !decryptedTs.has(m.MessageInfo.TimestampNanosString)
        );
        return updateConv(prev, convKey, (c) => ({
          ...c,
          messages: [...newOptimistic, ...decrypted],
        }));
      });
      clearHistoricalState();
      return true;
    } catch (e) {
      console.error("Failed to reload latest:", e);
      // Don't clear historical state — the user is still viewing a slice
      // and needs the Jump-to-Latest button to retry.
      toast.error("Failed to reload messages");
      return false;
    }
  }, [appUser, setAllAccessGroups]);

  // Progressive forward-load. When the user is viewing a historical slice
  // (jumped to a search/reply/pinned message) and scrolls toward the bottom
  // of the slice, fetch the messages between the slice's newest and the
  // present. DeSo only paginates backward, so we walk back from "now" until
  // we close the gap or run out of newer messages.
  const isLoadingNewerRef = useRef(false);
  const handleLoadNewer = useCallback(async (): Promise<{ done: boolean }> => {
    if (isLoadingNewerRef.current) return { done: false };
    const convKey = selectedConversationPublicKeyRef.current;
    const conv = conversationsRef.current[convKey];
    if (!appUser || !conv || conv.messages.length === 0) return { done: true };

    const newestVisible = conv.messages[0]!;
    const newestVisibleNanos = BigInt(
      newestVisible.MessageInfo.TimestampNanosString
    );

    isLoadingNewerRef.current = true;
    try {
      const fetchedNewer: any[] = [];
      let cursorNanos = BigInt(Date.now()) * 1_000_000n + 1_000_000n;
      let done = false;

      // Safety cap — walking back 8 windows of 25 covers 200 newer messages.
      // If the gap is larger, the user can click Jump-to-Latest to skip.
      for (let i = 0; i < 8; i++) {
        let raw: any[];
        if (conv.ChatType === ChatType.GROUPCHAT) {
          const recip = newestVisible.RecipientInfo;
          if (!recip) {
            done = true;
            break;
          }
          const resp = await getPaginatedGroupChatThread({
            UserPublicKeyBase58Check: recip.OwnerPublicKeyBase58Check,
            AccessGroupKeyName: recip.AccessGroupKeyName,
            StartTimeStampString: cursorNanos.toString(),
            MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
          });
          raw = resp.GroupChatMessages;
        } else {
          const resp = await getPaginatedDMThread({
            UserGroupOwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
            UserGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            PartyGroupOwnerPublicKeyBase58Check: conv.firstMessagePublicKey,
            PartyGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            StartTimeStampString: cursorNanos.toString(),
            MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
          });
          raw = resp.ThreadMessages;
        }
        if (selectedConversationPublicKeyRef.current !== convKey) {
          return { done: true };
        }
        if (!raw || raw.length === 0) {
          done = true;
          break;
        }
        for (const m of raw) {
          const ts = BigInt(m.MessageInfo.TimestampNanosString);
          if (ts > newestVisibleNanos) fetchedNewer.push(m);
        }
        const oldestInBatch = BigInt(
          raw[raw.length - 1]!.MessageInfo.TimestampNanosString
        );
        if (oldestInBatch <= newestVisibleNanos) {
          done = true;
          break;
        }
        if (raw.length < MESSAGES_ONE_REQUEST_LIMIT) {
          done = true;
          break;
        }
        cursorNanos = oldestInBatch;
      }

      if (fetchedNewer.length > 0) {
        const { decrypted, updatedAllAccessGroups } =
          await decryptAccessGroupMessagesWithRetry(
            appUser.PublicKeyBase58Check,
            fetchedNewer,
            allAccessGroupsRef.current
          );
        if (selectedConversationPublicKeyRef.current !== convKey) {
          return { done: true };
        }
        setAllAccessGroups(updatedAllAccessGroups);
        setConversations((prev) => {
          const existing = prev[convKey];
          if (!existing) return prev;
          const existingTs = new Set(
            existing.messages.map(
              (m: any) => m.MessageInfo.TimestampNanosString
            )
          );
          const filtered = decrypted.filter(
            (m) => !existingTs.has(m.MessageInfo.TimestampNanosString)
          );
          return updateConv(prev, convKey, (c) => ({
            ...c,
            messages: [...filtered, ...c.messages],
          }));
        });
      }

      if (done) {
        viewingHistoricalSliceRef.current = null;
        setViewingHistoricalSliceKey(null);
        savedMessagesRef.current = null;
      }
      return { done };
    } catch (e) {
      console.error("Failed to load newer messages:", e);
      toast.error("Failed to load newer messages");
      return { done: false };
    } finally {
      isLoadingNewerRef.current = false;
    }
  }, [appUser, setAllAccessGroups]);

  // Clear the historical-slice state when the user switches conversations so
  // the saved snapshot from the previous conv doesn't leak into the new one
  // and the Jump-to-Latest button doesn't surface spuriously.
  useEffect(() => {
    if (
      viewingHistoricalSliceRef.current &&
      viewingHistoricalSliceRef.current !== selectedConversationPublicKey
    ) {
      viewingHistoricalSliceRef.current = null;
      setViewingHistoricalSliceKey(null);
      savedMessagesRef.current = null;
    }
  }, [selectedConversationPublicKey]);

  const handleScrollToReply = useCallback(
    async (ts: string) => {
      if (!appUser || !selectedConversation) return;
      const firstMsg = selectedConversation.messages[0];
      if (!firstMsg) return;
      const fetchId = ++replyFetchIdRef.current;
      const convKey = selectedConversationPublicKeyRef.current;
      try {
        const tsNanos = BigInt(ts);
        let rawMessages;
        if (isGroupChat) {
          const recipientInfo = firstMsg.RecipientInfo;
          if (!recipientInfo) return;
          const resp = await getPaginatedGroupChatThread({
            UserPublicKeyBase58Check: recipientInfo.OwnerPublicKeyBase58Check,
            AccessGroupKeyName: recipientInfo.AccessGroupKeyName,
            StartTimeStampString: (tsNanos + 1n).toString(),
            MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
          });
          rawMessages = resp.GroupChatMessages;
        } else {
          const resp = await getPaginatedDMThread({
            UserGroupOwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
            UserGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            PartyGroupOwnerPublicKeyBase58Check:
              selectedConversation.firstMessagePublicKey,
            PartyGroupKeyName: DEFAULT_KEY_MESSAGING_GROUP_NAME,
            StartTimeStampString: (tsNanos + 1n).toString(),
            MaxMessagesToFetch: MESSAGES_ONE_REQUEST_LIMIT,
          });
          rawMessages = resp.ThreadMessages;
        }
        if (replyFetchIdRef.current !== fetchId) return;
        if (!rawMessages || rawMessages.length === 0) {
          toast.error("Original message not found");
          return;
        }
        const hasTarget = rawMessages.some(
          (m: any) => m.MessageInfo?.TimestampNanosString === ts
        );
        if (!hasTarget) {
          toast.error("Original message not found");
          return;
        }
        const { decrypted, updatedAllAccessGroups } =
          await decryptAccessGroupMessagesWithRetry(
            appUser.PublicKeyBase58Check,
            rawMessages,
            allAccessGroupsRef.current
          );
        if (replyFetchIdRef.current !== fetchId) return;
        setAllAccessGroups(updatedAllAccessGroups);
        const currentConv = conversationsRef.current[convKey];
        if (
          currentConv &&
          (!savedMessagesRef.current ||
            savedMessagesRef.current.convKey !== convKey)
        ) {
          savedMessagesRef.current = {
            messages: currentConv.messages,
            convKey,
          };
        }
        viewingHistoricalSliceRef.current = convKey;
        setViewingHistoricalSliceKey(convKey);
        setConversations((prev) => {
          const existing = prev[convKey];
          if (!existing) return prev;
          const optimistic = existing.messages.filter(
            (m: any) => m._localId && m._status === "sending"
          );
          return updateConv(prev, convKey, (c) => ({
            ...c,
            messages: [...decrypted, ...optimistic],
          }));
        });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            bubblesRef.current?.scrollToMessage(ts);
          });
        });
      } catch (e) {
        console.error("Failed to load replied message:", e);
        toast.error("Failed to load original message");
      }
    },
    [appUser, selectedConversation, isGroupChat, setAllAccessGroups]
  );

  useEffect(() => {
    if (!pendingSearchJump) return;
    if (pendingSearchJump.convKey !== selectedConversationPublicKey) {
      setPendingSearchJump(null);
      return;
    }
    if (loadingConversation) return;
    if (!selectedConversation) return;

    const target = pendingSearchJump;
    setPendingSearchJump(null);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (selectedConversationPublicKeyRef.current !== target.convKey) return;
        if (bubblesRef.current?.scrollToMessage(target.ts)) return;
        void handleScrollToReply(target.ts);
      });
    });
  }, [
    pendingSearchJump,
    selectedConversationPublicKey,
    selectedConversation,
    loadingConversation,
    handleScrollToReply,
  ]);

  const handlePinMessage = useCallback(
    async (timestampNanosString: string, preview?: string) => {
      if (!appUser || !selectedConversation || !isGroupChat) return;
      const recipientInfo = selectedConversation.messages[0]?.RecipientInfo;
      if (!recipientInfo) return;
      const groupOwnerKey = recipientInfo.OwnerPublicKeyBase58Check;
      const groupKeyName = recipientInfo.AccessGroupKeyName;

      const group = allAccessGroups.find(
        (g) =>
          g.AccessGroupOwnerPublicKeyBase58Check === groupOwnerKey &&
          g.AccessGroupKeyName === groupKeyName
      );
      if (!group) return;

      const oldExtraData = { ...(group.ExtraData || {}) };
      const isPinning = !!timestampNanosString;
      // Truncate preview to 100 chars to keep ExtraData small
      const previewText = preview?.slice(0, 100) || "";

      // Optimistic: update Zustand store immediately
      const updatedGroups = allAccessGroups.map((g) => {
        if (
          g.AccessGroupOwnerPublicKeyBase58Check === groupOwnerKey &&
          g.AccessGroupKeyName === groupKeyName
        ) {
          const newExtraData = { ...(g.ExtraData || {}) };
          if (isPinning) {
            newExtraData[GROUP_PINNED_MESSAGE] = timestampNanosString;
            newExtraData[GROUP_PINNED_PREVIEW] = previewText;
          } else {
            delete newExtraData[GROUP_PINNED_MESSAGE];
            delete newExtraData[GROUP_PINNED_PREVIEW];
          }
          return { ...g, ExtraData: newExtraData };
        }
        return g;
      });
      useStore.setState({ allAccessGroups: updatedGroups });
      toast.success(isPinning ? "Message pinned" : "Message unpinned");

      try {
        const mergedExtraData = { ...(group.ExtraData || {}) };
        if (isPinning) {
          mergedExtraData[GROUP_PINNED_MESSAGE] = timestampNanosString;
          mergedExtraData[GROUP_PINNED_PREVIEW] = previewText;
        } else {
          mergedExtraData[GROUP_PINNED_MESSAGE] = "";
          mergedExtraData[GROUP_PINNED_PREVIEW] = "";
        }

        await withAuth(() =>
          updateAccessGroup({
            AccessGroupOwnerPublicKeyBase58Check: appUser.PublicKeyBase58Check,
            AccessGroupKeyName: groupKeyName,
            AccessGroupPublicKeyBase58Check:
              group.AccessGroupPublicKeyBase58Check,
            ExtraData: mergedExtraData,
          })
        );
      } catch (err) {
        console.error("Failed to pin message:", err);
        toast.error(
          isPinning ? "Failed to pin message" : "Failed to unpin message"
        );
        // Rollback
        const rollbackGroups = allAccessGroups.map((g) => {
          if (
            g.AccessGroupOwnerPublicKeyBase58Check === groupOwnerKey &&
            g.AccessGroupKeyName === groupKeyName
          ) {
            return { ...g, ExtraData: oldExtraData };
          }
          return g;
        });
        useStore.setState({ allAccessGroups: rollbackGroups });
      }
    },
    [appUser, selectedConversation, isGroupChat, allAccessGroups]
  );

  const chatMembers = membersByGroupKey[selectedConversationPublicKey];

  // Fetch presence data when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;
    if (isGroupChat) {
      if (chatMembers) {
        fetchPresenceForKeys(Object.keys(chatMembers));
      }
    } else if (selectedConversation.firstMessagePublicKey) {
      fetchPresenceForKeys([selectedConversation.firstMessagePublicKey]);
    }
  }, [
    safeSelectedKey,
    isGroupChat,
    chatMembers,
    fetchPresenceForKeys,
    selectedConversation,
  ]);

  const activeChatUsersMap: { [k: string]: string } = isGroupChat
    ? Object.keys(chatMembers || {}).reduce<{ [k: string]: string }>(
        (acc, curr) => ({
          ...acc,
          [curr]:
            chatMembers?.[curr]?.Username ||
            usernameByPublicKeyBase58Check[curr] ||
            "",
        }),
        { ...usernameByPublicKeyBase58Check }
      )
    : usernameByPublicKeyBase58Check;

  // For group chats, notify ALL members (relay skips the sender).
  // For DMs, notify just the other participant.
  // Always resolves the sender's username from available sources so callers don't need to pass it.
  const notifyConversation = (
    convKey: string,
    dmRecipient: string,
    fromUsername?: string
  ) => {
    const resolvedUsername =
      fromUsername ||
      usernameByPublicKeyBase58Check[appUser?.PublicKeyBase58Check ?? ""] ||
      appUser?.ProfileEntryResponse?.Username ||
      appUser?.PublicKeyBase58Check ||
      "Someone";
    const conv = conversations[convKey];
    const members = membersByGroupKey[convKey];
    if (conv?.ChatType === ChatType.GROUPCHAT && members) {
      const recipientInfo = conv.messages[0]?.RecipientInfo;
      const groupKeyName = recipientInfo?.AccessGroupKeyName;
      // Resolve human-readable display name for notification title
      const groupName =
        (recipientInfo && groupKeyName
          ? getGroupDisplayName(
              allAccessGroups,
              recipientInfo.OwnerPublicKeyBase58Check,
              groupKeyName
            )
          : undefined) || groupKeyName?.replace(/\0/g, "");
      sendNotify(convKey, Object.keys(members), resolvedUsername, groupName);
    } else {
      sendNotify(convKey, [dmRecipient], resolvedUsername);
    }
  };
  return (
    <div className="h-full">
      <UserActionMenu
        onMessage={(pk) =>
          rehydrateConversation(pk + DEFAULT_KEY_MESSAGING_GROUP_NAME, true)
        }
        onViewProfile={(pk, un) =>
          setProfileModal({ publicKey: pk, username: un })
        }
      />

      {profileModal && (
        <ProfileModal
          publicKey={profileModal.publicKey}
          username={profileModal.username}
          isSelf={
            !!appUser && appUser.PublicKeyBase58Check === profileModal.publicKey
          }
          onClose={() => setProfileModal(null)}
          onMessage={() =>
            rehydrateConversation(
              profileModal.publicKey + DEFAULT_KEY_MESSAGING_GROUP_NAME,
              true
            )
          }
        />
      )}

      {/* Onboarding wizard for new users */}
      {showOnboarding &&
        appUser &&
        hasSetupMessaging(appUser) &&
        !isLoadingUser && (
          <div className="h-full overflow-y-auto">
            <OnboardingWizard onComplete={handleOnboardingComplete} />
          </div>
        )}

      {/* Loading / setup states (non-onboarding) */}
      {!showOnboarding && (!hasSetupMessaging(appUser) || isLoadingUser) && (
        <div className="m-auto relative top-8 overflow-hidden pt-[30px] pb-[50px]">
          <div className="bg-gradient"></div>
          <div className="w-full lg:max-w-[1200px] m-auto bg-transparent p-0 shadow-none">
            <div>
              {isLoadingUser && (
                <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-10">
                  <Loader2 className="w-11 h-11 animate-spin text-brand" />
                </div>
              )}
              {!hasSetupMessaging(appUser) && !isLoadingUser && (
                <div className="text-left flex flex-col lg:flex-row items-center justify-between">
                  <div className="w-full lg:w-[50%]">
                    <div>
                      {appUser ? (
                        <div>
                          <h2 className="text-2xl font-bold mb-3 text-ink">
                            Set up your account
                          </h2>
                          <p className="text-lg mb-6 text-fg-400">
                            It seems like your account needs more configuration
                            to be able to send messages. Press the button below
                            to set it up automatically
                          </p>
                        </div>
                      ) : (
                        <div className="text-left w-full md:max-w-[100%]">
                          <h2 className="text-3xl lg:text-3xl font-semibold mb-6 text-ink">
                            Chat with anyone, on DeSo or Ethereum. Without the
                            risk of being censored.
                          </h2>
                          <p className="text-sm mb-5 text-fg-400">
                            DeSo Chat Protocol is a censorship-resistant
                            messaging protocol built on top of the DeSo
                            blockchain. It enables fully decentralized
                            cross-chain messaging between DeSo and Ethereum
                            wallets (and soon, Solana).
                          </p>
                          <p className="text-sm mb-5 text-fg-400">
                            This is possible due to DeSo's infinite-state
                            blockchain & derived keys cryptography.
                          </p>
                          <p className="text-sm mb-5 text-fg-400">
                            Messages are stored directly on-chain, at an average
                            cost of ~$0.000002 (<em>basically free</em>), with
                            end-to-end encryption, and support for DMs & group
                            chats — including fully on-chain social, identity,
                            creator coins, tokens & NFTs.
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-6 text-brand">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> E2EE messages &
                        group chats
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> 100% on-chain (fully
                        synced)
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> Sign in with DeSo &
                        MetaMask
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> Message DeSo &
                        Ethereum wallets
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> Open-source & built
                        on DeSo
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> On-chain social,
                        identity & assets
                      </div>
                    </div>
                    <MessagingSetupButton />
                    <p className="mt-5 text-sm mb-5 text-blue-300/40">
                      This project is fully open-sourced for developers to fork
                      & build on top of. You can add features like ENS support,
                      NFT profile pictures, message tipping, paid DMs,
                      token-gated group chats and more. Visit&nbsp;
                      <a
                        target="_blank"
                        className="underline text-blue-300/40 hover:text-blue-300"
                        href="https://github.com/sungkhum/chaton"
                        rel="noreferrer"
                      >
                        Github Repository &rarr;
                      </a>{" "}
                      or&nbsp;
                      <a
                        target="_blank"
                        className="underline text-blue-300/40 hover:text-blue-300"
                        href="https://github.com/sungkhum/chaton"
                        rel="noreferrer"
                      >
                        Developer Docs &rarr;
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {hasSetupMessaging(appUser) && !isLoadingUser && (
                <MessagingConversationButton onClick={rehydrateConversation} />
              )}
            </div>
          </div>
        </div>
      )}
      {!showOnboarding &&
        hasSetupMessaging(appUser) &&
        appUser &&
        !isLoadingUser && (
          <div className="flex h-full">
            {(!isMobile || !selectedConversationPublicKey) && (
              <ViewTransition
                enter="chat-list-enter"
                exit="chat-list-exit"
                default="none"
              >
                <div className="w-full md:w-[340px] lg:w-[380px] xl:w-[420px] border-r border-ink/5 bg-surface-deep shrink-0">
                  <MessagingConversationAccount
                    rehydrateConversation={rehydrateConversation}
                    refreshConversations={() =>
                      rehydrateConversation("", false, false)
                    }
                    conversationsLoading={conversationsLoading}
                    conversationsError={conversationsError}
                    onRetryLoad={() => {
                      setConversationsError(null);
                      setConversationsLoading(true);
                      rehydrateConversation();
                    }}
                    onClick={async (key: string) => {
                      if (key === selectedConversationPublicKey) {
                        // Still clear unread badge if a message arrived while viewing
                        if (unreadByConversation.has(key)) {
                          clearUnread(key);
                          removeUnreadConversation(key);
                          const ts =
                            conversations[key]?.messages[0]?.MessageInfo
                              ?.TimestampNanos;
                          sendRead(key, ts ? String(ts) : undefined);
                          if (appUser && ts) {
                            cacheLastReadTimestamp(
                              appUser.PublicKeyBase58Check,
                              key,
                              ts
                            );
                          }
                        }
                        return;
                      }
                      // Capture the last-read timestamp BEFORE updating it,
                      // so MessagingBubblesAndAvatar can show the "new messages" divider.
                      if (appUser) {
                        const timestamps = getCachedLastReadTimestamps(
                          appUser.PublicKeyBase58Check
                        );
                        setOpenedLastReadNanos(timestamps[key] ?? null);
                      } else {
                        setOpenedLastReadNanos(null);
                      }

                      startTransition(() => {
                        setSelectedConversationPublicKey(key);
                        setPubKeyPlusGroupName(key);
                        setLoadingConversation(true);
                        setEditingMessage(null);
                        setReplyToMessage(null);
                      });
                      clearUnread(key);
                      removeUnreadConversation(key);
                      {
                        const ts =
                          conversations[key]?.messages[0]?.MessageInfo
                            ?.TimestampNanos;
                        sendRead(key, ts ? String(ts) : undefined);
                        if (appUser && ts) {
                          cacheLastReadTimestamp(
                            appUser.PublicKeyBase58Check,
                            key,
                            ts
                          );
                        }
                      }

                      // Cache the last selected conversation
                      if (appUser) {
                        cacheLastConversationKey(
                          appUser.PublicKeyBase58Check,
                          key
                        );
                      }

                      // Lazy acceptance: if user opens a group they're already
                      // participating in (classified as "chat" via message check)
                      // but haven't explicitly accepted, create the on-chain
                      // association in the background. Skip groups in Requests —
                      // those require explicit Accept action.
                      if (appUser) {
                        const convo = conversations[key];
                        if (
                          convo?.ChatType === ChatType.GROUPCHAT &&
                          convo.firstMessagePublicKey !==
                            appUser.PublicKeyBase58Check &&
                          !acceptedGroups.has(key) &&
                          !requestConversations[key]
                        ) {
                          acceptGroup(key);
                          const ownerPk = key.slice(0, PUBLIC_KEY_LENGTH);
                          const gkn = key.slice(PUBLIC_KEY_LENGTH);
                          createGroupAcceptedAssociation(
                            appUser.PublicKeyBase58Check,
                            ownerPk,
                            gkn
                          )
                            .then(() =>
                              fetchAcceptedGroups(
                                appUser.PublicKeyBase58Check
                              ).then((ids) => mergeAcceptedGroupIds(ids))
                            )
                            .catch(() => {});
                        }
                      }

                      setLockRefresh(true);

                      // Start the network fetch immediately — don't wait for
                      // the cache read to finish first (async-parallel).
                      const freshPromise = getConversation(key);

                      // Try cache-first for messages
                      if (appUser) {
                        const cached = await getCachedConversationMessages(
                          appUser.PublicKeyBase58Check,
                          key
                        );
                        if (
                          cached &&
                          cached.length > 0 &&
                          key === selectedConversationPublicKeyRef.current
                        ) {
                          setConversations((prev) => {
                            const existing = prev[key];
                            if (!existing) return prev;
                            const listHead = existing.messages[0];
                            const cacheHead = cached[0] as
                              | DecryptedMessageEntryResponse
                              | undefined;
                            // If the conversation list has a newer message than
                            // the cache (message arrived since cache was written),
                            // keep it at position 0 so the sort order doesn't
                            // visibly jump during the slide transition.
                            let messages =
                              cached as DecryptedMessageEntryResponse[];
                            if (
                              listHead &&
                              cacheHead &&
                              listHead.MessageInfo.TimestampNanos >
                                cacheHead.MessageInfo.TimestampNanos
                            ) {
                              messages = [listHead, ...messages];
                            }
                            return {
                              ...prev,
                              [key]: { ...existing, messages },
                            };
                          });
                          setLoadingConversation(false);
                        }
                      }

                      try {
                        const { updatedConversations, pubKeyPlusGroupName } =
                          await freshPromise;
                        // Only apply if this conversation is still selected
                        // (prevents stale fetch from overwriting after rapid clicks)
                        if (key === selectedConversationPublicKeyRef.current) {
                          const updatedConvo = updatedConversations[key];
                          if (updatedConvo) {
                            setConversations((prev) => ({
                              ...prev,
                              [key]: updatedConvo,
                            }));

                            // Cache the fetched messages
                            if (appUser) {
                              cacheConversationMessages(
                                appUser.PublicKeyBase58Check,
                                key,
                                updatedConvo.messages
                              );
                            }
                          }
                          setPubKeyPlusGroupName(pubKeyPlusGroupName);
                        }
                      } finally {
                        // Only unlock if still on this conversation to avoid
                        // releasing the lock for a subsequent click's fetch
                        if (key === selectedConversationPublicKeyRef.current) {
                          setLoadingConversation(false);
                          setLockRefresh(false);
                        }
                      }
                    }}
                    conversations={chatConversations}
                    requestConversations={requestConversations}
                    archivedConversations={archivedConversations}
                    onAccept={handleAcceptRequest}
                    onBlock={handleBlockRequest}
                    onDismiss={handleDismissRequest}
                    onAcceptGroup={handleAcceptGroupRequest}
                    onLeaveGroup={handleLeaveGroupRequest}
                    onUnarchive={handleUnarchiveGroup}
                    onUnarchiveChat={handleUnarchiveChat}
                    onUnblock={handleUnblock}
                    onUndismiss={handleUndismiss}
                    blockedUsers={blockedUsers}
                    dismissedUsers={dismissedUsers}
                    getUsernameByPublicKeyBase58Check={
                      usernameByPublicKeyBase58Check
                    }
                    profilePicByPublicKey={profilePicByPublicKey}
                    selectedConversationPublicKey={
                      selectedConversationPublicKey
                    }
                    unreadByConversation={unreadByConversation}
                    mutedConversations={mutedConversations}
                    searchQuery={searchQuery}
                    searchResults={searchResults}
                    isSearching={isMessageSearching}
                    isDeepSearching={isDeepSearching}
                    searchProgress={searchProgress}
                    onSearchQueryChange={setSearchQuery}
                    onSearchResultClick={handleSearchResultClick}
                    searchClearTrigger={searchClearTrigger}
                    highlightsByConversation={highlightsByConversation}
                  />
                </div>
              </ViewTransition>
            )}

            {(!isMobile || selectedConversationPublicKey) && (
              <ViewTransition
                enter="chat-detail-enter"
                exit="chat-detail-exit"
                default="none"
              >
                <div
                  {...(isMobile ? swipeHandlers : {})}
                  className="w-full h-full shrink-0 md:flex-1 md:min-w-0 bg-surface flex flex-col"
                >
                  <header className="flex justify-between items-center border-b border-ink/5 relative px-4 md:px-5 h-14 shrink-0">
                    <button
                      type="button"
                      onClick={handleMobileBack}
                      aria-label="Back to conversations"
                      className="md:hidden shrink-0 -ml-2 mr-1 p-2 rounded-full text-fg-400 hover:text-ink hover:bg-ink/10 active:bg-ink/15 transition-colors cursor-pointer"
                    >
                      <ChevronLeft className="w-6 h-6" strokeWidth={2.25} />
                    </button>
                    {selectedConversation &&
                      (selectedConversation.messages[0] ||
                        (!isGroupChat &&
                          selectedConversation.firstMessagePublicKey)) && (
                        <div className="flex items-center gap-2 min-w-0 px-2 md:hidden">
                          {isGroupChat && selectedGroupImageUrl && (
                            <MessagingDisplayAvatar
                              publicKey={getCurrentChatName()}
                              groupChat
                              groupImageUrl={selectedGroupImageUrl}
                              diameter={32}
                            />
                          )}
                          <div className="min-w-0">
                            <span className="text-ink font-bold text-base truncate block">
                              {getCurrentChatName()}
                            </span>
                            {(() => {
                              if (isGroupChat) {
                                const memberKeys = chatMembers
                                  ? Object.keys(chatMembers)
                                  : [];
                                const online = getOnlineCount(memberKeys);
                                const total = memberKeys.length;
                                if (total === 0) return null;
                                return (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-fg-500">
                                      {total}{" "}
                                      {total === 1 ? "member" : "members"}
                                    </span>
                                    {online > 0 && (
                                      <span className="flex items-center gap-1 text-brand/80">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#34F080] animate-pulse" />
                                        {online} online
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              const otherKey =
                                selectedConversation?.firstMessagePublicKey;
                              if (!otherKey) return null;
                              const presence = getPresence(otherKey);
                              if (presence.status === "online")
                                return (
                                  <span className="flex items-center gap-1 text-brand text-xs">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#34F080] animate-pulse" />
                                    Online
                                  </span>
                                );
                              if (presence.status === "last-seen")
                                return (
                                  <span className="text-fg-500 text-xs whitespace-nowrap">
                                    {formatLastSeen(presence.timestamp)}
                                  </span>
                                );
                              return null;
                            })()}
                          </div>
                        </div>
                      )}
                    <div className="text-fg-400 items-center hidden md:block">
                      {isGroupChat
                        ? (() => {
                            const memberKeys = chatMembers
                              ? Object.keys(chatMembers)
                              : [];
                            const online = getOnlineCount(memberKeys);
                            const total = memberKeys.length;
                            if (total === 0) return null;
                            return (
                              <span className="text-fg-500 text-sm whitespace-nowrap inline-flex items-center gap-1">
                                {total} {total === 1 ? "member" : "members"}
                                {online > 0 && (
                                  <>
                                    {" "}
                                    ·{" "}
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#34F080] animate-pulse inline-block" />
                                    <span className="text-brand/80">
                                      {online} online
                                    </span>
                                  </>
                                )}
                              </span>
                            );
                          })()
                        : (() => {
                            const otherKey =
                              selectedConversation?.firstMessagePublicKey;
                            if (!otherKey) return null;
                            const presence = getPresence(otherKey);
                            if (presence.status === "online")
                              return (
                                <span className="flex items-center gap-1.5 text-brand text-sm">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#34F080] animate-pulse" />
                                  Online
                                </span>
                              );
                            if (presence.status === "last-seen")
                              return (
                                <span className="text-fg-500 text-sm whitespace-nowrap">
                                  {formatLastSeen(presence.timestamp)}
                                </span>
                              );
                            return null;
                          })()}
                    </div>
                    <div
                      className={`flex items-center gap-3 justify-end shrink-0 ${
                        !isGroupOwner ? "md:w-full md:shrink" : ""
                      }`}
                    >
                      {selectedConversationPublicKey && (
                        <button
                          onClick={() => {
                            toggleMute(selectedConversationPublicKey);
                            const wasMuted = mutedConversations.has(
                              selectedConversationPublicKey
                            );
                            toast(
                              wasMuted
                                ? "Notifications unmuted"
                                : "Notifications muted",
                              {
                                icon: wasMuted ? (
                                  <Bell className="w-4 h-4" />
                                ) : (
                                  <BellOff className="w-4 h-4" />
                                ),
                                duration: 2000,
                              }
                            );
                          }}
                          className="p-2 rounded-full hover:bg-ink/10 transition-colors cursor-pointer"
                          title={
                            mutedConversations.has(
                              selectedConversationPublicKey
                            )
                              ? "Unmute notifications"
                              : "Mute notifications"
                          }
                        >
                          {mutedConversations.has(
                            selectedConversationPublicKey
                          ) ? (
                            <BellOff className="w-5 h-5 text-fg-500" />
                          ) : (
                            <Bell className="w-5 h-5 text-fg-400" />
                          )}
                        </button>
                      )}
                      {isGroupChat ? (
                        <Suspense fallback={null}>
                          <LazyManageMembersDialog
                            conversation={selectedConversation}
                            conversationKey={selectedConversationPublicKey}
                            onSuccess={rehydrateConversation}
                            onLeaveGroup={(
                              conversationKey,
                              groupOwnerPublicKey,
                              groupKeyName
                            ) => {
                              setArchiveConfirm({
                                type: "group",
                                conversationKey,
                                publicKey: groupOwnerPublicKey,
                                name: getCurrentChatName(),
                                groupOwnerPublicKey,
                                groupKeyName,
                              });
                            }}
                            onOptimisticSystemMessage={
                              handleOptimisticSystemMessage
                            }
                            isGroupOwner={!!isGroupOwner}
                          />
                        </Suspense>
                      ) : (
                        selectedConversation &&
                        selectedConversation.firstMessagePublicKey && (
                          <>
                            <button
                              onClick={() => {
                                setArchiveConfirm({
                                  type: "dm",
                                  conversationKey:
                                    selectedConversationPublicKey,
                                  publicKey:
                                    selectedConversation.firstMessagePublicKey,
                                  name: getCurrentChatName(),
                                });
                              }}
                              className="p-2 rounded-full hover:bg-ink/10 transition-colors cursor-pointer"
                              title="Archive chat"
                            >
                              <Archive className="w-5 h-5 text-fg-400" />
                            </button>
                            {/* Three-dot menu for DM actions (block, etc.) */}
                            <div className="relative">
                              <button
                                onClick={() => setDmMenuOpen(!dmMenuOpen)}
                                className="p-2 rounded-full hover:bg-ink/10 transition-colors cursor-pointer"
                                title="More options"
                              >
                                <EllipsisVertical className="w-5 h-5 text-fg-400" />
                              </button>
                              {dmMenuOpen && (
                                <>
                                  <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setDmMenuOpen(false)}
                                  />
                                  <div className="absolute right-0 top-full mt-1 z-50 bg-surface-raised border border-ink/10 rounded-lg shadow-xl py-1 min-w-[160px]">
                                    <button
                                      onClick={() => {
                                        setDmMenuOpen(false);
                                        setBlockConfirm({
                                          conversationKey:
                                            selectedConversationPublicKey,
                                          publicKey:
                                            selectedConversation.firstMessagePublicKey,
                                          name:
                                            activeChatUsersMap[
                                              selectedConversation
                                                .firstMessagePublicKey
                                            ] || "this user",
                                        });
                                      }}
                                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-400 hover:bg-ink/5 cursor-pointer transition-colors"
                                    >
                                      <ShieldBan className="w-4 h-4" />
                                      Block user
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                            <MessagingDisplayAvatar
                              username={
                                activeChatUsersMap[
                                  selectedConversation.firstMessagePublicKey
                                ]
                              }
                              publicKey={
                                selectedConversation.firstMessagePublicKey
                              }
                              extraDataPicUrl={
                                profilePicByPublicKey[
                                  selectedConversation.firstMessagePublicKey
                                ]
                              }
                              diameter={40}
                              classNames="shrink-0"
                            />
                          </>
                        )
                      )}
                    </div>
                  </header>

                  {/* Pinned message bar */}
                  {pinnedMessageTimestamp && isGroupChat && (
                    <div className="shrink-0 border-b border-ink/5 bg-surface flex items-center group">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleScrollToPinnedMessage()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleScrollToPinnedMessage();
                          }
                        }}
                        className="flex-1 min-w-0 flex items-center gap-3 px-4 md:px-5 py-2 text-left hover:bg-ink/5 transition-colors cursor-pointer"
                      >
                        <Pin className="w-4 h-4 text-brand shrink-0 rotate-45" />
                        <div className="min-w-0 flex-1">
                          <span className="text-[11px] font-semibold text-brand block leading-tight">
                            Pinned Message
                          </span>
                          <span className="text-xs text-fg-400 truncate block leading-tight">
                            {loadingPinnedMessage
                              ? "Loading…"
                              : pinnedMessage?.DecryptedMessage ||
                                pinnedPreview ||
                                "Tap to view"}
                          </span>
                        </div>
                      </div>
                      {isGroupOwner && (
                        <button
                          onClick={() => handlePinMessage("")}
                          className="p-2.5 mr-1 md:mr-2 rounded-full hover:bg-ink/10 transition-colors md:opacity-0 md:group-hover:opacity-100 cursor-pointer"
                          aria-label="Unpin message"
                        >
                          <X className="w-3.5 h-3.5 text-fg-500" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Paid DM price banner */}
                  {recipientDmPrice &&
                    recipientDmPrice.cents > 0 &&
                    selectedConversation?.ChatType === ChatType.DM && (
                      <div className="shrink-0 border-b border-amber-500/10 bg-amber-500/[0.03] px-4 md:px-5 py-2.5 flex items-start gap-3">
                        <CircleDollarSign className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="text-xs text-fg-300 block leading-relaxed">
                            <span className="text-ink font-medium">
                              @
                              {activeChatUsersMap[
                                selectedConversation.firstMessagePublicKey
                              ] ||
                                selectedConversation.firstMessagePublicKey.slice(
                                  0,
                                  8
                                )}
                            </span>{" "}
                            charges{" "}
                            <span className="text-amber-400 font-semibold">
                              ${(recipientDmPrice.cents / 100).toFixed(2)}
                            </span>{" "}
                            per message
                          </span>
                          <span className="text-[11px] text-fg-500 block mt-1">
                            Payment goes to their wallet. Once they reply,
                            messaging is free.
                          </span>
                          <button
                            onClick={() => setRecipientDmPrice(null)}
                            className="text-[11px] text-fg-600 hover:text-fg-400 mt-1 transition-colors"
                          >
                            Send to Requests instead (free) →
                          </button>
                        </div>
                      </div>
                    )}

                  <div className="pr-2 rounded-none w-[100%] bg-transparent ml-[calc-340px] pb-0 flex-1 min-h-0">
                    <div className="border-none flex flex-col h-full">
                      <div className="overflow-hidden flex-1 min-h-0">
                        {loadingConversation ? (
                          <div className="h-full flex items-center justify-center">
                            <Loader2 className="w-11 h-11 animate-spin text-brand" />
                          </div>
                        ) : !selectedConversation ? (
                          <div className="h-full flex items-center justify-center text-center px-6">
                            <p className="text-sm text-ink/40">
                              Select a conversation to start chatting.
                            </p>
                          </div>
                        ) : (
                          <MessagingBubblesAndAvatar
                            ref={bubblesRef}
                            key={selectedConversationPublicKey}
                            conversationPublicKey={
                              selectedConversationPublicKey
                            }
                            conversations={conversations}
                            getUsernameByPublicKey={activeChatUsersMap}
                            profilePicByPublicKey={profilePicByPublicKey}
                            lastReadTimestampNanos={openedLastReadNanos}
                            onPin={isGroupOwner ? handlePinMessage : undefined}
                            pinnedMessageTimestamp={pinnedMessageTimestamp}
                            onScrollToReply={handleScrollToReply}
                            onReloadLatest={handleReloadLatest}
                            onLoadNewer={handleLoadNewer}
                            viewingHistoricalSlice={
                              viewingHistoricalSliceKey ===
                              selectedConversationPublicKey
                            }
                            onScroll={(
                              e: Array<DecryptedMessageEntryResponse>
                            ) => {
                              setConversations((prev) =>
                                updateConv(
                                  prev,
                                  selectedConversationPublicKey,
                                  (c) => ({
                                    ...c,
                                    messages: [...c.messages, ...e],
                                  })
                                )
                              );
                            }}
                            onReply={(msg) => {
                              sendInputRef.current?.focus();
                              const senderKey =
                                msg.SenderInfo.OwnerPublicKeyBase58Check;
                              startTransition(() => {
                                setEditingMessage(null);
                                setReplyToMessage({
                                  text:
                                    msg.DecryptedMessage &&
                                    !looksLikeEncryptedHex(msg.DecryptedMessage)
                                      ? msg.DecryptedMessage
                                      : "",
                                  timestamp:
                                    msg.MessageInfo.TimestampNanosString,
                                  sender:
                                    activeChatUsersMap[senderKey] || senderKey,
                                });
                              });
                            }}
                            hiddenMessageIds={hiddenMessageIds}
                            pendingTipTimestamps={pendingTipTimestamps}
                            onEdit={(msg) => {
                              sendInputRef.current?.focus();
                              startTransition(() => {
                                setReplyToMessage(null);
                                setEditingMessage({
                                  text: msg.DecryptedMessage || "",
                                  timestamp:
                                    msg.MessageInfo.TimestampNanosString,
                                });
                              });
                            }}
                            onDeleteForMe={async (timestampNanosString) => {
                              if (!appUser) return;
                              setHiddenMessageIds((prev) => {
                                const next = new Set(prev);
                                next.add(timestampNanosString);
                                return next;
                              });
                              await hideMessage(
                                appUser.PublicKeyBase58Check,
                                timestampNanosString
                              );
                            }}
                            onDeleteForEveryone={async (message) => {
                              if (!appUser || !selectedConversation) return;
                              const convKey = selectedConversationPublicKey;
                              const recipientPublicKey =
                                selectedConversation.ChatType === ChatType.DM
                                  ? selectedConversation.firstMessagePublicKey
                                  : selectedConversation.messages[0]!
                                      .RecipientInfo.OwnerPublicKeyBase58Check;
                              const recipientKeyName =
                                selectedConversation.ChatType === ChatType.DM
                                  ? DEFAULT_KEY_MESSAGING_GROUP_NAME
                                  : selectedConversation.messages[0]!
                                      .RecipientInfo.AccessGroupKeyName;
                              const timestampNanosString =
                                message.MessageInfo.TimestampNanosString;

                              // Save original for rollback
                              const originalMessage = message;

                              // Preserve existing ExtraData and add deleted flag
                              const existingExtraData =
                                message.MessageInfo?.ExtraData || {};
                              const deletedExtraData = {
                                ...existingExtraData,
                                "msg:deleted": "true",
                              };

                              // Optimistic: replace message with tombstone
                              // _deletedLocally marker prevents mergeConversationUpdate
                              // from overwriting the tombstone with stale server data
                              // before the node indexes the update transaction.
                              setConversations((prev) =>
                                updateConv(prev, convKey, (c) => ({
                                  ...c,
                                  messages: c.messages.map((m) =>
                                    m.MessageInfo.TimestampNanosString ===
                                    timestampNanosString
                                      ? {
                                          ...m,
                                          _localId: undefined,
                                          DecryptedMessage: "",
                                          _deletedLocally: true,
                                          MessageInfo: {
                                            ...m.MessageInfo,
                                            ExtraData: deletedExtraData,
                                          },
                                        }
                                      : m
                                  ),
                                }))
                              );
                              setLockRefresh(true);

                              try {
                                await encryptAndUpdateMessage(
                                  "[deleted]",
                                  appUser.PublicKeyBase58Check,
                                  recipientPublicKey,
                                  recipientKeyName,
                                  DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                  timestampNanosString,
                                  deletedExtraData
                                );
                                // No push notification for deletions
                              } catch {
                                toast.error(
                                  "Failed to delete message for everyone"
                                );
                                // Rollback
                                setConversations((prev) =>
                                  updateConv(prev, convKey, (c) => ({
                                    ...c,
                                    messages: c.messages.map((m) =>
                                      m.MessageInfo.TimestampNanosString ===
                                      timestampNanosString
                                        ? originalMessage
                                        : m
                                    ),
                                  }))
                                );
                              } finally {
                                setLockRefresh(false);
                              }
                            }}
                            onRetry={async (localId) => {
                              if (!appUser) return;
                              const pendingMsgs = await getPendingMessages(
                                appUser.PublicKeyBase58Check
                              );
                              const pending = pendingMsgs.find(
                                (m) => m.localId === localId
                              );
                              if (pending) {
                                retryPendingMessage(pending);
                              }
                            }}
                            onDeleteFailed={(localId) => {
                              if (!appUser) return;
                              const convKey = selectedConversationPublicKey;
                              // Remove the failed optimistic message from UI
                              setConversations((prev) =>
                                updateConv(prev, convKey, (c) => ({
                                  ...c,
                                  messages: c.messages.filter(
                                    (m: any) => m._localId !== localId
                                  ),
                                }))
                              );
                              // Remove from pending messages storage
                              removePendingMessage(
                                appUser.PublicKeyBase58Check,
                                localId
                              );
                            }}
                            onReact={async (
                              timestampNanosString,
                              emoji,
                              forceAction
                            ) => {
                              if (!appUser || !selectedConversation) return;
                              const myKey = appUser.PublicKeyBase58Check;
                              const convKey = selectedConversationPublicKey;

                              // Determine if this is an add or remove (toggle logic).
                              // Check if the user already has an active "add" reaction
                              // for this emoji on this message (last-write-wins).
                              let action: "add" | "remove" =
                                forceAction || "add";
                              if (!forceAction) {
                                const msgs = selectedConversation.messages;
                                let latestTs = -1;
                                let latestAction: "add" | "remove" = "add";
                                for (const m of msgs) {
                                  const p = parseMessageType(m);
                                  if (
                                    p.type === "reaction" &&
                                    p.replyTo === timestampNanosString &&
                                    p.emoji === emoji &&
                                    m.SenderInfo.OwnerPublicKeyBase58Check ===
                                      myKey
                                  ) {
                                    const ts = m.MessageInfo.TimestampNanos;
                                    if (ts > latestTs) {
                                      latestTs = ts;
                                      latestAction = p.action || "add";
                                    }
                                  }
                                }
                                // If they already have an active "add", toggle to "remove"
                                if (latestTs > -1 && latestAction === "add") {
                                  action = "remove";
                                }
                              }

                              // Debounce: skip if there's already a pending reaction
                              // for this same emoji+target combo
                              const msgs = selectedConversation.messages;
                              const hasPending = msgs.some(
                                (m: any) =>
                                  m._status === "sending" &&
                                  m._localId?.startsWith("local-reaction-") &&
                                  m.MessageInfo?.ExtraData?.["msg:replyTo"] ===
                                    timestampNanosString &&
                                  m.MessageInfo?.ExtraData?.["msg:emoji"] ===
                                    emoji
                              );
                              if (hasPending) return;

                              const recipientPublicKey =
                                selectedConversation.ChatType === ChatType.DM
                                  ? selectedConversation.firstMessagePublicKey
                                  : selectedConversation.messages[0]!
                                      .RecipientInfo.OwnerPublicKeyBase58Check;
                              const recipientKeyName =
                                selectedConversation.ChatType === ChatType.DM
                                  ? DEFAULT_KEY_MESSAGING_GROUP_NAME
                                  : selectedConversation.messages[0]!
                                      .RecipientInfo.AccessGroupKeyName;

                              // Build a human-readable fallback for apps without reaction support
                              const reactedToMsg =
                                selectedConversation.messages.find(
                                  (m) =>
                                    m.MessageInfo.TimestampNanosString ===
                                    timestampNanosString
                                );
                              const preview =
                                reactedToMsg?.DecryptedMessage?.slice(0, 30) ||
                                "";
                              const fallbackText =
                                action === "remove"
                                  ? `Removed ${emoji} reaction`
                                  : preview
                                  ? `Reacted ${emoji} to "${preview}${
                                      (reactedToMsg?.DecryptedMessage?.length ??
                                        0) > 30
                                        ? "…"
                                        : ""
                                    }"`
                                  : `Reacted ${emoji}`;

                              // Optimistic: insert a mock reaction message immediately
                              const localId = `local-reaction-${Date.now()}-${Math.random()}`;
                              const TimestampNanos = new Date().getTime() * 1e6;
                              const mockReaction = {
                                DecryptedMessage: fallbackText,
                                IsSender: true,
                                _status: "sending" as const,
                                _localId: localId,
                                SenderInfo: {
                                  OwnerPublicKeyBase58Check: myKey,
                                  AccessGroupKeyName:
                                    DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                },
                                RecipientInfo: {
                                  OwnerPublicKeyBase58Check: recipientPublicKey,
                                  AccessGroupKeyName: recipientKeyName,
                                },
                                MessageInfo: {
                                  TimestampNanos,
                                  TimestampNanosString: String(TimestampNanos),
                                  ExtraData: buildExtraData({
                                    type: "reaction",
                                    replyTo: timestampNanosString,
                                    emoji,
                                    action,
                                  }),
                                },
                              } as DecryptedMessageEntryResponse & {
                                _status: string;
                                _localId: string;
                              };

                              setConversations((prev) =>
                                updateConv(prev, convKey, (c) => ({
                                  ...c,
                                  messages: [mockReaction, ...c.messages],
                                }))
                              );
                              cacheLastReadTimestamp(
                                myKey,
                                convKey,
                                TimestampNanos
                              );
                              clearUnread(convKey);
                              removeUnreadConversation(convKey);
                              sendRead(convKey, String(TimestampNanos));

                              try {
                                await encryptAndSendNewMessage(
                                  fallbackText,
                                  myKey,
                                  recipientPublicKey,
                                  recipientKeyName,
                                  DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                  buildExtraData({
                                    type: "reaction",
                                    replyTo: timestampNanosString,
                                    emoji,
                                    action,
                                  })
                                );
                                // Mark as sent
                                setConversations((prev) =>
                                  updateConv(prev, convKey, (c) => ({
                                    ...c,
                                    messages: c.messages.map((m: any) =>
                                      m._localId === localId
                                        ? { ...m, _status: "sent" }
                                        : m
                                    ),
                                  }))
                                );
                              } catch {
                                // Remove failed optimistic reaction
                                setConversations((prev) =>
                                  updateConv(prev, convKey, (c) => ({
                                    ...c,
                                    messages: c.messages.filter(
                                      (m: any) => m._localId !== localId
                                    ),
                                  }))
                                );
                                toast.error(
                                  action === "remove"
                                    ? "Failed to remove reaction"
                                    : "Failed to send reaction"
                                );
                              }
                            }}
                            onTip={(msg, amountUsd) => {
                              if (!appUser) return;
                              const senderPk =
                                msg.SenderInfo.OwnerPublicKeyBase58Check;
                              if (senderPk === appUser.PublicKeyBase58Check) {
                                toast.info("You can't tip yourself");
                                return;
                              }
                              const { blockedUsers, dismissedUsers } =
                                useStore.getState();
                              if (
                                blockedUsers.has(senderPk) ||
                                dismissedUsers.has(senderPk)
                              )
                                return;
                              setTipTarget({
                                recipientPublicKey: senderPk,
                                recipientUsername: activeChatUsersMap[senderPk],
                                tipReplyTo:
                                  msg.MessageInfo.TimestampNanosString,
                                initialAmountUsd: amountUsd,
                              });
                              setPendingTipTimestamps((prev) =>
                                new Set(prev).add(
                                  msg.MessageInfo.TimestampNanosString
                                )
                              );
                            }}
                            onMicroTip={async (msg) => {
                              if (!appUser || !selectedConversation) return;
                              const senderPk =
                                msg.SenderInfo.OwnerPublicKeyBase58Check;
                              if (senderPk === appUser.PublicKeyBase58Check) {
                                toast.info("You can't tip yourself");
                                return;
                              }
                              const { blockedUsers, dismissedUsers } =
                                useStore.getState();
                              if (
                                blockedUsers.has(senderPk) ||
                                dismissedUsers.has(senderPk)
                              )
                                return;
                              // Cooldown check
                              const now = Date.now();
                              if (
                                now - lastMicroTipTime <
                                MICRO_TIP_COOLDOWN_MS
                              ) {
                                toast.info(
                                  "Please wait a moment before sending another tip"
                                );
                                return;
                              }
                              lastMicroTipTime = now;
                              const tipTs =
                                msg.MessageInfo.TimestampNanosString;
                              setPendingTipTimestamps((prev) =>
                                new Set(prev).add(tipTs)
                              );

                              try {
                                const tipCurrency =
                                  (getCachedTipCurrency(
                                    appUser.PublicKeyBase58Check
                                  ) as TipCurrency) || "DESO";
                                let txHash = "";
                                let tipAmountNanos = 0;
                                let tipAmountUsdcBaseUnits: string | undefined;

                                if (tipCurrency === "USDC") {
                                  const usdcAmount = usdToUsdcBaseUnits(0.01);
                                  const balance = await fetchUsdcBalance(
                                    appUser.PublicKeyBase58Check
                                  );
                                  if (balance < usdcAmount) {
                                    toast.info("Insufficient USDC balance");
                                    return;
                                  }
                                  if (appUser.BalanceNanos < 1e6) {
                                    toast.info(
                                      "Need a small amount of DESO for fees"
                                    );
                                    return;
                                  }

                                  const hasPerms = identity.hasPermissions({
                                    GlobalDESOLimit: 1e6,
                                    DAOCoinOperationLimitMap: {
                                      [USDC_CREATOR_PUBLIC_KEY]: {
                                        transfer: 1,
                                      },
                                    },
                                  });
                                  if (!hasPerms) {
                                    await identity.requestPermissions({
                                      GlobalDESOLimit: 1e7,
                                      TransactionCountLimitMap: {
                                        AUTHORIZE_DERIVED_KEY: 1,
                                      },
                                      DAOCoinOperationLimitMap: {
                                        [USDC_CREATOR_PUBLIC_KEY]: {
                                          transfer: 1,
                                        },
                                      },
                                    });
                                  }
                                  const result = await withAuth(() =>
                                    transferDeSoToken({
                                      SenderPublicKeyBase58Check:
                                        appUser.PublicKeyBase58Check,
                                      ProfilePublicKeyBase58CheckOrUsername:
                                        USDC_CREATOR_PUBLIC_KEY,
                                      ReceiverPublicKeyBase58CheckOrUsername:
                                        senderPk,
                                      DAOCoinToTransferNanos:
                                        toHexUint256(usdcAmount),
                                    })
                                  );
                                  txHash =
                                    (result as any)?.TxnHashHex ||
                                    (result as any)?.TransactionIDBase58Check ||
                                    "";
                                  tipAmountUsdcBaseUnits =
                                    toHexUint256(usdcAmount);
                                  invalidateUsdcBalanceCache();
                                } else {
                                  if (appUser.BalanceNanos <= 0) {
                                    toast.info("Add funds to send tips");
                                    return;
                                  }
                                  const rate = await fetchExchangeRate();
                                  tipAmountNanos = usdToNanos(0.01, rate);
                                  if (tipAmountNanos <= 0) {
                                    toast.error(
                                      "Could not calculate tip amount"
                                    );
                                    return;
                                  }
                                  if (tipAmountNanos > appUser.BalanceNanos) {
                                    toast.info("Insufficient balance");
                                    return;
                                  }

                                  const hasPerms = identity.hasPermissions({
                                    GlobalDESOLimit: tipAmountNanos,
                                    TransactionCountLimitMap: {
                                      BASIC_TRANSFER: 1,
                                    },
                                  });
                                  if (!hasPerms) {
                                    await identity.requestPermissions({
                                      GlobalDESOLimit: tipAmountNanos + 1e7,
                                      TransactionCountLimitMap: {
                                        AUTHORIZE_DERIVED_KEY: 1,
                                        BASIC_TRANSFER: 1,
                                      },
                                    });
                                  }
                                  const result = await withAuth(() =>
                                    sendDeso({
                                      SenderPublicKeyBase58Check:
                                        appUser.PublicKeyBase58Check,
                                      RecipientPublicKeyOrUsername: senderPk,
                                      AmountNanos: tipAmountNanos,
                                    })
                                  );
                                  txHash =
                                    (result as any)?.TxnHashHex ||
                                    (result as any)?.TransactionIDBase58Check ||
                                    "";
                                }

                                // Send tip message
                                const recipientPublicKey =
                                  selectedConversation.ChatType === ChatType.DM
                                    ? selectedConversation.firstMessagePublicKey
                                    : selectedConversation.messages[0]!
                                        .RecipientInfo
                                        .OwnerPublicKeyBase58Check;
                                const recipientKeyName =
                                  selectedConversation.ChatType === ChatType.DM
                                    ? DEFAULT_KEY_MESSAGING_GROUP_NAME
                                    : selectedConversation.messages[0]!
                                        .RecipientInfo.AccessGroupKeyName;

                                const senderUsername =
                                  activeChatUsersMap[senderPk];
                                const fallback = `Tipped $0.01 to ${
                                  senderUsername
                                    ? `@${senderUsername}`
                                    : senderPk.slice(0, 8)
                                }`;

                                await encryptAndSendNewMessage(
                                  fallback,
                                  appUser.PublicKeyBase58Check,
                                  recipientPublicKey,
                                  recipientKeyName,
                                  DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                  buildExtraData({
                                    type: "tip",
                                    tipAmountNanos:
                                      tipCurrency === "DESO"
                                        ? tipAmountNanos
                                        : undefined,
                                    tipAmountUsdcBaseUnits,
                                    tipCurrency,
                                    tipTxHash: txHash,
                                    tipReplyTo:
                                      msg.MessageInfo.TimestampNanosString,
                                    tipRecipient: senderPk,
                                  })
                                );
                                // No push notification for tips
                                useStore.getState().addSessionTipUsd(0.01);
                                toast.success(
                                  `Tipped $0.01 ${tipCurrency} to ${
                                    senderUsername
                                      ? `@${senderUsername}`
                                      : "user"
                                  }`
                                );
                              } catch (error: any) {
                                const errMsg =
                                  error?.message || error?.toString?.() || "";
                                if (
                                  errMsg.includes("user cancelled") ||
                                  errMsg.includes("WINDOW_CLOSED")
                                ) {
                                  toast.error("Transaction cancelled.");
                                } else {
                                  toast.error("Tip failed.");
                                  console.error("Micro-tip error:", error);
                                }
                              } finally {
                                setPendingTipTimestamps((prev) => {
                                  const next = new Set(prev);
                                  next.delete(tipTs);
                                  return next;
                                });
                              }
                            }}
                            onPrivateMessage={(senderPublicKey) => {
                              const dmKey =
                                senderPublicKey +
                                DEFAULT_KEY_MESSAGING_GROUP_NAME;
                              // Inject placeholder so the safeSelectedKey guard
                              // doesn't reset the selection before rehydration
                              // adds the real conversation. Order matters:
                              // setConversations must come before setSelectedConversationPublicKey.
                              setConversations((prev) => {
                                if (prev[dmKey]) return prev;
                                return {
                                  [dmKey]: {
                                    ChatType: ChatType.DM,
                                    firstMessagePublicKey: senderPublicKey,
                                    messages: [],
                                  },
                                  ...prev,
                                };
                              });
                              setSelectedConversationPublicKey(dmKey);
                              rehydrateConversation(dmKey, true);
                            }}
                          />
                        )}
                      </div>

                      {selectedConversation && (
                        <SendMessageButtonAndInput
                          ref={sendInputRef}
                          key={selectedConversationPublicKey}
                          conversationKey={selectedConversationPublicKey}
                          replyTo={replyToMessage}
                          onCancelReply={() =>
                            startTransition(() => setReplyToMessage(null))
                          }
                          editingMessage={editingMessage}
                          onCancelEdit={() =>
                            startTransition(() => setEditingMessage(null))
                          }
                          onSubmitEdit={async (
                            newText,
                            timestamp,
                            extraData
                          ) => {
                            if (
                              !appUser ||
                              (!newText.trim() && !extraData) ||
                              !selectedConversation
                            )
                              return;
                            setEditingMessage(null);
                            const convKey = selectedConversationPublicKey;
                            const recipientPublicKey =
                              selectedConversation.ChatType === ChatType.DM
                                ? selectedConversation.firstMessagePublicKey
                                : selectedConversation.messages[0]!
                                    .RecipientInfo.OwnerPublicKeyBase58Check;
                            const recipientKeyName =
                              selectedConversation.ChatType === ChatType.DM
                                ? DEFAULT_KEY_MESSAGING_GROUP_NAME
                                : selectedConversation.messages[0]!
                                    .RecipientInfo.AccessGroupKeyName;

                            // Find original message for rollback
                            const originalMessage =
                              selectedConversation.messages.find(
                                (m) =>
                                  m.MessageInfo.TimestampNanosString ===
                                  timestamp
                              );

                            // Preserve existing ExtraData (e.g. reply info), merge new extraData (e.g. image), and add edited flag
                            const existingExtraData =
                              originalMessage?.MessageInfo?.ExtraData || {};
                            const updatedExtraData = {
                              ...existingExtraData,
                              ...extraData,
                              "msg:edited": "true",
                            };

                            // Optimistic: update message text and add edited flag
                            if (originalMessage) {
                              const updatedMsg = {
                                ...originalMessage,
                                DecryptedMessage: newText,
                                MessageInfo: {
                                  ...originalMessage.MessageInfo,
                                  ExtraData: updatedExtraData,
                                },
                              };
                              cacheDecryptionResult(
                                originalMessage.MessageInfo.TimestampNanos,
                                updatedMsg
                              );
                            }
                            setConversations((prev) =>
                              updateConv(prev, convKey, (c) => ({
                                ...c,
                                messages: c.messages.map((m) =>
                                  m.MessageInfo.TimestampNanosString ===
                                  timestamp
                                    ? {
                                        ...m,
                                        DecryptedMessage: newText,
                                        MessageInfo: {
                                          ...m.MessageInfo,
                                          ExtraData: updatedExtraData,
                                        },
                                      }
                                    : m
                                ),
                              }))
                            );
                            setLockRefresh(true);

                            try {
                              await encryptAndUpdateMessage(
                                newText,
                                appUser.PublicKeyBase58Check,
                                recipientPublicKey,
                                recipientKeyName,
                                DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                timestamp,
                                updatedExtraData
                              );
                              // Force re-decrypt on next poll so blockchain
                              // data replaces the optimistic cache entry
                              if (originalMessage) {
                                invalidateMessageCache(
                                  originalMessage.MessageInfo.TimestampNanos,
                                  convKey
                                );
                              }
                              // Update per-conversation IndexedDB cache so
                              // navigating away and back doesn't show stale text
                              const currentConv =
                                conversationsRef.current[convKey];
                              if (currentConv) {
                                cacheConversationMessages(
                                  appUser.PublicKeyBase58Check,
                                  convKey,
                                  currentConv.messages
                                );
                              }
                              // No push notification for edits
                            } catch {
                              toast.error("Failed to edit message");
                              // Rollback
                              if (originalMessage) {
                                cacheDecryptionResult(
                                  originalMessage.MessageInfo.TimestampNanos,
                                  originalMessage
                                );
                                setConversations((prev) =>
                                  updateConv(prev, convKey, (c) => ({
                                    ...c,
                                    messages: c.messages.map((m) =>
                                      m.MessageInfo.TimestampNanosString ===
                                      timestamp
                                        ? originalMessage
                                        : m
                                    ),
                                  }))
                                );
                              }
                            } finally {
                              setLockRefresh(false);
                            }
                          }}
                          onKeystroke={() =>
                            onKeystroke(selectedConversationPublicKey)
                          }
                          typingUsers={getTypingUsersForConversation(
                            selectedConversationPublicKey
                          )
                            .map(
                              (pk) =>
                                usernameByPublicKeyBase58Check[pk] ||
                                shortenLongWord(pk)
                            )
                            .filter(Boolean)}
                          mentionCandidates={
                            isGroupChat && chatMembers
                              ? Object.entries(chatMembers)
                                  .filter(
                                    ([pk, profile]) =>
                                      pk !== appUser?.PublicKeyBase58Check &&
                                      profile?.Username
                                  )
                                  .map(([pk, profile]) => ({
                                    publicKey: pk,
                                    username: profile!.Username,
                                  }))
                              : undefined
                          }
                          onTipClick={
                            selectedConversation.ChatType === ChatType.DM
                              ? () => {
                                  const recipientPk =
                                    selectedConversation.firstMessagePublicKey;
                                  if (
                                    recipientPk ===
                                    appUser?.PublicKeyBase58Check
                                  ) {
                                    toast.info("You can't tip yourself");
                                    return;
                                  }
                                  setTipTarget({
                                    recipientPublicKey: recipientPk,
                                    recipientUsername:
                                      activeChatUsersMap[recipientPk],
                                  });
                                }
                              : undefined
                          }
                          paidDmPriceCents={
                            recipientDmPrice
                              ? recipientDmPrice.cents
                              : undefined
                          }
                          onClick={async (
                            messageToSend: string,
                            extraData?: Record<string, string>,
                            options?: {
                              prepare?: () => Promise<Record<string, string>>;
                            }
                          ) => {
                            if (!selectedConversation) return;

                            // Paid DM first-send confirmation: check BEFORE any
                            // state mutations so the message text stays in the input.
                            // Returns false to abort the send — the input component
                            // will NOT clear the text field because onClick threw.
                            const wouldBePaidDm =
                              recipientDmPrice &&
                              recipientDmPrice.cents > 0 &&
                              selectedConversation.ChatType === ChatType.DM;
                            if (wouldBePaidDm && !paidDmConfirmed) {
                              setPaidDmConfirmed(true);
                              toast(
                                `Messages to this user cost $${(
                                  recipientDmPrice.cents / 100
                                ).toFixed(
                                  2
                                )} each (paid to their wallet). Tap Send again to confirm.`,
                                { duration: 5000 }
                              );
                              // Throw to prevent the input from clearing the message
                              throw new Error("__PAID_DM_CONFIRM__");
                            }
                            // DeSo caps EncryptedText at 10,000 bytes. Reject
                            // oversized plaintext here so it never gets saved
                            // to IndexedDB and stuck in the retry loop.
                            const messageByteLength = new TextEncoder().encode(
                              messageToSend
                            ).length;
                            if (messageByteLength > MAX_MESSAGE_TEXT_BYTES) {
                              toast.error(
                                `Message too long (${messageByteLength.toLocaleString()} bytes). Split it into smaller messages under ${MAX_MESSAGE_TEXT_BYTES.toLocaleString()} bytes.`
                              );
                              throw new Error("__MESSAGE_TOO_LONG__");
                            }
                            // Merge reply data into extraData if replying
                            if (replyToMessage) {
                              extraData = {
                                ...extraData,
                                [MSG_REPLY_TO]: replyToMessage.timestamp,
                                [MSG_REPLY_PREVIEW]: looksLikeEncryptedHex(
                                  replyToMessage.text
                                )
                                  ? ""
                                  : replyToMessage.text.slice(0, 200),
                                [MSG_REPLY_SENDER]: replyToMessage.sender,
                              };
                              setReplyToMessage(null);
                            }
                            // Detect message language (franc, < 1ms) and store in ExtraData
                            // so receivers can skip detection and translate efficiently.
                            const detectedLang =
                              detectLanguageSync(messageToSend);
                            if (detectedLang) {
                              extraData = {
                                ...extraData,
                                [MSG_LANG]: detectedLang,
                              };
                            }

                            // Generate a mock message to display in the UI to give
                            // the user immediate feedback.
                            const TimestampNanos = new Date().getTime() * 1e6;
                            const recipientPublicKey =
                              selectedConversation.ChatType === ChatType.DM
                                ? selectedConversation.firstMessagePublicKey
                                : selectedConversation.messages[0]!
                                    .RecipientInfo.OwnerPublicKeyBase58Check;
                            const recipientAccessGroupKeyName =
                              selectedConversation.ChatType === ChatType.DM
                                ? DEFAULT_KEY_MESSAGING_GROUP_NAME
                                : selectedConversation.messages[0]!
                                    .RecipientInfo.AccessGroupKeyName;
                            const localId = `local-${Date.now()}-${Math.random()}`;
                            const hasPrepare = !!options?.prepare;
                            const mockMessage = {
                              DecryptedMessage: messageToSend,
                              IsSender: true,
                              _status: (hasPrepare
                                ? "processing"
                                : "sending") as string,
                              _localId: localId,
                              SenderInfo: {
                                OwnerPublicKeyBase58Check:
                                  appUser.PublicKeyBase58Check,
                                AccessGroupKeyName:
                                  DEFAULT_KEY_MESSAGING_GROUP_NAME,
                              },
                              RecipientInfo: {
                                OwnerPublicKeyBase58Check: recipientPublicKey,
                                AccessGroupKeyName: recipientAccessGroupKeyName,
                              },
                              MessageInfo: {
                                TimestampNanos,
                                TimestampNanosString: String(TimestampNanos),
                                ExtraData: extraData || {},
                              },
                            } as DecryptedMessageEntryResponse & {
                              _status: string;
                              _localId: string;
                            };

                            // Sending a message = implicit "caught up" — clear
                            // the unread divider so it doesn't persist above.
                            setOpenedLastReadNanos(null);

                            // Optimistic: insert immediately
                            const convKey = selectedConversationPublicKey;
                            setConversations((prev) =>
                              updateConv(prev, convKey, (c) => ({
                                ...c,
                                messages: [mockMessage, ...c.messages],
                              }))
                            );
                            // Advance the read cursor — sending implies the user
                            // has seen every prior message in the conversation.
                            cacheLastReadTimestamp(
                              appUser.PublicKeyBase58Check,
                              convKey,
                              TimestampNanos
                            );
                            clearUnread(convKey);
                            removeUnreadConversation(convKey);
                            sendRead(convKey, String(TimestampNanos));
                            setLockRefresh(true);

                            // If a prepare callback was provided (e.g. audio upload),
                            // run it now to get the final extraData before sending.
                            if (hasPrepare) {
                              try {
                                extraData = await options!.prepare!();
                                // Update the bubble with the real data
                                setConversations((prev) =>
                                  updateConv(prev, convKey, (c) => ({
                                    ...c,
                                    messages: c.messages.map((m: any) =>
                                      m._localId === localId
                                        ? {
                                            ...m,
                                            _status: "sending",
                                            MessageInfo: {
                                              ...m.MessageInfo,
                                              ExtraData: extraData,
                                            },
                                          }
                                        : m
                                    ),
                                  }))
                                );
                              } catch (e: any) {
                                // Remove the processing bubble on failure
                                setConversations((prev) =>
                                  updateConv(prev, convKey, (c) => ({
                                    ...c,
                                    messages: c.messages.filter(
                                      (m: any) => m._localId !== localId
                                    ),
                                  }))
                                );
                                setLockRefresh(false);
                                toast.error(`Upload failed: ${e.message || e}`);
                                return;
                              }
                            }

                            // Paid DM: atomic payment + message bundle
                            const isPaidDm =
                              recipientDmPrice &&
                              recipientDmPrice.cents > 0 &&
                              selectedConversation?.ChatType === ChatType.DM &&
                              recipientAccessGroupKeyName ===
                                DEFAULT_KEY_MESSAGING_GROUP_NAME;

                            try {
                              if (isPaidDm) {
                                // Paid DMs are NOT persisted to IndexedDB — retry
                                // would send without payment. User must re-send manually.
                                const { requestBody } =
                                  await prepareEncryptedDMMessage(
                                    messageToSend,
                                    appUser.PublicKeyBase58Check,
                                    recipientPublicKey,
                                    recipientAccessGroupKeyName,
                                    DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                    extraData
                                  );
                                await withAuth(() =>
                                  sendAtomicPaidMessage(
                                    appUser.PublicKeyBase58Check,
                                    recipientPublicKey,
                                    recipientDmPrice.cents,
                                    "DESO",
                                    requestBody
                                  )
                                );
                                // Fire-and-forget: create paid association
                                createPaidAssociation(
                                  appUser.PublicKeyBase58Check,
                                  recipientPublicKey
                                ).catch(() => {});
                              } else {
                                // Persist to IndexedDB so the message survives app close.
                                const pending: PendingMessage = {
                                  localId,
                                  conversationKey: convKey,
                                  messageText: messageToSend,
                                  senderPublicKey: appUser.PublicKeyBase58Check,
                                  recipientPublicKey,
                                  recipientAccessGroupKeyName,
                                  extraData,
                                  createdAt: Date.now(),
                                };
                                await addPendingMessage(pending);
                                await encryptAndSendNewMessage(
                                  messageToSend,
                                  appUser.PublicKeyBase58Check,
                                  recipientPublicKey,
                                  recipientAccessGroupKeyName,
                                  DEFAULT_KEY_MESSAGING_GROUP_NAME,
                                  extraData
                                );
                              }

                              // Auto-approve on reply: if the other user paid
                              // to DM us, approve them so future messages are free
                              if (
                                paidUsers.has(recipientPublicKey) &&
                                !approvedUsers.has(recipientPublicKey)
                              ) {
                                useStore
                                  .getState()
                                  .approveUser(recipientPublicKey);
                                import(
                                  "../services/conversations.service"
                                ).then((mod) =>
                                  mod
                                    .createApprovalAssociation(
                                      appUser.PublicKeyBase58Check,
                                      recipientPublicKey
                                    )
                                    .catch(() => {
                                      useStore
                                        .getState()
                                        .rollbackApproval(recipientPublicKey);
                                    })
                                );
                              }

                              // Sent successfully — remove from IndexedDB
                              removePendingMessage(
                                appUser.PublicKeyBase58Check,
                                localId
                              );
                              // Notify via WebSocket relay
                              notifyConversation(
                                convKey,
                                recipientPublicKey,
                                usernameByPublicKeyBase58Check[
                                  appUser.PublicKeyBase58Check
                                ] || appUser.PublicKeyBase58Check
                              );
                              // Update status to "sent" (blockchain accepted)
                              setConversations((prev) =>
                                updateConv(prev, convKey, (c) => ({
                                  ...c,
                                  messages: c.messages.map((m: any) =>
                                    m._localId === localId
                                      ? { ...m, _status: "sent" }
                                      : m
                                  ),
                                }))
                              );
                              // Start dedicated confirmation poller for this message.
                              // Checks the single conversation every 3s to confirm
                              // on-chain quickly instead of waiting for the general poll.
                              scheduleConfirmationCheck(
                                convKey,
                                localId,
                                selectedConversation.ChatType,
                                recipientPublicKey,
                                recipientAccessGroupKeyName
                              );
                            } catch (e: any) {
                              // Update status to "failed" — pending message stays in IndexedDB for retry
                              setConversations((prev) =>
                                updateConv(prev, convKey, (c) => ({
                                  ...c,
                                  messages: c.messages.map((m: any) =>
                                    m._localId === localId
                                      ? { ...m, _status: "failed" }
                                      : m
                                  ),
                                }))
                              );
                              // Show user-friendly error for paid DM failures
                              const errStr = e?.toString?.() || "";
                              const isInsufficientBalance =
                                errStr.includes("InsufficientBalance") ||
                                errStr.includes("not enough") ||
                                errStr.includes("RuleErrorInsufficientBalance");
                              if (isInsufficientBalance) {
                                toast.error(
                                  "Not enough DESO to send this paid message. Add funds and try again."
                                );
                              } else {
                                toast.error(
                                  `Failed to send message. ${
                                    errStr.length > 120
                                      ? "Please try again."
                                      : errStr
                                  }`
                                );
                              }
                              return Promise.reject(e);
                            } finally {
                              setLockRefresh(false);
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ViewTransition>
            )}
          </div>
        )}
      {/* Archive / Leave Group confirmation */}
      {archiveConfirm && (
        <ArchiveConfirmModal
          type={archiveConfirm.type}
          name={archiveConfirm.name}
          onCancel={() => setArchiveConfirm(null)}
          onConfirm={() => {
            if (archiveConfirm.type === "dm") {
              handleArchiveChat(
                archiveConfirm.conversationKey,
                archiveConfirm.publicKey
              );
            } else if (
              archiveConfirm.groupOwnerPublicKey &&
              archiveConfirm.groupKeyName
            ) {
              handleArchiveGroup(
                archiveConfirm.conversationKey,
                archiveConfirm.groupOwnerPublicKey,
                archiveConfirm.groupKeyName
              );
            }
            setArchiveConfirm(null);
          }}
        />
      )}

      {/* Block confirmation dialog */}
      {blockConfirm && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setBlockConfirm(null)}
          />
          <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface-raised border border-ink/10 rounded-xl shadow-2xl p-6 w-[320px] max-w-[90vw]">
            <h3 className="text-ink font-bold text-base mb-2">
              Block {blockConfirm.name}?
            </h3>
            <p className="text-fg-400 text-sm mb-5">
              They won't be able to message you. You can unblock them later from
              the Requests tab.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setBlockConfirm(null)}
                className="flex-1 py-2.5 rounded-lg bg-ink/5 text-fg-300 text-sm font-medium hover:bg-ink/10 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleBlockRequest(
                    blockConfirm.conversationKey,
                    blockConfirm.publicKey
                  );
                  setBlockConfirm(null);
                }}
                className="flex-1 py-2.5 rounded-lg glass-btn-danger text-red-400 text-sm font-bold cursor-pointer transition-colors"
              >
                Block
              </button>
            </div>
          </div>
        </>
      )}

      {/* Tip confirm dialog */}
      {tipTarget && appUser && (
        <Suspense fallback={null}>
          <LazyTipConfirmDialog
            appUser={appUser}
            recipientPublicKey={tipTarget.recipientPublicKey}
            recipientUsername={tipTarget.recipientUsername}
            tipReplyTo={tipTarget.tipReplyTo}
            initialAmountUsd={tipTarget.initialAmountUsd}
            onClose={() => {
              if (tipTarget?.tipReplyTo) {
                setPendingTipTimestamps((prev) => {
                  const next = new Set(prev);
                  next.delete(tipTarget.tipReplyTo!);
                  return next;
                });
              }
              setTipTarget(null);
            }}
            onTipSent={async (tipData) => {
              useStore.getState().addSessionTipUsd(tipData.amountUsd);
              try {
                const convKey = selectedConversationPublicKey;
                const conv = conversations[convKey];
                if (!conv) return;
                const recipientPublicKey =
                  conv.ChatType === ChatType.DM
                    ? conv.firstMessagePublicKey
                    : conv.messages[0]!.RecipientInfo.OwnerPublicKeyBase58Check;
                const recipientKeyName =
                  conv.ChatType === ChatType.DM
                    ? DEFAULT_KEY_MESSAGING_GROUP_NAME
                    : conv.messages[0]!.RecipientInfo.AccessGroupKeyName;

                const recipientName = tipData.recipientUsername
                  ? `@${tipData.recipientUsername}`
                  : tipData.recipientPublicKey.slice(0, 8);
                const hasCustomMessage = !!tipData.message?.trim();
                const fallback = tipData.message || `Tipped ${recipientName}`;

                await encryptAndSendNewMessage(
                  fallback,
                  appUser.PublicKeyBase58Check,
                  recipientPublicKey,
                  recipientKeyName,
                  DEFAULT_KEY_MESSAGING_GROUP_NAME,
                  buildExtraData({
                    type: "tip",
                    tipAmountNanos:
                      tipData.currency === "DESO"
                        ? tipData.amountNanos
                        : undefined,
                    tipAmountUsdcBaseUnits: tipData.amountUsdcBaseUnits,
                    tipCurrency: tipData.currency,
                    tipTxHash: tipData.txHash,
                    tipReplyTo: tipData.tipReplyTo,
                    tipRecipient: tipData.recipientPublicKey,
                    tipHasCustomMessage: hasCustomMessage,
                  })
                );
                // No push notification for tips
              } catch (e) {
                toast.error(
                  "Tip sent but message failed. The recipient received the funds."
                );
                console.error("Tip message error:", e);
              }
            }}
          />
        </Suspense>
      )}

      {/* In-app join group modal — triggered by clicking a join link in a chat */}
      <JoinGroupModalWrapper />

      {/* Confirmation modal — shown after redirect from /join/:code page */}
      <Suspense fallback={null}>
        <LazyJoinConfirmationModal />
      </Suspense>
    </div>
  );
};

/** Wrapper that subscribes to the pendingJoinCode store value */
function JoinGroupModalWrapper() {
  const code = useStore((s) => s.pendingJoinCode);
  if (!code) return null;
  return (
    <JoinGroupModal
      code={code}
      onClose={() => useStore.getState().setPendingJoinCode(null)}
    />
  );
}
